import StudioExportMenu from './StudioExportMenu';
import StatisticsHeaderActions from '../../components/planning/StatisticsHeaderActions';
import StudioProportionChart from './StudioProportionChart';
import {defaultMetricChartType} from './studioChartDefaults';
import {Bookmark,ChevronDown,FileChartColumn,SlidersHorizontal,ChartPie,Donut,ChartSpline,ChartArea,ChartColumn,ChartScatter} from 'lucide-react';
import StudioMetricLibrary from './StudioMetricLibrary';
import StudioInsights from './StudioInsights';
import StudioCardFilters, {StudioFilterSummary,StudioInsightPicker} from './StudioCardFilters';
import {EMPTY_CARD_FILTERS,INSIGHT_VIEWS,metricKeyFor,newMetricInstance,cardFilters,filterSignature,filteredTitle,filterInsightData,eventCountRow} from './studioDashboard';
import {studioReportRows} from './studioReportRows';
import {noteClassification} from '../../utils/noteClassification';
import StudioMetricsMenuTabs from './StudioMetricsMenuTabs';
import {STUDIO_STARTER_PATTERNS,starterMetricKeys} from './studioStarterMetrics';
import {studioPointKey, metricDate, knownRoundDifference, knownMatchResult, knownPistolWins, canonicalPlayerMatches, extraMatchFilter} from './studioAnalytics';
import {setupStatsProjection} from "../../utils/setupStatsProjection";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import "./TeamStatsSummary.css";

import { db } from "../../Security/firebase-config";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import CustomAlert from "../../components/core-components/CustomAlert";

import LoginTeamGate from "../../components/core-components/LoginTeamGate";

const AGENT_ROLE_MAP = {
  Jett: "duelist",
  Raze: "duelist",
  Reyna: "duelist",
  Phoenix: "duelist",
  Neon: "duelist",
  Yoru: "duelist",
  Iso: "duelist",
  Waylay: "duelist",

  Sova: "initiator",
  Fade: "initiator",
  Skye: "initiator",
  Breach: "initiator",
  KAYO: "initiator",
  "KAY/O": "initiator",
  Gekko: "initiator",
  Tejo: "initiator",

  Omen: "smoke",
  Brimstone: "smoke",
  Astra: "smoke",
  Harbor: "smoke",
  Viper: "smoke",
  Clove: "smoke",

  Killjoy: "sentinel",
  Cypher: "sentinel",
  Sage: "sentinel",
  Chamber: "sentinel",
  Deadlock: "sentinel",
  Vyse: "sentinel",
  Veto: "sentinel",
};

const ROLE_ORDER = {
  duelist: 0,
  initiator: 1,
  smoke: 2,
  sentinel: 3,
  unknown: 4,
};

const CATEGORY_ORDER = {
  Review: 7,
  Core: 1,
  Pistols: 2,
  Conversions: 3,
  Strategies: 4,
  Roster: 5,
  Comps: 6,
};

const SECTION_ORDER = {
  overall: 1,
  mapSpecific: 2,
  mapCompSpecific: 3,
};

const STAT_ORDER = {
  "Overall Match Win Rate": 1,
  "Overall Average Round Differential": 2,
  "Overall Total Round Differential": 3,

  "Overall Double Pistol Conversion Rate": 4,
  "Overall No Pistol Conversion Rate": 5,
  "Overall Pistol Win Rate": 6,
  "Overall Pistol Win Rate ATK": 7,
  "Overall Pistol Win Rate DEF": 8,

  "Overall Round 2 Conversion after Pistol Win": 9,
  "Overall Round 2 Conversion after Pistol Win ATK": 10,
  "Overall Round 2 Conversion after Pistol Win DEF": 11,

  "Overall Round 2 Conversion after Pistol Loss": 12,
  "Overall Round 2 Conversion after Pistol Loss ATK": 13,
  "Overall Round 2 Conversion after Pistol Loss DEF": 14,

  "Overall Bonus Conversion (WW → R3)": 15,
  "Overall Bonus Conversion (WW → R3) ATK": 16,
  "Overall Bonus Conversion (WW → R3) DEF": 17,

  "Overall Anti-Bonus Conversion (LL → R3)": 18,
  "Overall Anti-Bonus Conversion (LL → R3) ATK": 19,
  "Overall Anti-Bonus Conversion (LL → R3) DEF": 20,

  "Overall Round 3 Win Rate after LW": 21,
  "Overall Round 3 Win Rate after LW ATK": 22,
  "Overall Round 3 Win Rate after LW DEF": 23,

  "Overall Round 3 Win Rate after WL": 24,
  "Overall Round 3 Win Rate after WL ATK": 25,
  "Overall Round 3 Win Rate after WL DEF": 26,

  "Overall Strategy Win Rate ATK Round 1": 26.1,
  "Overall Strategy Win Rate ATK Round 2": 26.2,
  "Overall Strategy Win Rate ATK Round 3": 26.3,
  "Overall Strategy Win Rate DEF Round 1": 26.4,
  "Overall Strategy Win Rate DEF Round 2": 26.5,
  "Overall Strategy Win Rate DEF Round 3": 26.6,

  "Overall Games by Player": 27,
  "Overall Player Win Rate": 28,
  "Overall Win Rate without Player": 29,
  "Overall Player Combo Win Rate (2)": 30,
  "Overall Player Combo Win Rate (3)": 31,
  "Overall Player Combo Win Rate (4)": 32,
  "Overall Player Combo Win Rate (5)": 33,

  "Overall Games by Team Comp": 34,
  "Overall Team Comp Win Rate": 35,
  "Overall Team Comp Average Round Differential": 36,

  "Map Match Win Rate": 101,
  "Map Average Round Differential": 102,
  "Map Total Round Differential": 103,

  "Map Double Pistol Conversion Rate": 104,
  "Map No Pistol Conversion Rate": 105,
  "Map Pistol Win Rate": 106,
  "Map Pistol Win Rate ATK": 107,
  "Map Pistol Win Rate DEF": 108,

  "Map Round 2 Conversion after Pistol Win": 109,
  "Map Round 2 Conversion after Pistol Win ATK": 110,
  "Map Round 2 Conversion after Pistol Win DEF": 111,

  "Map Round 2 Conversion after Pistol Loss": 112,
  "Map Round 2 Conversion after Pistol Loss ATK": 113,
  "Map Round 2 Conversion after Pistol Loss DEF": 114,

  "Map Bonus Conversion (WW → R3)": 115,
  "Map Bonus Conversion (WW → R3) ATK": 116,
  "Map Bonus Conversion (WW → R3) DEF": 117,

  "Map Anti-Bonus Conversion (LL → R3)": 118,
  "Map Anti-Bonus Conversion (LL → R3) ATK": 119,
  "Map Anti-Bonus Conversion (LL → R3) DEF": 120,

  "Map Round 3 Win Rate after LW": 121,
  "Map Round 3 Win Rate after LW ATK": 122,
  "Map Round 3 Win Rate after LW DEF": 123,

  "Map Round 3 Win Rate after WL": 124,
  "Map Round 3 Win Rate after WL ATK": 125,
  "Map Round 3 Win Rate after WL DEF": 126,

  "Map Strategy Win Rate ATK Round 1": 126.1,
  "Map Strategy Win Rate ATK Round 2": 126.2,
  "Map Strategy Win Rate ATK Round 3": 126.3,
  "Map Strategy Win Rate DEF Round 1": 126.4,
  "Map Strategy Win Rate DEF Round 2": 126.5,
  "Map Strategy Win Rate DEF Round 3": 126.6,

  "Map Games by Team Comp": 127,
  "Map Team Comp Win Rate": 128,
  "Map Team Comp Average Round Differential": 129,

  "Map Games by Player": 130,
  "Map Player Win Rate": 131,
  "Map Win Rate without Player": 132,
  "Map Player Combo Win Rate (2)": 133,
  "Map Player Combo Win Rate (3)": 134,
  "Map Player Combo Win Rate (4)": 135,
  "Map Player Combo Win Rate (5)": 136,

  "Map+Comp Match Win Rate": 201,
  "Map+Comp Average Round Differential": 202,
  "Map+Comp Total Round Differential": 203,

  "Map+Comp Double Pistol Conversion Rate": 204,
  "Map+Comp No Pistol Conversion Rate": 205,
  "Map+Comp Pistol Win Rate": 206,
  "Map+Comp Pistol Win Rate ATK": 207,
  "Map+Comp Pistol Win Rate DEF": 208,

  "Map+Comp Round 2 Conversion after Pistol Win": 209,
  "Map+Comp Round 2 Conversion after Pistol Win ATK": 210,
  "Map+Comp Round 2 Conversion after Pistol Win DEF": 211,

  "Map+Comp Round 2 Conversion after Pistol Loss": 212,
  "Map+Comp Round 2 Conversion after Pistol Loss ATK": 213,
  "Map+Comp Round 2 Conversion after Pistol Loss DEF": 214,

  "Map+Comp Bonus Conversion (WW → R3)": 215,
  "Map+Comp Bonus Conversion (WW → R3) ATK": 216,
  "Map+Comp Bonus Conversion (WW → R3) DEF": 217,

  "Map+Comp Anti-Bonus Conversion (LL → R3)": 218,
  "Map+Comp Anti-Bonus Conversion (LL → R3) ATK": 219,
  "Map+Comp Anti-Bonus Conversion (LL → R3) DEF": 220,

  "Map+Comp Round 3 Win Rate after LW": 221,
  "Map+Comp Round 3 Win Rate after LW ATK": 222,
  "Map+Comp Round 3 Win Rate after LW DEF": 223,

  "Map+Comp Round 3 Win Rate after WL": 224,
  "Map+Comp Round 3 Win Rate after WL ATK": 225,
  "Map+Comp Round 3 Win Rate after WL DEF": 226,

  "Map+Comp Strategy Win Rate ATK Round 1": 226.1,
  "Map+Comp Strategy Win Rate ATK Round 2": 226.2,
  "Map+Comp Strategy Win Rate ATK Round 3": 226.3,
  "Map+Comp Strategy Win Rate DEF Round 1": 226.4,
  "Map+Comp Strategy Win Rate DEF Round 2": 226.5,
  "Map+Comp Strategy Win Rate DEF Round 3": 226.6,
};

const ROUND_TAG_SLOT_DEFS = [
  {
    side: "atk",
    roundKey: "r1",
    sideLabel: "ATK Round 1",
    statSuffix: "ATK Round 1",
  },
  {
    side: "atk",
    roundKey: "r2",
    sideLabel: "ATK Round 2",
    statSuffix: "ATK Round 2",
  },
  {
    side: "atk",
    roundKey: "r3",
    sideLabel: "ATK Round 3",
    statSuffix: "ATK Round 3",
  },
  {
    side: "def",
    roundKey: "r1",
    sideLabel: "DEF Round 1",
    statSuffix: "DEF Round 1",
  },
  {
    side: "def",
    roundKey: "r2",
    sideLabel: "DEF Round 2",
    statSuffix: "DEF Round 2",
  },
  {
    side: "def",
    roundKey: "r3",
    sideLabel: "DEF Round 3",
    statSuffix: "DEF Round 3",
  },
];

export const percentFmt = (n) => `${Math.round(Number(n) || 0)}%`;

const safeBool = (v) =>
  v === true ? true : v === false ? false : null;

const normalizeRoundTagValue = (tag) =>
  String(tag || "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeRoundTagList = (tags) => {
  const seen = new Set();
  const next = [];

  (Array.isArray(tags) ? tags : []).forEach((tag) => {
    const normalized = normalizeRoundTagValue(tag);

    if (!normalized) {
      return;
    }

    const dedupeKey = normalized.toLowerCase();

    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    next.push(normalized);
  });

  return next;
};

const getMatchRoundTags = (match, side, roundKey) =>
  normalizeRoundTagList(match?.roundTags?.[side]?.[roundKey]);

const normalizePlayerName = (player) =>
  String(
    player?.username ||
      player?.name ||
      player?.uid ||
      "Unknown"
  ).trim() || "Unknown";

const getMatchPlayers = (match) => {
  const players = Array.isArray(match?.players)
    ? match.players
    : [];

  return players
    .map(normalizePlayerName)
    .filter(Boolean)
    .sort((a, b) =>
      a.localeCompare(
        b,
        undefined,
        { sensitivity: "base" }
      )
    );
};

const chooseCombinations = (arr, size) => {
  const out = [];

  if (
    !Array.isArray(arr) ||
    size < 1 ||
    arr.length < size
  ) {
    return out;
  }

  const walk = (start, path) => {
    if (path.length === size) {
      out.push([...path]);
      return;
    }

    for (
      let i = start;
      i <= arr.length - (size - path.length);
      i += 1
    ) {
      path.push(arr[i]);
      walk(i + 1, path);
      path.pop();
    }
  };

  walk(0, []);

  return out;
};

const sortCompAgents = (compArray = []) =>
  [...compArray].sort((a, b) => {
    const roleA =
      ROLE_ORDER[
        AGENT_ROLE_MAP[a] || "unknown"
      ];

    const roleB =
      ROLE_ORDER[
        AGENT_ROLE_MAP[b] || "unknown"
      ];

    if (roleA !== roleB) {
      return roleA - roleB;
    }

    return String(a).localeCompare(
      String(b),
      undefined,
      { sensitivity: "base" }
    );
  });

const normalizeCompKey = (compArray = []) => {
  const clean = Array.isArray(compArray)
    ? compArray
        .map((agent) =>
          String(agent || "").trim()
        )
        .filter(Boolean)
    : [];

  if (!clean.length) {
    return "Unknown Comp";
  }

  return sortCompAgents(clean).join(" / ");
};

const getRoundDifferential = knownRoundDifference;
const getMatchResult = knownMatchResult;

const clampPercent = (n) =>
  Math.max(
    0,
    Math.min(
      100,
      Number(n) || 0
    )
  );

const computeStandardErrorPercent = (
  made,
  total
) => {
  const n = Number(total);
  const x = Number(made);

  if (
    !Number.isFinite(n) ||
    n <= 0 ||
    !Number.isFinite(x)
  ) {
    return null;
  }

  const p = x / n;
  const se =
    Math.sqrt(
      (p * (1 - p)) / n
    ) * 100;

  return Number.isFinite(se)
    ? se
    : null;
};

const computeWilsonCI = (
  made,
  total,
  z = 1.96
) => {
  const n = Number(total);
  const x = Number(made);

  if (
    !Number.isFinite(n) ||
    n <= 0 ||
    !Number.isFinite(x)
  ) {
    return null;
  }

  const p = x / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;

  const center =
    (p + z2 / (2 * n)) /
    denom;

  const margin =
    (
      z *
      Math.sqrt(
        (p * (1 - p)) / n +
          z2 / (4 * n * n)
      )
    ) /
    denom;

  return {
    low: clampPercent(
      (center - margin) * 100
    ),
    high: clampPercent(
      (center + margin) * 100
    ),
  };
};

function erfApprox(x) {
  const sign =
    x < 0 ? -1 : 1;

  const absX =
    Math.abs(x);

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const t =
    1 / (1 + p * absX);

  const y =
    1 -
    (
      (
        (
          (
            (
              a5 * t +
              a4
            ) *
              t +
            a3
          ) *
            t +
          a2
        ) *
          t +
        a1
      ) *
        t *
        Math.exp(
          -absX * absX
        )
    );

  return sign * y;
}

function normalCdf(x) {
  return (
    0.5 *
    (
      1 +
      erfApprox(
        x / Math.sqrt(2)
      )
    )
  );
}

function computePValuePercentVs50(
  made,
  total
) {
  const n = Number(total);
  const x = Number(made);

  if (
    !Number.isFinite(n) ||
    n <= 0 ||
    !Number.isFinite(x)
  ) {
    return null;
  }

  const p0 = 0.5;
  const phat = x / n;

  const se0 =
    Math.sqrt(
      (p0 * (1 - p0)) /
        n
    );

  if (
    !Number.isFinite(se0) ||
    se0 <= 0
  ) {
    return null;
  }

  const z =
    (phat - p0) /
    se0;

  const pValue =
    2 *
    (
      1 -
      normalCdf(
        Math.abs(z)
      )
    );

  if (
    !Number.isFinite(pValue)
  ) {
    return null;
  }

  return Math.max(
    0,
    Math.min(
      1,
      pValue
    )
  );
}

export function getPValueBandLabel(
  pValue
) {
  const n =
    Number(pValue);

  if (!Number.isFinite(n)) {
    return "n.d.";
  }

  if (n < 0.05) {
    return "Significant";
  }

  if (n < 0.1) {
    return "Grey zone";
  }

  return "Not significant";
}

function evaluateSideMetric(
  match,
  side,
  metricValue
) {
  const r1 =
    safeBool(
      match?.roundWins?.[side]?.r1
    );

  const r2 =
    safeBool(
      match?.roundWins?.[side]?.r2
    );

  const r3 =
    safeBool(
      match?.roundWins?.[side]?.r3
    );

  switch (metricValue) {
    case "pistol_wr":
      return r1 === null
        ? null
        : {
            made: r1 ? 1 : 0,
            total: 1,
          };

    case "round2_after_win_pistol":
      if (
        r1 !== true ||
        r2 === null
      ) {
        return null;
      }

      return {
        made: r2 ? 1 : 0,
        total: 1,
      };

    case "round2_after_lose_pistol":
      if (
        r1 !== false ||
        r2 === null
      ) {
        return null;
      }

      return {
        made: r2 ? 1 : 0,
        total: 1,
      };

    case "bonus_conversion":
      if (
        r1 !== true ||
        r2 !== true ||
        r3 === null
      ) {
        return null;
      }

      return {
        made: r3 ? 1 : 0,
        total: 1,
      };

    case "anti_bonus_conversion":
      if (
        r1 !== false ||
        r2 !== false ||
        r3 === null
      ) {
        return null;
      }

      return {
        made: r3 ? 1 : 0,
        total: 1,
      };

    case "round3_after_lw":
      if (
        r1 !== false ||
        r2 !== true ||
        r3 === null
      ) {
        return null;
      }

      return {
        made: r3 ? 1 : 0,
        total: 1,
      };

    case "round3_after_wl":
      if (
        r1 !== true ||
        r2 !== false ||
        r3 === null
      ) {
        return null;
      }

      return {
        made: r3 ? 1 : 0,
        total: 1,
      };

    default:
      return null;
  }
}

function evaluateMatchMetric(
  match,
  metricValue
) {
  const pistolWins = knownPistolWins(match);

  const result =
    getMatchResult(match);

  const rd =
    getRoundDifferential(match);

  switch (metricValue) {
    case "map_wr":
      if (
        !["W", "L", "T"].includes(
          result
        )
      ) {
        return null;
      }

      return {
        made:
          result === "W"
            ? 1
            : 0,
        total: 1,
      };

    case "both_pistols_match":
      if(pistolWins===null)return null;
      return {
        made:
          pistolWins === 2
            ? 1
            : 0,
        total: 1,
      };

    case "no_pistols_match":
      if(pistolWins===null)return null;
      return {
        made:
          pistolWins === 0
            ? 1
            : 0,
        total: 1,
      };

    case "avg_round_diff":
      if (rd === null) {
        return null;
      }

      return {
        sum: rd,
        total: 1,
      };

    case "total_round_diff":
      if (rd === null) {
        return null;
      }

      return {
        sum: rd,
        total: 1,
      };

    default:
      return null;
  }
}

function makeNoDataRow({
  id,
  category,
  displayLabel,
  baseSortKey,
  scope,
  side = "Match",
  sectionType,
  mapName = "",
  compName = "",
  playerName = "",
  comboKey = "",
}) {
  return {
    id,
    category,
    displayLabel,
    baseSortKey,
    scope,
    side,
    sectionType,
    mapName,
    compName,
    playerName,
    comboKey,

    valueType: "nd",
    value: null,

    made: null,
    total: null,
    sample: 0,

    se: null,
    ciLow: null,
    ciHigh: null,
    pValue: null,

    isNoData: true,
  };
}

function makePercentRow({
  id,
  category,
  displayLabel,
  baseSortKey,
  scope,
  side = "Match",
  sectionType,
  made,
  total,
  mapName = "",
  compName = "",
  playerName = "",
  comboKey = "",
}) {
  if (
    !Number.isFinite(
      Number(total)
    ) ||
    Number(total) <= 0
  ) {
    return makeNoDataRow({
      id,
      category,
      displayLabel,
      baseSortKey,
      scope,
      side,
      sectionType,
      mapName,
      compName,
      playerName,
      comboKey,
    });
  }

  const safeMade =
    Number(made || 0);

  const safeTotal =
    Number(total || 0);

  const se =
    computeStandardErrorPercent(
      safeMade,
      safeTotal
    );

  const ci =
    computeWilsonCI(
      safeMade,
      safeTotal
    );

  const pValue =
    computePValuePercentVs50(
      safeMade,
      safeTotal
    );

  return {
    id,
    category,
    displayLabel,
    baseSortKey,
    scope,
    side,
    sectionType,
    mapName,
    compName,
    playerName,
    comboKey,

    valueType: "percent",

    value:
      safeTotal > 0
        ? (
            safeMade /
            safeTotal
          ) *
          100
        : null,

    made: safeMade,
    total: safeTotal,
    sample: safeTotal,

    se,

    ciLow:
      ci?.low ??
      null,

    ciHigh:
      ci?.high ??
      null,

    pValue,

    isNoData: false,
  };
}

function makeNumberRow({
  id,
  category,
  displayLabel,
  baseSortKey,
  scope,
  side = "Match",
  sectionType,
  sum,
  total,
  mode = "average",
  mapName = "",
  compName = "",
  playerName = "",
  comboKey = "",
}) {
  const safeTotal =
    Number(total || 0);

  if (
    !Number.isFinite(
      safeTotal
    ) ||
    safeTotal <= 0
  ) {
    return makeNoDataRow({
      id,
      category,
      displayLabel,
      baseSortKey,
      scope,
      side,
      sectionType,
      mapName,
      compName,
      playerName,
      comboKey,
    });
  }

  const safeSum =
    Number(sum || 0);

  const value =
    mode === "sum"
      ? safeSum
      : safeSum / safeTotal;

  return {
    id,
    category,
    displayLabel,
    baseSortKey,
    scope,
    side,
    sectionType,
    mapName,
    compName,
    playerName,
    comboKey,

    valueType: "number",
    value,

    made:
      mode === "sum"
        ? safeSum
        : null,

    total: safeTotal,
    sample: safeTotal,

    se: null,
    ciLow: null,
    ciHigh: null,
    pValue: null,

    isNoData: false,
  };
}

function makeCountRow({
  id,
  category,
  displayLabel,
  baseSortKey,
  scope,
  side = "Match",
  sectionType,
  value,
  mapName = "",
  compName = "",
  playerName = "",
  comboKey = "",
}) {
  const safeValue =
    Number(value || 0);

  if (
    !Number.isFinite(
      safeValue
    ) ||
    safeValue <= 0
  ) {
    return makeNoDataRow({
      id,
      category,
      displayLabel,
      baseSortKey,
      scope,
      side,
      sectionType,
      mapName,
      compName,
      playerName,
      comboKey,
    });
  }

  return {
    id,
    category,
    displayLabel,
    baseSortKey,
    scope,
    side,
    sectionType,
    mapName,
    compName,
    playerName,
    comboKey,

    valueType: "count",
    value: safeValue,

    made: null,
    total: null,
    sample: safeValue,

    se: null,
    ciLow: null,
    ciHigh: null,
    pValue: null,

    isNoData: false,
  };
}

function buildDefaultPriority(
  row
) {
  const sectionRank =
    SECTION_ORDER[
      row.sectionType
    ] ?? 999;

  const categoryRank =
    CATEGORY_ORDER[
      row.category
    ] ?? 999;

  const statRank =
    STAT_ORDER[
      row.baseSortKey
    ] ?? 999;

  const scopeRank =
    String(
      row.scope || ""
    ).toLowerCase();

  const compRank =
    String(
      row.compName || ""
    ).toLowerCase();

  const playerRank =
    String(
      row.playerName || ""
    ).toLowerCase();

  const comboRank =
    String(
      row.comboKey || ""
    ).toLowerCase();

  return [
    sectionRank,
    categoryRank,
    statRank,
    scopeRank,
    compRank,
    playerRank,
    comboRank,
  ];
}

function compareDefaultPriority(
  a,
  b
) {
  const aa =
    buildDefaultPriority(a);

  const bb =
    buildDefaultPriority(b);

  for (
    let i = 0;
    i < aa.length;
    i += 1
  ) {
    if (aa[i] < bb[i]) {
      return -1;
    }

    if (aa[i] > bb[i]) {
      return 1;
    }
  }

  return 0;
}

function buildScopeMatches(
  matches,
  predicate
) {
  return matches.filter(
    predicate
  );
}

function aggregateMatchPercent(
  matches,
  metricKey
) {
  let made = 0;
  let total = 0;

  matches.forEach(
    (match) => {
      const res =
        evaluateMatchMetric(
          match,
          metricKey
        );

      if (!res) {
        return;
      }

      made +=
        Number(
          res.made || 0
        );

      total +=
        Number(
          res.total || 0
        );
    }
  );

  return {
    made,
    total,
  };
}

function aggregateMatchNumber(
  matches,
  metricKey
) {
  let sum = 0;
  let total = 0;

  matches.forEach(
    (match) => {
      const res =
        evaluateMatchMetric(
          match,
          metricKey
        );

      if (!res) {
        return;
      }

      sum +=
        Number(
          res.sum || 0
        );

      total +=
        Number(
          res.total || 0
        );
    }
  );

  return {
    sum,
    total,
  };
}

function aggregateSidePercent(
  matches,
  side,
  metricKey
) {
  let made = 0;
  let total = 0;

  matches.forEach(
    (match) => {
      const res =
        evaluateSideMetric(
          match,
          side,
          metricKey
        );

      if (!res) {
        return;
      }

      made +=
        Number(
          res.made || 0
        );

      total +=
        Number(
          res.total || 0
        );
    }
  );

  return {
    made,
    total,
  };
}

function aggregateTaggedRoundPercent(
  matches,
  side,
  roundKey,
  strategyTag
) {
  let made = 0;
  let total = 0;

  matches.forEach(
    (match) => {
      const roundResult =
        safeBool(
          match?.roundWins?.[
            side
          ]?.[roundKey]
        );

      if (
        roundResult ===
        null
      ) {
        return;
      }

      const hasStrategyTag =
        getMatchRoundTags(
          match,
          side,
          roundKey
        ).some(
          (tag) =>
            normalizeRoundTagValue(
              tag
            ).toLowerCase() ===
            strategyTag.toLowerCase()
        );

      if (!hasStrategyTag) {
        return;
      }

      total += 1;

      if (
        roundResult ===
        true
      ) {
        made += 1;
      }
    }
  );

  return {
    made,
    total,
  };
}

function getDistinctStrategyTags(
  matches,
  side,
  roundKey
) {
  const deduped =
    new Map();

  matches.forEach(
    (match) => {
      getMatchRoundTags(
        match,
        side,
        roundKey
      ).forEach(
        (tag) => {
          const dedupeKey =
            normalizeRoundTagValue(
              tag
            ).toLowerCase();

          if (
            !deduped.has(
              dedupeKey
            )
          ) {
            deduped.set(
              dedupeKey,
              tag
            );
          }
        }
      );
    }
  );

  return Array.from(
    deduped.values()
  ).sort(
    (a, b) =>
      a.localeCompare(
        b,
        undefined,
        {
          sensitivity:
            "base",
        }
      )
  );
}

function getDistinctMaps(
  matches
) {
  return Array.from(
    new Set(
      matches.map(
        (m) =>
          m.map ||
          "Unknown"
      )
    )
  ).sort(
    (a, b) =>
      String(a).localeCompare(
        String(b),
        undefined,
        {
          sensitivity:
            "base",
        }
      )
  );
}

function getDistinctComps(
  matches
) {
  return Array.from(
    new Set(
      matches.map(
        (m) =>
          normalizeCompKey(
            m?.comp
          )
      )
    )
  ).sort(
    (a, b) =>
      String(a).localeCompare(
        String(b),
        undefined,
        {
          sensitivity:
            "base",
        }
      )
  );
}

function getDistinctPlayers(
  matches
) {
  const set =
    new Set();

  matches.forEach(
    (match) => {
      getMatchPlayers(
        match
      ).forEach(
        (player) =>
          set.add(player)
      );
    }
  );

  return Array.from(
    set
  ).sort(
    (a, b) =>
      String(a).localeCompare(
        String(b),
        undefined,
        {
          sensitivity:
            "base",
        }
      )
  );
}

function buildStrategyRows({
  matches,
  sectionType,
  scope,
  labelPrefix,
  baseKeyPrefix,
  mapName = "",
  compName = "",
}) {
  const rows = [];

  ROUND_TAG_SLOT_DEFS.forEach(
    ({
      side,
      roundKey,
      sideLabel,
      statSuffix,
    }) => {
      const strategyTags =
        getDistinctStrategyTags(
          matches,
          side,
          roundKey
        );

      strategyTags.forEach(
        (strategyTag) => {
          const {
            made,
            total,
          } =
            aggregateTaggedRoundPercent(
              matches,
              side,
              roundKey,
              strategyTag
            );

          rows.push(
            makePercentRow({
              id: `${sectionType}-strategy-${side}-${roundKey}-${mapName}-${compName || "all"}-${strategyTag}`,
              category:
                "Strategies",
              displayLabel:
                `${labelPrefix} Strategy Win Rate ${sideLabel}`,
              baseSortKey:
                `${baseKeyPrefix} Strategy Win Rate ${statSuffix}`,
              scope,
              side:
                sideLabel,
              sectionType,
              mapName,
              compName,
              comboKey:
                strategyTag,
              made,
              total,
            })
          );
        }
      );
    }
  );

  return rows;
}

function buildRosterRowsForMatches({
  matches,
  sectionType,
  scope,
  labelPrefix,
  baseKeyPrefix,
  mapName = "",
}) {
  const rows = [];

  const players =
    getDistinctPlayers(
      matches
    );

  players.forEach(
    (player) => {
      let gamesWithPlayer =
        0;

      let winsWithPlayer =
        0;

      let gamesWithoutPlayer =
        0;

      let winsWithoutPlayer =
        0;

      matches.forEach(
        (match) => {
          const result =
            getMatchResult(
              match
            );

          if (
            ![
              "W",
              "L",
              "T",
            ].includes(
              result
            )
          ) {
            return;
          }

          const matchPlayers =
            new Set(
              getMatchPlayers(
                match
              )
            );

          if (
            matchPlayers.has(
              player
            )
          ) {
            gamesWithPlayer +=
              1;

            if (
              result === "W"
            ) {
              winsWithPlayer +=
                1;
            }
          } else {
            gamesWithoutPlayer +=
              1;

            if (
              result === "W"
            ) {
              winsWithoutPlayer +=
                1;
            }
          }
        }
      );

      rows.push(
        makeCountRow({
          id:
            `${sectionType}-games-player-${mapName}-${player}`,

          category:
            "Roster",

          displayLabel:
            `${labelPrefix} Games by Player ${player}`,

          baseSortKey:
            `${baseKeyPrefix} Games by Player`,

          scope,
          sectionType,
          playerName:
            player,
          mapName,

          value:
            gamesWithPlayer,
        })
      );

      rows.push(
        makePercentRow({
          id:
            `${sectionType}-player-wr-${mapName}-${player}`,

          category:
            "Roster",

          displayLabel:
            `${labelPrefix} Player Win Rate ${player}`,

          baseSortKey:
            `${baseKeyPrefix} Player Win Rate`,

          scope,
          sectionType,
          playerName:
            player,
          mapName,

          made:
            winsWithPlayer,

          total:
            gamesWithPlayer,
        })
      );

      rows.push(
        makePercentRow({
          id:
            `${sectionType}-without-player-wr-${mapName}-${player}`,

          category:
            "Roster",

          displayLabel:
            `${labelPrefix} Win Rate without Player ${player}`,

          baseSortKey:
            `${baseKeyPrefix} Win Rate without Player`,

          scope,
          sectionType,
          playerName:
            player,
          mapName,

          made:
            winsWithoutPlayer,

          total:
            gamesWithoutPlayer,
        })
      );

      [2, 3, 4, 5].forEach(
        (comboSize) => {
          const comboStats =
            new Map();

          matches.forEach(
            (match) => {
              const result =
                getMatchResult(
                  match
                );

              if (
                ![
                  "W",
                  "L",
                  "T",
                ].includes(
                  result
                )
              ) {
                return;
              }

              const matchPlayers =
                getMatchPlayers(
                  match
                );

              if (
                !matchPlayers.includes(
                  player
                )
              ) {
                return;
              }

              if (
                matchPlayers.length <
                comboSize
              ) {
                return;
              }

              const combos =
                chooseCombinations(
                  matchPlayers,
                  comboSize
                ).filter(
                  (combo) =>
                    combo.includes(
                      player
                    )
                );

              combos.forEach(
                (combo) => {
                  const key =
                    combo.join(
                      " + "
                    );

                  const curr =
                    comboStats.get(
                      key
                    ) || {
                      games: 0,
                      wins: 0,
                    };

                  curr.games +=
                    1;

                  if (
                    result ===
                    "W"
                  ) {
                    curr.wins +=
                      1;
                  }

                  comboStats.set(
                    key,
                    curr
                  );
                }
              );
            }
          );

          if (
            comboStats.size ===
            0
          ) {
            rows.push(
              makeNoDataRow({
                id:
                  `${sectionType}-combo-${comboSize}-${mapName}-${player}-none`,

                category:
                  "Roster",

                displayLabel:
                  `${labelPrefix} Player Combo Win Rate (${comboSize})`,

                baseSortKey:
                  `${baseKeyPrefix} Player Combo Win Rate (${comboSize})`,

                scope,
                sectionType,
                playerName:
                  player,
                mapName,
                comboKey: "",
              })
            );
          } else {
            Array.from(
              comboStats.entries()
            )
              .sort(
                (a, b) =>
                  String(
                    a[0]
                  ).localeCompare(
                    String(
                      b[0]
                    ),
                    undefined,
                    {
                      sensitivity:
                        "base",
                    }
                  )
              )
              .forEach(
                ([
                  comboKey,
                  stats,
                ]) => {
                  rows.push(
                    makePercentRow({
                      id:
                        `${sectionType}-combo-${comboSize}-${mapName}-${player}-${comboKey}`,

                      category:
                        "Roster",

                      displayLabel:
                        `${labelPrefix} Player Combo Win Rate (${comboSize})`,

                      baseSortKey:
                        `${baseKeyPrefix} Player Combo Win Rate (${comboSize})`,

                      scope,
                      sectionType,
                      playerName:
                        player,
                      mapName,
                      comboKey,

                      made:
                        stats.wins,

                      total:
                        stats.games,
                    })
                  );
                }
              );
          }
        }
      );
    }
  );

  return rows;
}

function buildOverallCompRows(
  matches
) {
  const rows = [];

  const scope =
    "Overall";

  const labelPrefix =
    "Overall";

  const baseKeyPrefix =
    "Overall";

  const byComp =
    new Map();

  getDistinctComps(
    matches
  ).forEach(
    (compName) => {
      byComp.set(
        compName,
        {
          games: 0,
          wins: 0,
          rdGames: 0,
          rdSum: 0,
        }
      );
    }
  );

  matches.forEach(
    (match) => {
      const compName =
        normalizeCompKey(
          match?.comp
        );

      const curr =
        byComp.get(
          compName
        ) || {
          games: 0,
          wins: 0,
          rdGames: 0,
          rdSum: 0,
        };

      const result =
        getMatchResult(
          match
        );

      const rd =
        getRoundDifferential(
          match
        );

      if (
        [
          "W",
          "L",
          "T",
        ].includes(
          result
        )
      ) {
        curr.games +=
          1;

        if (
          result === "W"
        ) {
          curr.wins +=
            1;
        }
      }

      if (
        rd !== null
      ) {
        curr.rdGames +=
          1;

        curr.rdSum +=
          rd;
      }

      byComp.set(
        compName,
        curr
      );
    }
  );

  Array.from(
    byComp.entries()
  )
    .sort(
      (a, b) =>
        String(
          a[0]
        ).localeCompare(
          String(
            b[0]
          ),
          undefined,
          {
            sensitivity:
              "base",
          }
        )
    )
    .forEach(
      ([
        compName,
        stats,
      ]) => {
        rows.push(
          makeCountRow({
            id:
              `overall-comp-games-${compName}`,

            category:
              "Comps",

            displayLabel:
              `${labelPrefix} Games by Team Comp`,

            baseSortKey:
              `${baseKeyPrefix} Games by Team Comp`,

            scope,
            sectionType:
              "overall",

            compName,

            value:
              stats.games,
          })
        );

        rows.push(
          makePercentRow({
            id:
              `overall-comp-wr-${compName}`,

            category:
              "Comps",

            displayLabel:
              `${labelPrefix} Team Comp Win Rate`,

            baseSortKey:
              `${baseKeyPrefix} Team Comp Win Rate`,

            scope,
            sectionType:
              "overall",

            compName,

            made:
              stats.wins,

            total:
              stats.games,
          })
        );

        rows.push(
          makeNumberRow({
            id:
              `overall-comp-rd-${compName}`,

            category:
              "Comps",

            displayLabel:
              `${labelPrefix} Team Comp Average Round Differential`,

            baseSortKey:
              `${baseKeyPrefix} Team Comp Average Round Differential`,

            scope,
            sectionType:
              "overall",

            compName,

            sum:
              stats.rdSum,

            total:
              stats.rdGames,

            mode:
              "average",
          })
        );
      }
    );

  if (
    rows.length ===
    0
  ) {
    rows.push(
      makeNoDataRow({
        id:
          "overall-comp-games-none",

        category:
          "Comps",

        displayLabel:
          "Overall Games by Team Comp",

        baseSortKey:
          "Overall Games by Team Comp",

        scope,
        sectionType:
          "overall",
      }),

      makeNoDataRow({
        id:
          "overall-comp-wr-none",

        category:
          "Comps",

        displayLabel:
          "Overall Team Comp Win Rate",

        baseSortKey:
          "Overall Team Comp Win Rate",

        scope,
        sectionType:
          "overall",
      }),

      makeNoDataRow({
        id:
          "overall-comp-rd-none",

        category:
          "Comps",

        displayLabel:
          "Overall Team Comp Average Round Differential",

        baseSortKey:
          "Overall Team Comp Average Round Differential",

        scope,
        sectionType:
          "overall",
      })
    );
  }

  return rows;
}

function buildMapCompBreakoutRows(
  mapMatches,
  mapName
) {
  const rows = [];

  const byComp =
    new Map();

  getDistinctComps(
    mapMatches
  ).forEach(
    (compName) => {
      byComp.set(
        compName,
        {
          games: 0,
          wins: 0,
          rdGames: 0,
          rdSum: 0,
        }
      );
    }
  );

  mapMatches.forEach(
    (match) => {
      const compName =
        normalizeCompKey(
          match?.comp
        );

      const curr =
        byComp.get(
          compName
        ) || {
          games: 0,
          wins: 0,
          rdGames: 0,
          rdSum: 0,
        };

      const result =
        getMatchResult(
          match
        );

      const rd =
        getRoundDifferential(
          match
        );

      if (
        [
          "W",
          "L",
          "T",
        ].includes(
          result
        )
      ) {
        curr.games +=
          1;

        if (
          result === "W"
        ) {
          curr.wins +=
            1;
        }
      }

      if (
        rd !== null
      ) {
        curr.rdGames +=
          1;

        curr.rdSum +=
          rd;
      }

      byComp.set(
        compName,
        curr
      );
    }
  );

  Array.from(
    byComp.entries()
  )
    .sort(
      (a, b) =>
        String(
          a[0]
        ).localeCompare(
          String(
            b[0]
          ),
          undefined,
          {
            sensitivity:
              "base",
          }
        )
    )
    .forEach(
      ([
        compName,
        stats,
      ]) => {
        const scope =
          mapName;

        rows.push(
          makeCountRow({
            id:
              `map-comp-breakout-games-${mapName}-${compName}`,

            category:
              "Comps",

            displayLabel:
              `${mapName} Listing of Team Comp`,

            baseSortKey:
              "Map Games by Team Comp",

            scope,

            sectionType:
              "mapSpecific",

            mapName,
            compName,

            value:
              stats.games,
          })
        );

        rows.push(
          makePercentRow({
            id:
              `map-comp-breakout-wr-${mapName}-${compName}`,

            category:
              "Comps",

            displayLabel:
              `${mapName} ${compName} Win Rate`,

            baseSortKey:
              "Map Team Comp Win Rate",

            scope,

            sectionType:
              "mapSpecific",

            mapName,
            compName,

            made:
              stats.wins,

            total:
              stats.games,
          })
        );

        rows.push(
          makeNumberRow({
            id:
              `map-comp-breakout-rd-${mapName}-${compName}`,

            category:
              "Comps",

            displayLabel:
              `${mapName} ${compName} Average Round Differential`,

            baseSortKey:
              "Map Team Comp Average Round Differential",

            scope,

            sectionType:
              "mapSpecific",

            mapName,
            compName,

            sum:
              stats.rdSum,

            total:
              stats.rdGames,

            mode:
              "average",
          })
        );
      }
    );

  if (
    rows.length ===
    0
  ) {
    rows.push(
      makeNoDataRow({
        id:
          `map-comp-breakout-none-games-${mapName}`,

        category:
          "Comps",

        displayLabel:
          `${mapName} Listing of Team Comp`,

        baseSortKey:
          "Map Games by Team Comp",

        scope:
          mapName,

        sectionType:
          "mapSpecific",

        mapName,
      })
    );
  }

  return rows;
}

function buildCorePistolConversionRows({
  matches,
  sectionType,
  scope,
  labelPrefix,
  baseKeyPrefix,
  mapName = "",
  compName = "",
}) {
  const rows = [];

  const matchPercentMetrics = [
    {
      metricKey:
        "map_wr",

      category:
        "Core",

      displayLabel:
        `${labelPrefix} Match Win Rate`,

      baseSortKey:
        `${baseKeyPrefix} Match Win Rate`,
    },

    {
      metricKey:
        "both_pistols_match",

      category:
        "Pistols",

      displayLabel:
        `${labelPrefix} Double Pistol Conversion Rate`,

      baseSortKey:
        `${baseKeyPrefix} Double Pistol Conversion Rate`,
    },

    {
      metricKey:
        "no_pistols_match",

      category:
        "Pistols",

      displayLabel:
        `${labelPrefix} No Pistol Conversion Rate`,

      baseSortKey:
        `${baseKeyPrefix} No Pistol Conversion Rate`,
    },
  ];

  const matchNumberMetrics = [
    {
      metricKey:
        "avg_round_diff",

      category:
        "Core",

      displayLabel:
        `${labelPrefix} Average Round Differential`,

      baseSortKey:
        `${baseKeyPrefix} Average Round Differential`,

      mode:
        "average",
    },

    {
      metricKey:
        "total_round_diff",

      category:
        "Core",

      displayLabel:
        `${labelPrefix} Total Round Differential`,

      baseSortKey:
        `${baseKeyPrefix} Total Round Differential`,

      mode:
        "sum",
    },
  ];

  const sideMetricDefs = [
    {
      metricKey:
        "pistol_wr",

      category:
        "Pistols",

      labels: {
        both:
          `${labelPrefix} Pistol Win Rate`,

        atk:
          `${labelPrefix} Pistol Win Rate ATK`,

        def:
          `${labelPrefix} Pistol Win Rate DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Pistol Win Rate`,

        atk:
          `${baseKeyPrefix} Pistol Win Rate ATK`,

        def:
          `${baseKeyPrefix} Pistol Win Rate DEF`,
      },
    },

    {
      metricKey:
        "round2_after_win_pistol",

      category:
        "Conversions",

      labels: {
        both:
          `${labelPrefix} Round 2 Conversion after Pistol Win`,

        atk:
          `${labelPrefix} Round 2 Conversion after Pistol Win ATK`,

        def:
          `${labelPrefix} Round 2 Conversion after Pistol Win DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Round 2 Conversion after Pistol Win`,

        atk:
          `${baseKeyPrefix} Round 2 Conversion after Pistol Win ATK`,

        def:
          `${baseKeyPrefix} Round 2 Conversion after Pistol Win DEF`,
      },
    },

    {
      metricKey:
        "round2_after_lose_pistol",

      category:
        "Conversions",

      labels: {
        both:
          `${labelPrefix} Round 2 Conversion after Pistol Loss`,

        atk:
          `${labelPrefix} Round 2 Conversion after Pistol Loss ATK`,

        def:
          `${labelPrefix} Round 2 Conversion after Pistol Loss DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Round 2 Conversion after Pistol Loss`,

        atk:
          `${baseKeyPrefix} Round 2 Conversion after Pistol Loss ATK`,

        def:
          `${baseKeyPrefix} Round 2 Conversion after Pistol Loss DEF`,
      },
    },

    {
      metricKey:
        "bonus_conversion",

      category:
        "Conversions",

      labels: {
        both:
          `${labelPrefix} Bonus Conversion (WW → R3)`,

        atk:
          `${labelPrefix} Bonus Conversion (WW → R3) ATK`,

        def:
          `${labelPrefix} Bonus Conversion (WW → R3) DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Bonus Conversion (WW → R3)`,

        atk:
          `${baseKeyPrefix} Bonus Conversion (WW → R3) ATK`,

        def:
          `${baseKeyPrefix} Bonus Conversion (WW → R3) DEF`,
      },
    },

    {
      metricKey:
        "anti_bonus_conversion",

      category:
        "Conversions",

      labels: {
        both:
          `${labelPrefix} Anti-Bonus Conversion (LL → R3)`,

        atk:
          `${labelPrefix} Anti-Bonus Conversion (LL → R3) ATK`,

        def:
          `${labelPrefix} Anti-Bonus Conversion (LL → R3) DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Anti-Bonus Conversion (LL → R3)`,

        atk:
          `${baseKeyPrefix} Anti-Bonus Conversion (LL → R3) ATK`,

        def:
          `${baseKeyPrefix} Anti-Bonus Conversion (LL → R3) DEF`,
      },
    },

    {
      metricKey:
        "round3_after_lw",

      category:
        "Conversions",

      labels: {
        both:
          `${labelPrefix} Round 3 Win Rate after LW`,

        atk:
          `${labelPrefix} Round 3 Win Rate after LW ATK`,

        def:
          `${labelPrefix} Round 3 Win Rate after LW DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Round 3 Win Rate after LW`,

        atk:
          `${baseKeyPrefix} Round 3 Win Rate after LW ATK`,

        def:
          `${baseKeyPrefix} Round 3 Win Rate after LW DEF`,
      },
    },

    {
      metricKey:
        "round3_after_wl",

      category:
        "Conversions",

      labels: {
        both:
          `${labelPrefix} Round 3 Win Rate after WL`,

        atk:
          `${labelPrefix} Round 3 Win Rate after WL ATK`,

        def:
          `${labelPrefix} Round 3 Win Rate after WL DEF`,
      },

      sortKeys: {
        both:
          `${baseKeyPrefix} Round 3 Win Rate after WL`,

        atk:
          `${baseKeyPrefix} Round 3 Win Rate after WL ATK`,

        def:
          `${baseKeyPrefix} Round 3 Win Rate after WL DEF`,
      },
    },
  ];

  matchPercentMetrics.forEach(
    (metric) => {
      const {
        made,
        total,
      } =
        aggregateMatchPercent(
          matches,
          metric.metricKey
        );

      rows.push(
        makePercentRow({
          id:
            `${sectionType}-${metric.metricKey}-${mapName}-${compName || "all"}`,

          category:
            metric.category,

          displayLabel:
            metric.displayLabel,

          baseSortKey:
            metric.baseSortKey,

          scope,
          sectionType,
          mapName,
          compName,
          made,
          total,
        })
      );
    }
  );

  matchNumberMetrics.forEach(
    (metric) => {
      const {
        sum,
        total,
      } =
        aggregateMatchNumber(
          matches,
          metric.metricKey
        );

      rows.push(
        makeNumberRow({
          id:
            `${sectionType}-${metric.metricKey}-${mapName}-${compName || "all"}`,

          category:
            metric.category,

          displayLabel:
            metric.displayLabel,

          baseSortKey:
            metric.baseSortKey,

          scope,
          sectionType,
          mapName,
          compName,
          sum,
          total,

          mode:
            metric.mode,
        })
      );
    }
  );

  sideMetricDefs.forEach(
    (metric) => {
      const combinedAtk =
        aggregateSidePercent(
          matches,
          "atk",
          metric.metricKey
        );

      const combinedDef =
        aggregateSidePercent(
          matches,
          "def",
          metric.metricKey
        );

      rows.push(
        makePercentRow({
          id:
            `${sectionType}-${metric.metricKey}-both-${mapName}-${compName || "all"}`,

          category:
            metric.category,

          displayLabel:
            metric.labels.both,

          baseSortKey:
            metric.sortKeys.both,

          scope,
          sectionType,
          mapName,
          compName,

          made:
            combinedAtk.made +
            combinedDef.made,

          total:
            combinedAtk.total +
            combinedDef.total,
        })
      );

      rows.push(
        makePercentRow({
          id:
            `${sectionType}-${metric.metricKey}-atk-${mapName}-${compName || "all"}`,

          category:
            metric.category,

          displayLabel:
            metric.labels.atk,

          baseSortKey:
            metric.sortKeys.atk,

          scope,
          side:
            "ATK",

          sectionType,
          mapName,
          compName,

          made:
            combinedAtk.made,

          total:
            combinedAtk.total,
        })
      );

      rows.push(
        makePercentRow({
          id:
            `${sectionType}-${metric.metricKey}-def-${mapName}-${compName || "all"}`,

          category:
            metric.category,

          displayLabel:
            metric.labels.def,

          baseSortKey:
            metric.sortKeys.def,

          scope,
          side:
            "DEF",

          sectionType,
          mapName,
          compName,

          made:
            combinedDef.made,

          total:
            combinedDef.total,
        })
      );
    }
  );

  return rows.sort(
    compareDefaultPriority
  );
}

export function buildAllRows(
  matches
) {
  const rows = [];

  const overallScope =
    buildScopeMatches(
      matches,
      () => true
    );

  rows.push(
    ...buildCorePistolConversionRows({
      matches:
        overallScope,

      sectionType:
        "overall",

      scope:
        "Overall",

      labelPrefix:
        "Overall",

      baseKeyPrefix:
        "Overall",
    })
  );

  rows.push(
    ...buildStrategyRows({
      matches:
        overallScope,

      sectionType:
        "overall",

      scope:
        "Overall",

      labelPrefix:
        "Overall",

      baseKeyPrefix:
        "Overall",
    })
  );

  rows.push(
    ...buildRosterRowsForMatches({
      matches:
        overallScope,

      sectionType:
        "overall",

      scope:
        "Overall",

      labelPrefix:
        "Overall",

      baseKeyPrefix:
        "Overall",
    })
  );

  rows.push(
    ...buildOverallCompRows(
      overallScope
    )
  );

  const maps =
    getDistinctMaps(
      matches
    );

  maps.forEach(
    (mapName) => {
      const mapMatches =
        buildScopeMatches(
          matches,
          (match) =>
            (
              match.map ||
              "Unknown"
            ) ===
            mapName
        );

      rows.push(
        ...buildCorePistolConversionRows({
          matches:
            mapMatches,

          sectionType:
            "mapSpecific",

          scope:
            mapName,

          labelPrefix:
            mapName,

          baseKeyPrefix:
            "Map",

          mapName,
        })
      );

      rows.push(
        ...buildStrategyRows({
          matches:
            mapMatches,

          sectionType:
            "mapSpecific",

          scope:
            mapName,

          labelPrefix:
            mapName,

          baseKeyPrefix:
            "Map",

          mapName,
        })
      );

      rows.push(
        ...buildMapCompBreakoutRows(
          mapMatches,
          mapName
        )
      );

      rows.push(
        ...buildRosterRowsForMatches({
          matches:
            mapMatches,

          sectionType:
            "mapSpecific",

          scope:
            mapName,

          labelPrefix:
            mapName,

          baseKeyPrefix:
            "Map",

          mapName,
        })
      );

      const compsOnMap =
        getDistinctComps(
          mapMatches
        );

      compsOnMap.forEach(
        (compName) => {
          const mapCompMatches =
            buildScopeMatches(
              mapMatches,
              (match) =>
                normalizeCompKey(
                  match?.comp
                ) ===
                compName
            );

          rows.push(
            ...buildCorePistolConversionRows({
              matches:
                mapCompMatches,

              sectionType:
                "mapCompSpecific",

              scope:
                `${mapName} • ${compName}`,

              labelPrefix:
                `${mapName} ${compName}`,

              baseKeyPrefix:
                "Map+Comp",

              mapName,
              compName,
            })
          );

          rows.push(
            ...buildStrategyRows({
              matches:
                mapCompMatches,

              sectionType:
                "mapCompSpecific",

              scope:
                `${mapName} • ${compName}`,

              labelPrefix:
                `${mapName} ${compName}`,

              baseKeyPrefix:
                "Map+Comp",

              mapName,
              compName,
            })
          );
        }
      );

      if (
        compsOnMap.length ===
        0
      ) {
        rows.push(
          ...buildCorePistolConversionRows({
            matches: [],

            sectionType:
              "mapCompSpecific",

            scope:
              `${mapName} • Unknown Comp`,

            labelPrefix:
              `${mapName} Unknown Comp`,

            baseKeyPrefix:
              "Map+Comp",

            mapName,

            compName:
              "Unknown Comp",
          })
        );

        rows.push(
          ...buildStrategyRows({
            matches: [],

            sectionType:
              "mapCompSpecific",

            scope:
              `${mapName} • Unknown Comp`,

            labelPrefix:
              `${mapName} Unknown Comp`,

            baseKeyPrefix:
              "Map+Comp",

            mapName,

            compName:
              "Unknown Comp",
          })
        );
      }
    }
  );

  return rows.sort(
    compareDefaultPriority
  );
}

const TSD_PREFS_KEY_PREFIX =
  "teamStatsSummaryDashboardPrefs_v3_";

const TSD_SAVED_VIEWS_KEY_PREFIX =
  "teamStatsSummarySavedViews_v1_";

const TSD_GRAPH_DEFAULTS = {
  graphType:
    "line",

  showN:
    true,

  showCI:
    false,

  showPValue:
    false,

  showMarkers:
    true,

  showExactY:
    false,

  showExactX:
    false,

  showGrid:
    true,

  showReferenceLine:
    false,

  referenceValue:
    50,

  dataLabelPosition:
    "above",

  xInterval:
    1,

  yInterval:
    10,

  yMin:
    "",

  yMax:
    "",

  confidenceLevel:
    95,
};

function tsdGraphDefaults(row) {return {...TSD_GRAPH_DEFAULTS,graphType:defaultMetricChartType(row)};}

const TSD_GRAPH_TYPES = [
  {
    value: "line", icon: ChartSpline,

    label:
      "Line",
  },

  {
    value: "area", icon: ChartArea,

    label:
      "Area",
  },

  {
    value: "bar", icon: ChartColumn,

    label:
      "Bar",
  },

  {
    value: "scatter", icon: ChartScatter,

    label:
      "Scatter",
  },

  {
    value: "pie", icon: ChartPie,

    label:
      "Pie",
  },

  {
    value: "donut", icon: Donut,

    label:
      "Donut",
  },
];

const TSD_DEFAULT_STAT_PATTERNS = STUDIO_STARTER_PATTERNS;

const TSD_STAT_MENU_SESSION_KEY_PREFIX =
  "teamStatsSummaryStatMenuSession_v1_";

const TSD_MAX_ANALYTICS_PRESETS =
  6;

const TSD_DASHBOARD_SCALE_VERSION =
  4;

const TSD_DASHBOARD_SCALE_OPTIONS = {
  compact: 50,
  balanced: 100,
  focus: 150,
};

const TSD_DASHBOARD_SCALE_RANGE = {
  min: 25,
  max: 300,
  default: 100,
};

function tsdClampDashboardScale(
  value
) {
  return Math.min(
    TSD_DASHBOARD_SCALE_RANGE.max,
    Math.max(
      TSD_DASHBOARD_SCALE_RANGE.min,
      Number.isFinite(
        Number(value)
      )
        ? Number(value)
        : TSD_DASHBOARD_SCALE_RANGE.default
    )
  );
}

function tsdNormalizeDashboardScale(
  value,
  version
) {
  if (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(
      TSD_DASHBOARD_SCALE_OPTIONS,
      value
    )
  ) {
    return TSD_DASHBOARD_SCALE_OPTIONS[
      value
    ];
  }

  const numericValue =
    Number(value);

  if (
    !Number.isFinite(
      numericValue
    )
  ) {
    return TSD_DASHBOARD_SCALE_RANGE.default;
  }

  const numericVersion =
    Number(version);

  const v3Scale =
    numericVersion >=
    TSD_DASHBOARD_SCALE_VERSION
      ? null
      : numericVersion >= 3
        ? numericValue
        : numericVersion >= 2
          ? 50 +
            numericValue /
              2
          : 50 +
            numericValue;

  const nextValue =
    numericVersion >=
    TSD_DASHBOARD_SCALE_VERSION
      ? numericValue
      : v3Scale <=
          100
        ? 25 +
          (
            v3Scale - 50
          ) *
            1.5
        : 100 +
          (
            v3Scale - 100
          ) *
            4;

  return tsdClampDashboardScale(
    nextValue
  );
}

function tsdLerp(
  min,
  max,
  ratio
) {
  return min + (max - min) * ratio;
}

function tsdInterpolateScaleAnchors(
  scale,
  anchors
) {
  if (
    !Array.isArray(
      anchors
    ) ||
    anchors.length === 0
  ) {
    return 0;
  }

  if (
    scale <=
    anchors[0].scale
  ) {
    return anchors[0].value;
  }

  for (
    let index = 1;
    index < anchors.length;
    index += 1
  ) {
    const previous =
      anchors[
        index - 1
      ];
    const current =
      anchors[index];

    if (
      scale <=
      current.scale
    ) {
      return tsdLerp(
        previous.value,
        current.value,
        (
          scale -
          previous.scale
        ) /
          (
            current.scale -
            previous.scale
          )
      );
    }
  }

  return anchors[
    anchors.length -
      1
  ].value;
}

const TSD_MENU_SCOPE_OPTIONS = [
  {
    value:
      "All scopes",

    label:
      "All views",

    description:
      "Keep the team total, per-map, and map + comp versions in this picker.",
  },

  {
    value:
      "Overall",

    label:
      "Overall only",

    description:
      "Only keep the team-total version of each stat in this picker.",
  },

  {
    value:
      "By Map",

    label:
      "By Map",

    description:
      "Compare how the team performs on each map.",
  },

  {
    value:
      "Map + Comp",

    label:
      "Map + Comp",

    description:
      "Drill into map-specific composition performance.",
  },
];

const TSD_MENU_GROUP_ORDER = [
  "Review & development",
  "All",
  "Starter",
  "Outcomes",
  "Economy",
  "Lineups",
  "Compositions",
  "Strategies",
];

const TSD_MENU_GROUP_META = {
  "Review & development": {title:"Review & development",description:"Focus ratings, Improvement Points, player-agent and preset results, and completeness."},
  All: {
    title:
      "All metrics",

    description:
      "Everything available inside the current scope and search.",
  },

  Starter: {
    title:
      "Starter set",

    description:
      "High-signal statistics that make a strong first dashboard.",
  },

  Outcomes: {
    title:
      "Match outcomes",

    description:
      "Headline win-rate and round-differential results.",
  },

  Economy: {
    title:
      "Pistols and conversions",

    description:
      "Early-round economy flow, pistol control, and conversion chains.",
  },

  Lineups: {
    title:
      "Roster and lineups",

    description:
      "Player-presence and lineup-combination performance.",
  },

  Compositions: {
    title:
      "Team comps",

    description:
      "Agent composition usage and composition win-rate breakdowns.",
  },

  Strategies: {
    title:
      "Strategy tags",

    description:
      "Tagged play success across sides and round slots.",
  },
};

const TSD_REPORT_PRINT_BRAND_URL =
  "Made using Valovate.com";

const TSD_PAGE_SIZES = {
  a4: {
    label:
      "A4",

    width:
      210,

    height:
      297,
  },

  letter: {
    label:
      "Letter",

    width:
      216,

    height:
      279,
  },

  legal: {
    label:
      "Legal",

    width:
      216,

    height:
      356,
  },

  presentation: {
    label:
      "Presentation 16:9",

    width:
      320,

    height:
      180,
  },
};

const TSD_Z_BY_CONFIDENCE = {
  90:
    1.645,

  95:
    1.96,

  99:
    2.576,
};

const TSD_REPORT_DEFAULT_PAGE = {
  size:
    "a4",

  orientation:
    "portrait",

  margin:
    4,

  showGrid:
    true,

  snapToGrid:
    true,
};

const TSD_GAME_TYPE_LABELS = {
  competitive:
    "Competitive",

  premier:
    "Premier",

  scrim:
    "Scrim",

  tournament:
    "Tournament",

  custom:
    "Custom",

  ranked:
    "Ranked",

  unrated:
    "Unrated",

  unknown:
    "Unknown",
};

function tsdGetMatchDate(match) {return metricDate(match?.matchDate ?? match?.date ?? match?.createdAt);}

function tsdDateInputValue(
  date
) {
  if (
    !(date instanceof Date) ||
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() +
        1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      date.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;
}

function tsdDateLabel(
  date
) {
  if (
    !(date instanceof Date) ||
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "Undated";
  }

  return date.toLocaleDateString(
    undefined,
    {
      month:
        "short",

      day:
        "numeric",

      year:
        "numeric",
    }
  );
}

function tsdGetMatchGameType(
  match
) {
  const raw =
    String(
      match?.gameType ??
        match?.matchType ??
        match?.queueType ??
        match?.queue ??
        match?.type ??
        "Unknown"
    )
      .trim()
      .toLowerCase();

  if (!raw) {
    return "Unknown";
  }

  const normalized =
    raw
      .replace(
        /[_-]+/g,
        " "
      )
      .replace(
        /\s+/g,
        " "
      )
      .trim();

  const exact =
    Object.keys(
      TSD_GAME_TYPE_LABELS
    ).find(
      (key) =>
        key ===
          normalized ||
        TSD_GAME_TYPE_LABELS[
          key
        ].toLowerCase() ===
          normalized
    );

  if (exact) {
    return TSD_GAME_TYPE_LABELS[
      exact
    ];
  }

  if (
    normalized.includes(
      "comp"
    )
  ) {
    return "Competitive";
  }

  if (
    normalized.includes(
      "premier"
    )
  ) {
    return "Premier";
  }

  if (
    normalized.includes(
      "scrim"
    )
  ) {
    return "Scrim";
  }

  if (
    normalized.includes(
      "tournament"
    ) ||
    normalized.includes(
      "event"
    )
  ) {
    return "Tournament";
  }

  if (
    normalized.includes(
      "custom"
    )
  ) {
    return "Custom";
  }

  if (
    normalized.includes(
      "rank"
    )
  ) {
    return "Ranked";
  }

  if (
    normalized.includes(
      "unrated"
    )
  ) {
    return "Unrated";
  }

  return normalized
    .split(" ")
    .filter(Boolean)
    .map(
      (word) =>
        word
          .charAt(0)
          .toUpperCase() +
        word.slice(1)
    )
    .join(" ");
}

function tsdStripScopePrefix(
  label
) {
  return String(
    label || ""
  )
    .replace(
      /^Overall\s+/i,
      ""
    )
    .replace(
      /^Map\+Comp\s+/i,
      ""
    )
    .replace(
      /^Map\s+/i,
      ""
    )
    .trim();
}

function tsdLegacySelectionKey(
  row
) {
  const base =
    String(
      row?.baseSortKey ||
        row?.displayLabel ||
        "stat"
    )
      .replace(
        /^Overall\s+/i,
        ""
      )
      .replace(
        /\s+(ATK|DEF)$/i,
        ""
      );

  return [
    base,
    row?.playerName ||
      "",
    row?.comboKey ||
      "",
    row?.compName ||
      "",
  ].join("||");
}

export function tsdSelectionKey(
  row
) {
  if(row?._dashboardKey)return row._dashboardKey;
  if(row?.studioMetric)return row.id;
  const key =
    tsdLegacySelectionKey(
      row
    );

  if (
    row?.sectionType ===
      "mapSpecific" ||
    row?.sectionType ===
      "mapCompSpecific"
  ) {
    return [
      key,
      row?.mapName ||
        "",
    ].join("||");
  }

  return key;
}

function tsdRowHasSideVariant(
  row,
  rows,
  suffix
) {
  const base =
    String(
      row?.baseSortKey ||
        ""
    );

  return rows.some(
    (candidate) => {
      const candidateBase =
        String(
          candidate?.baseSortKey ||
            ""
        );

      return (
        candidateBase ===
        `${base} ${suffix}`
      );
    }
  );
}

function tsdFilterRowsForSide(
  rows,
  side
) {
  if (
    side === "both"
  ) {
    return rows.filter(
      (row) =>
        row.side !==
          "ATK" &&
        row.side !==
          "DEF"
    );
  }

  const wanted =
    side === "attack"
      ? "ATK"
      : "DEF";

  const opposite =
    side === "attack"
      ? "DEF"
      : "ATK";

  return rows.filter(
    (row) => {
      const sideText =
        String(
          row?.side ||
            ""
        ).toUpperCase();

      if (
        sideText.startsWith(
          `${wanted} ROUND`
        )
      ) {
        return true;
      }

      if (
        sideText.startsWith(
          `${opposite} ROUND`
        )
      ) {
        return false;
      }

      if (
        row.side ===
        wanted
      ) {
        return true;
      }

      if (
        row.side ===
        opposite
      ) {
        return false;
      }

      const hasWantedSibling =
        tsdRowHasSideVariant(
          row,
          rows,
          wanted
        );

      const hasOppositeSibling =
        tsdRowHasSideVariant(
          row,
          rows,
          opposite
        );

      if (
        hasWantedSibling ||
        hasOppositeSibling
      ) {
        return false;
      }

      return true;
    }
  );
}

function tsdGetStatMeta(
  row
) {
  if(row?._dashboardMeta)return row._dashboardMeta;
  if(row?.studioMetric)return row.meta;
  const key =
    `${row?.baseSortKey || ""} ${row?.displayLabel || ""}`.toLowerCase();

  const title=[tsdStripScopePrefix(row?.displayLabel || row?.baseSortKey || 'Statistic'),row?.playerName,row?.comboKey,row?.compName].filter(Boolean).join(' · ');

  const meta = {
    title,

    definition:
      "A statistic calculated from the matches that satisfy the active dashboard filters.",

    formula:
      "Calculated from the eligible observations in the filtered dataset.",

    interpretation:
      "Use the value together with its sample size and uncertainty before drawing conclusions.",

    whenUseful:
      "Useful when comparing filtered slices of your team's match history.",
  };

  if (
    key.includes(
      "match win rate"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of eligible matches your team won.",

      formula:
        "Matches won ÷ eligible matches × 100.",

      interpretation:
        "Higher values indicate a larger share of filtered matches ended in a win.",

      whenUseful:
        "Use this as the headline outcome statistic for a map, composition, event type, or date period.",
    };
  }

  if (
    key.includes(
      "pistol win rate"
    ) &&
    !key.includes(
      "round 2"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of eligible pistol rounds won.",

      formula:
        "Pistol rounds won ÷ eligible pistol rounds × 100.",

      interpretation:
        "Measures opening-round performance for the selected side or both sides.",

      whenUseful:
        "Useful for identifying whether pistol preparation is creating an early-round advantage.",
    };
  }

  if (
    key.includes(
      "round 2 conversion after pistol win"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of round 2s won after your team won the preceding pistol round.",

      formula:
        "Round 2 wins after pistol wins ÷ pistol wins with a recorded round 2 × 100.",

      interpretation:
        "Shows how consistently pistol advantages are converted into a 2–0 start.",

      whenUseful:
        "Useful for evaluating anti-eco execution, purchase discipline, and conversion reliability.",
    };
  }

  if (
    key.includes(
      "round 2 conversion after pistol loss"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of round 2s won after your team lost the preceding pistol round.",

      formula:
        "Round 2 wins after pistol losses ÷ pistol losses with a recorded round 2 × 100.",

      interpretation:
        "Shows how often the team immediately recovers from a lost pistol.",

      whenUseful:
        "Useful for evaluating force-buy, eco, and second-round recovery plans.",
    };
  }

  if (
    key.includes(
      "bonus conversion"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of round 3s won after winning both the pistol and round 2.",

      formula:
        "Round 3 wins after WW starts ÷ eligible WW starts × 100.",

      interpretation:
        "Measures how frequently a strong 2–0 opening is converted against the opponent's first full buy.",

      whenUseful:
        "Useful for evaluating bonus-round planning and early-half momentum.",
    };
  }

  if (
    key.includes(
      "anti-bonus conversion"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of round 3s won after losing the first two rounds.",

      formula:
        "Round 3 wins after LL starts ÷ eligible LL starts × 100.",

      interpretation:
        "Measures full-buy recovery after beginning a half 0–2.",

      whenUseful:
        "Useful for evaluating round-3 full-buy plans and resilience after losing pistol sequences.",
    };
  }

  if (
    key.includes(
      "round 3 win rate after lw"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of round 3s won after losing pistol and then winning round 2.",

      formula:
        "Round 3 wins after LW starts ÷ eligible LW starts × 100.",

      interpretation:
        "Measures whether the team can consolidate momentum after stealing or recovering round 2.",

      whenUseful:
        "Useful for assessing conversion after a successful second-round recovery.",
    };
  }

  if (
    key.includes(
      "round 3 win rate after wl"
    )
  ) {
    return {
      ...meta,

      definition:
        "The percentage of round 3s won after winning pistol but losing round 2.",

      formula:
        "Round 3 wins after WL starts ÷ eligible WL starts × 100.",

      interpretation:
        "Measures recovery after failing to convert a pistol win into round 2.",

      whenUseful:
        "Useful for finding whether the team can stabilize after an anti-eco upset.",
    };
  }

  if (
    key.includes(
      "average round differential"
    )
  ) {
    return {
      ...meta,

      definition:
        "The average difference between rounds won and rounds lost per eligible match.",

      formula:
        "Σ (rounds won − rounds lost) ÷ eligible matches.",

      interpretation:
        "Positive values indicate the team wins more rounds than it loses on average.",

      whenUseful:
        "Useful when match win rate alone hides how close or dominant the matches were.",
    };
  }

  if (
    key.includes(
      "total round differential"
    )
  ) {
    return {
      ...meta,

      definition:
        "The cumulative difference between rounds won and rounds lost across eligible matches.",

      formula:
        "Σ (rounds won − rounds lost).",

      interpretation:
        "Positive totals indicate an overall round advantage across the selected dataset.",

      whenUseful:
        "Useful for describing aggregate dominance across a fixed event or match sample.",
    };
  }

  if (
    key.includes(
      "strategy win rate"
    )
  ) {
    return {
      ...meta,

      definition:
        `The win rate of rounds tagged with “${row?.comboKey || "this strategy"}” in ${row?.side || "the selected round slot"}.`,

      formula:
        "Tagged rounds won ÷ tagged rounds recorded × 100.",

      interpretation:
        "Shows how successful a specific tagged strategy has been in its recorded use cases.",

      whenUseful:
        "Useful for comparing recurring set plays, defaults, retakes, or other round-plan tags.",
    };
  }

  if (
    key.includes(
      "games by player"
    )
  ) {
    return {
      ...meta,

      definition:
        `The number of eligible matches containing ${row?.playerName || "the selected player"}.`,

      formula:
        "Count of eligible matches where the player appears in the stored lineup.",

      interpretation:
        "This is a sample-size statistic rather than a performance percentage.",

      whenUseful:
        "Useful for judging whether player-specific win-rate estimates have enough observations.",
    };
  }

  if (
    key.includes(
      "player win rate"
    )
  ) {
    return {
      ...meta,

      definition:
        `The team's match win rate in eligible matches containing ${row?.playerName || "the selected player"}.`,

      formula:
        "Wins with player ÷ eligible matches with player × 100.",

      interpretation:
        "Describes team results when the selected player was present; it does not by itself prove causation.",

      whenUseful:
        "Useful for lineup review when interpreted together with sample size, opponent quality, map, and composition.",
    };
  }

  if (
    key.includes(
      "win rate without player"
    )
  ) {
    return {
      ...meta,

      definition:
        `The team's match win rate in eligible matches where ${row?.playerName || "the selected player"} was absent.`,

      formula:
        "Wins without player ÷ eligible matches without player × 100.",

      interpretation:
        "Provides a contextual comparison to matches where the selected player did participate.",

      whenUseful:
        "Useful as a descriptive comparison, not as evidence that a player caused the difference.",
    };
  }

  if (
    key.includes(
      "player combo win rate"
    )
  ) {
    return {
      ...meta,

      definition:
        `The team's match win rate with the lineup combination ${row?.comboKey || "shown"}.`,

      formula:
        "Wins with combination ÷ eligible matches with combination × 100.",

      interpretation:
        "Describes results for a specific group of players appearing together.",

      whenUseful:
        "Useful for lineup experimentation when the sample is large enough to support comparison.",
    };
  }

  if (
    key.includes(
      "games by team comp"
    )
  ) {
    return {
      ...meta,

      definition:
        `The number of eligible matches played with ${row?.compName || "the listed team composition"}.`,

      formula:
        "Count of eligible matches using the composition.",

      interpretation:
        "This is the amount of evidence available for that composition.",

      whenUseful:
        "Useful for checking whether composition performance is based on a meaningful sample.",
    };
  }

  if (
    key.includes(
      "team comp win rate"
    )
  ) {
    return {
      ...meta,

      definition:
        `The match win rate for ${row?.compName || "the listed team composition"}.`,

      formula:
        "Wins with composition ÷ eligible matches with composition × 100.",

      interpretation:
        "Describes match outcomes for that exact stored agent composition.",

      whenUseful:
        "Useful for comparing compositions on the same map, game type, or date period.",
    };
  }

  return meta;
}

function tsdGetMetricDescriptor(
  row
) {
  const key =
    String(
      row?.baseSortKey ||
        ""
    ).toLowerCase();

  if (
    key.includes(
      "match win rate"
    )
  ) {
    return {
      kind:
        "matchPercent",

      metricKey:
        "map_wr",
    };
  }

  if (
    key.includes(
      "double pistol conversion"
    )
  ) {
    return {
      kind:
        "matchPercent",

      metricKey:
        "both_pistols_match",
    };
  }

  if (
    key.includes(
      "no pistol conversion"
    )
  ) {
    return {
      kind:
        "matchPercent",

      metricKey:
        "no_pistols_match",
    };
  }

  if (
    key.includes(
      "team comp average round differential"
    )
  ) {
    return {
      kind:
        "compRoundDiff",

      mode:
        "average",
    };
  }

  if (
    key.includes(
      "average round differential"
    )
  ) {
    return {
      kind:
        "matchNumber",

      metricKey:
        "avg_round_diff",

      mode:
        "average",
    };
  }

  if (
    key.includes(
      "total round differential"
    )
  ) {
    return {
      kind:
        "matchNumber",

      metricKey:
        "total_round_diff",

      mode:
        "sum",
    };
  }

  if (
    key.includes(
      "pistol win rate"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "pistol_wr",
    };
  }

  if (
    key.includes(
      "round 2 conversion after pistol win"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "round2_after_win_pistol",
    };
  }

  if (
    key.includes(
      "round 2 conversion after pistol loss"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "round2_after_lose_pistol",
    };
  }

  if (
    key.includes(
      "anti-bonus conversion"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "anti_bonus_conversion",
    };
  }

  if (
    key.includes(
      "bonus conversion"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "bonus_conversion",
    };
  }

  if (
    key.includes(
      "round 3 win rate after lw"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "round3_after_lw",
    };
  }

  if (
    key.includes(
      "round 3 win rate after wl"
    )
  ) {
    return {
      kind:
        "sidePercent",

      metricKey:
        "round3_after_wl",
    };
  }

  if (
    key.includes(
      "strategy win rate"
    )
  ) {
    return {
      kind:
        "strategy",
    };
  }

  if (
    key.includes(
      "games by player"
    )
  ) {
    return {
      kind:
        "gamesByPlayer",
    };
  }

  if (
    key.includes(
      "win rate without player"
    )
  ) {
    return {
      kind:
        "withoutPlayerWinRate",
    };
  }

  if (
    key.includes(
      "player combo win rate"
    )
  ) {
    return {
      kind:
        "playerComboWinRate",
    };
  }

  if (
    key.includes(
      "player win rate"
    )
  ) {
    return {
      kind:
        "playerWinRate",
    };
  }

  if (
    key.includes(
      "games by team comp"
    )
  ) {
    return {
      kind:
        "gamesByComp",
    };
  }

  if (
    key.includes(
      "team comp win rate"
    )
  ) {
    return {
      kind:
        "compWinRate",
    };
  }

  return null;
}

function tsdResolveMetricSide(
  row
) {
  if (
    row?.side === "ATK"
  ) {
    return "atk";
  }

  if (
    row?.side === "DEF"
  ) {
    return "def";
  }

  return "both";
}

function tsdEvaluateRowForMatch(
  row,
  match
) {
  if (row.mapName && (match.map || "Unknown") !== row.mapName) return null;
  const descriptor =
    tsdGetMetricDescriptor(
      row
    );

  if (!descriptor) {
    return null;
  }

  if (
    descriptor.kind ===
    "matchPercent"
  ) {
    const res =
      evaluateMatchMetric(
        match,
        descriptor.metricKey
      );

    return res
      ? {
          kind:
            "percent",

          made:
            res.made,

          total:
            res.total,
        }
      : null;
  }

  if (
    descriptor.kind ===
    "matchNumber"
  ) {
    const res =
      evaluateMatchMetric(
        match,
        descriptor.metricKey
      );

    return res
      ? {
          kind:
            "number",

          sum:
            res.sum,

          total:
            res.total,

          mode:
            descriptor.mode,
        }
      : null;
  }

  if (
    descriptor.kind ===
    "sidePercent"
  ) {
    const side =
      tsdResolveMetricSide(
        row
      );

    if (
      side === "both"
    ) {
      const atk =
        evaluateSideMetric(
          match,
          "atk",
          descriptor.metricKey
        );

      const def =
        evaluateSideMetric(
          match,
          "def",
          descriptor.metricKey
        );

      const made =
        Number(
          atk?.made || 0
        ) +
        Number(
          def?.made || 0
        );

      const total =
        Number(
          atk?.total || 0
        ) +
        Number(
          def?.total || 0
        );

      return total > 0
        ? {
            kind:
              "percent",
            made,
            total,
          }
        : null;
    }

    const res =
      evaluateSideMetric(
        match,
        side,
        descriptor.metricKey
      );

    return res
      ? {
          kind:
            "percent",

          made:
            res.made,

          total:
            res.total,
        }
      : null;
  }

  if (
    descriptor.kind ===
    "strategy"
  ) {
    const sideText =
      String(
        row?.side ||
          ""
      ).toUpperCase();

    const side =
      sideText.startsWith(
        "DEF"
      )
        ? "def"
        : "atk";

    const roundNumber =
      Number(
        (
          sideText.match(
            /ROUND\s+(\d)/i
          ) || []
        )[1] || 1
      );

    const roundKey =
      `r${Math.max(
        1,
        Math.min(
          3,
          roundNumber
        )
      )}`;

    const tags =
      getMatchRoundTags(
        match,
        side,
        roundKey
      );

    const target =
      normalizeRoundTagValue(
        row?.comboKey
      ).toLowerCase();

    if (
      !tags.some(
        (tag) =>
          normalizeRoundTagValue(
            tag
          ).toLowerCase() ===
          target
      )
    ) {
      return null;
    }

    const roundResult =
      safeBool(
        match?.roundWins?.[
          side
        ]?.[roundKey]
      );

    if (
      roundResult ===
      null
    ) {
      return null;
    }

    return {
      kind:
        "percent",

      made:
        roundResult
          ? 1
          : 0,

      total:
        1,
    };
  }

  const result =
    getMatchResult(
      match
    );

  const validResult =
    [
      "W",
      "L",
      "T",
    ].includes(
      result
    );

  const players =
    getMatchPlayers(
      match
    );

  const playerSet =
    new Set(
      players
    );

  if (
    descriptor.kind ===
    "gamesByPlayer"
  ) {
    return playerSet.has(
      row?.playerName
    )
      ? {
          kind:
            "count",

          count:
            1,
        }
      : null;
  }

  if (
    descriptor.kind ===
    "playerWinRate"
  ) {
    if (
      !validResult ||
      !playerSet.has(
        row?.playerName
      )
    ) {
      return null;
    }

    return {
      kind:
        "percent",

      made:
        result === "W"
          ? 1
          : 0,

      total:
        1,
    };
  }

  if (
    descriptor.kind ===
    "withoutPlayerWinRate"
  ) {
    if (
      !validResult ||
      playerSet.has(
        row?.playerName
      )
    ) {
      return null;
    }

    return {
      kind:
        "percent",

      made:
        result === "W"
          ? 1
          : 0,

      total:
        1,
    };
  }

  if (
    descriptor.kind ===
    "playerComboWinRate"
  ) {
    if (!validResult) {
      return null;
    }

    const comboPlayers =
      String(
        row?.comboKey ||
          ""
      )
        .split(
          " + "
        )
        .map(
          (name) =>
            name.trim()
        )
        .filter(Boolean);

    if (
      !comboPlayers.length ||
      !comboPlayers.every(
        (name) =>
          playerSet.has(
            name
          )
      )
    ) {
      return null;
    }

    return {
      kind:
        "percent",

      made:
        result === "W"
          ? 1
          : 0,

      total:
        1,
    };
  }

  const compKey =
    normalizeCompKey(
      match?.comp
    );

  if (
    descriptor.kind ===
    "gamesByComp"
  ) {
    return (
      compKey ===
      row?.compName
    )
      ? {
          kind:
            "count",

          count:
            1,
        }
      : null;
  }

  if (
    descriptor.kind ===
    "compWinRate"
  ) {
    if (
      !validResult ||
      compKey !==
        row?.compName
    ) {
      return null;
    }

    return {
      kind:
        "percent",

      made:
        result === "W"
          ? 1
          : 0,

      total:
        1,
    };
  }

  if (
    descriptor.kind ===
    "compRoundDiff"
  ) {
    if (
      compKey !==
      row?.compName
    ) {
      return null;
    }

    const rd =
      getRoundDifferential(
        match
      );

    return rd == null
      ? null
      : {
          kind:
            "number",

          sum:
            rd,

          total:
            1,

          mode:
            "average",
        };
  }

  return null;
}

// Values and N use exactly the observations consumed by the graph curve.
export function tsdEvaluateCardMetric(base, matches, side = 'both', candidateRows = buildAllRows(matches)) {
  const key = tsdSelectionKey(base);
  const row = tsdFilterRowsForSide(candidateRows, side).find(candidate => tsdSelectionKey(candidate) === key) || base;
  const observations = matches.map(match => tsdEvaluateRowForMatch(row, match)).filter(Boolean);
  const kind = observations[0]?.kind;
  let result;
  if (kind === 'percent') result = makePercentRow({...row, made: observations.reduce((sum, item) => sum + Number(item.made || 0), 0), total: observations.reduce((sum, item) => sum + Number(item.total || 0), 0)});
  else if (kind === 'number') result = makeNumberRow({...row, sum: observations.reduce((sum, item) => sum + Number(item.sum || 0), 0), total: observations.reduce((sum, item) => sum + Number(item.total || 0), 0), mode: observations[0].mode});
  else if (kind === 'count') result = makeCountRow({...row, value: observations.reduce((sum, item) => sum + Number(item.count || 0), 0)});
  else result = makeNoDataRow(row);
  return {...row, ...result};
}

export function tsdBuildSeries(
  row,
  matches
) {
  if(row?.studioMetric)return row.series || [];
  const dated =
    [...matches]
      .map(
        (
          match,
          index
        ) => ({
          match,

          date:
            tsdGetMatchDate(
              match
            ),

          index,
        })
      )
      .sort(
        (a, b) => {
          if (
            a.date &&
            b.date
          ) {
            return (
              a.date -
              b.date
            );
          }

          if (a.date) {
            return -1;
          }

          if (b.date) {
            return 1;
          }

          return (
            a.index -
            b.index
          );
        }
      );

  let made = 0;
  let total = 0;
  let sum = 0;
  let count = 0;
  let numericTotal = 0;

  const points = [];

  dated.forEach(
    ({
      match,
      date,
      index,
    }) => {
      const observation =
        tsdEvaluateRowForMatch(
          row,
          match
        );

      if (!observation) {
        return;
      }

      let value =
        null;

      let ciLow =
        null;

      let ciHigh =
        null;

      if (
        observation.kind ===
        "percent"
      ) {
        made +=
          Number(
            observation.made ||
              0
          );

        total +=
          Number(
            observation.total ||
              0
          );

        if (
          total <= 0
        ) {
          return;
        }

        value =
          (
            made /
            total
          ) *
          100;

        const ci =
          computeWilsonCI(
            made,
            total
          );

        ciLow =
          ci?.low ??
          null;

        ciHigh =
          ci?.high ??
          null;
      } else if (
        observation.kind ===
        "number"
      ) {
        sum +=
          Number(
            observation.sum ||
              0
          );

        numericTotal +=
          Number(
            observation.total ||
              0
          );

        if (
          numericTotal <= 0
        ) {
          return;
        }

        value =
          observation.mode ===
          "sum"
            ? sum
            : sum /
              numericTotal;

        total =
          numericTotal;
      } else if (
        observation.kind ===
        "count"
      ) {
        count +=
          Number(
            observation.count ||
              0
          );

        total =
          count;

        value =
          count;
      }

      if (
        !Number.isFinite(
          Number(
            value
          )
        )
      ) {
        return;
      }

      points.push({
        x:
          points.length,

        date,

        dateLabel:
          date
            ? tsdDateLabel(
                date
              )
            : `Match ${index + 1}`,

        shortDate:
          date
            ? date.toLocaleDateString(
                undefined,
                {
                  month:
                    "short",

                  day:
                    "numeric",
                }
              )
            : `M${index + 1}`,

        value:
          Number(
            value
          ),

        n:
          total,

        ciLow,
        ciHigh,
      });
    }
  );

  return points;
}

function tsdFormatValue(
  row,
  value = row?.value
) {
  if (
    row?.isNoData ||
    !Number.isFinite(
      Number(value)
    )
  ) {
    return "n.d.";
  }

  const n =
    Number(value);

  if (
    row?.valueType ===
    "percent"
  ) {
    return `${n.toFixed(1)}%`;
  }

  if (
    row?.valueType ===
    "count"
  ) {
    return Math.round(
      n
    ).toLocaleString();
  }

  if(row?.studioMetric)return `${n.toFixed(2)}${row.id.startsWith("focus:")?" / 5":""}`;
  return `${n > 0 ? "+" : ""}${n.toFixed(2)}`;
}

function tsdGetConfidenceInterval(
  row,
  level
) {
  if (
    row?.valueType !==
      "percent" ||
    !Number.isFinite(
      Number(
        row?.total
      )
    ) ||
    Number(
      row.total
    ) <= 0
  ) {
    return null;
  }

  return computeWilsonCI(
    Number(
      row.made || 0
    ),

    Number(
      row.total || 0
    ),

    TSD_Z_BY_CONFIDENCE[
      level
    ] || 1.96
  );
}

export function tsdFilterMatches(
  matches,
  filters,
  {
    ignoreDates = false,
  } = {}
) {
  const maps =
    new Set(
      filters.maps || []
    );

  const comps =
    new Set(
      filters.comps || []
    );

  const gameTypes =
    new Set(
      filters.gameTypes || []
    );

  const players =
    filters.players || [];

  const from =
    !ignoreDates &&
    filters.dateFrom
      ? new Date(
          `${filters.dateFrom}T00:00:00`
        )
      : null;

  const to =
    !ignoreDates &&
    filters.dateTo
      ? new Date(
          `${filters.dateTo}T23:59:59.999`
        )
      : null;

  return matches.filter(
    (match) => {
      if (
        maps.size > 0 &&
        !maps.has(
          match?.map ||
            "Unknown"
        )
      ) {
        return false;
      }

      const compName =
        normalizeCompKey(
          match?.comp
        );

      if (
        comps.size > 0 &&
        !comps.has(
          compName
        )
      ) {
        return false;
      }

      const gameType =
        tsdGetMatchGameType(
          match
        );

      if (
        gameTypes.size >
          0 &&
        !gameTypes.has(
          gameType
        )
      ) {
        return false;
      }

      if (
        players.length >
        0
      ) {
        const matchPlayers =
          new Set(
            getMatchPlayers(
              match
            )
          );

        if (
          !players.every(
            (player) =>
              matchPlayers.has(
                player
              )
          )
        ) {
          return false;
        }
      }

      if (
        from ||
        to
      ) {
        const date =
          tsdGetMatchDate(
            match
          );

        if (!date) {
          return false;
        }

        if (
          from &&
          date < from
        ) {
          return false;
        }

        if (
          to &&
          date > to
        ) {
          return false;
        }
      }

      return true;
    }
  );
}

function tsdBuildPreviousPeriodFilters(
  filters
) {
  if (
    !filters?.dateFrom ||
    !filters?.dateTo
  ) {
    return null;
  }

  const from =
    new Date(
      `${filters.dateFrom}T00:00:00`
    );

  const to =
    new Date(
      `${filters.dateTo}T23:59:59.999`
    );

  if (
    Number.isNaN(
      from.getTime()
    ) ||
    Number.isNaN(
      to.getTime()
    ) ||
    to < from
  ) {
    return null;
  }

  const dayMs =
    24 *
    60 *
    60 *
    1000;

  const days =
    Math.max(
      1,
      Math.floor(
        (to - from) /
          dayMs
      ) + 1
    );

  const previousTo =
    new Date(
      from.getTime() -
        dayMs
    );

  const previousFrom =
    new Date(
      previousTo.getTime() -
        (days - 1) *
          dayMs
    );

  return {
    ...filters,

    dateFrom:
      tsdDateInputValue(
        previousFrom
      ),

    dateTo:
      tsdDateInputValue(
        previousTo
      ),
  };
}

function tsdBuildOverallRowsOnly(
  matches
) {
  const rows = [];

  rows.push(
    ...buildCorePistolConversionRows({
      matches,

      sectionType:
        "overall",

      scope:
        "Overall",

      labelPrefix:
        "Overall",

      baseKeyPrefix:
        "Overall",
    })
  );

  rows.push(
    ...buildStrategyRows({
      matches,

      sectionType:
        "overall",

      scope:
        "Overall",

      labelPrefix:
        "Overall",

      baseKeyPrefix:
        "Overall",
    })
  );

  rows.push(
    ...buildRosterRowsForMatches({
      matches,

      sectionType:
        "overall",

      scope:
        "Overall",

      labelPrefix:
        "Overall",

      baseKeyPrefix:
        "Overall",
    })
  );

  rows.push(
    ...buildOverallCompRows(
      matches
    )
  );

  return rows.sort(
    compareDefaultPriority
  );
}

export function tsdGetOverallRows(
  matches,
  side
) {
  return tsdFilterRowsForSide(
    tsdBuildOverallRowsOnly(
      matches
    ),
    side
  );
}

function tsdDefaultSelectedKeys(rows) {
  return starterMetricKeys(rows,tsdSelectionKey,tsdStripScopePrefix);
}

function tsdDownloadBlob(
  blob,
  filename
) {
  const url =
    URL.createObjectURL(
      blob
    );

  const anchor =
    document.createElement(
      "a"
    );

  anchor.href =
    url;

  anchor.download =
    filename;

  document.body.appendChild(
    anchor
  );

  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(
    url
  );
}

function tsdGetSidePreferenceScore(
  row,
  side
) {
  const sideText =
    String(
      row?.side ||
        ""
    ).toUpperCase();

  const isAttack =
    sideText === "ATK" ||
    sideText.startsWith(
      "ATK ROUND"
    );

  const isDefense =
    sideText === "DEF" ||
    sideText.startsWith(
      "DEF ROUND"
    );

  if (
    side === "attack"
  ) {
    if (isAttack) {
      return 3;
    }

    if (isDefense) {
      return 1;
    }

    return 2;
  }

  if (
    side === "defense"
  ) {
    if (isDefense) {
      return 3;
    }

    if (isAttack) {
      return 1;
    }

    return 2;
  }

  return isAttack ||
    isDefense
    ? 1
    : 3;
}

function tsdGetMenuScope(
  row
) {
  if (
    row?.sectionType ===
    "overall"
  ) {
    return "Overall";
  }

  if (
    row?.sectionType ===
    "mapSpecific"
  ) {
    return "By Map";
  }

  if (
    row?.sectionType ===
    "mapCompSpecific"
  ) {
    return "Map + Comp";
  }

  return "All scopes";
}

function tsdGetMenuGroup(
  row
) {
  const title =
    tsdGetStatMeta(
      row
    ).title;

  const isStarter =
    row?.sectionType ===
      "overall" &&
    TSD_DEFAULT_STAT_PATTERNS.some(
      (pattern) =>
        title.includes(
          pattern
        )
    );

  if (isStarter) {
    return "Starter";
  }
  if(row?.studioMetric&&row.category==='Review')return "Review & development";

  switch (
    row?.category
  ) {
    case "Core":
      return "Outcomes";

    case "Pistols":
    case "Conversions":
      return "Economy";

    case "Roster":
      return "Lineups";

    case "Comps":
      return "Compositions";

    case "Strategies":
      return "Strategies";

    default:
      return "All";
  }
}

function tsdCloneReportSnapshot(
  snapshot
) {
  return JSON.parse(
    JSON.stringify(
      snapshot
    )
  );
}

function tsdIsEditableTarget(
  target
) {
  if (
    typeof HTMLElement ===
      "undefined" ||
    !(
      target instanceof
      HTMLElement
    )
  ) {
    return false;
  }

  return Boolean(
    target.closest(
      "input, textarea, select, [contenteditable='true'], [contenteditable='']"
    )
  );
}

function TSDInfo({
  row,
}) {
  const meta =
    tsdGetStatMeta(
      row
    );

  const triggerRef =
    useRef(null);

  const popoverRef =
    useRef(null);

  const closeTimerRef =
    useRef(null);

  const [
    popoverOpen,
    setPopoverOpen,
  ] = useState(false);

  const [
    popoverStyle,
    setPopoverStyle,
  ] = useState(null);

  const clearCloseTimer =
    () => {
      if (
        typeof window ===
          "undefined" ||
        !closeTimerRef.current
      ) {
        return;
      }

      window.clearTimeout(
        closeTimerRef.current
      );
      closeTimerRef.current =
        null;
    };

  const openPopover =
    () => {
      clearCloseTimer();
      setPopoverOpen(true);
    };

  const schedulePopoverClose =
    () => {
      clearCloseTimer();

      if (
        typeof window ===
        "undefined"
      ) {
        setPopoverOpen(false);
        return;
      }

      closeTimerRef.current =
        window.setTimeout(
          () => {
            setPopoverOpen(false);
            closeTimerRef.current =
              null;
          },
          140
        );
    };

  useEffect(
    () => () =>
      clearCloseTimer(),
    []
  );

  useEffect(
    () => {
      if (
        !popoverOpen ||
        typeof window ===
          "undefined" ||
        !triggerRef.current
      ) {
        return undefined;
      }

      const gutter =
        16;

      const gap =
        10;

      const updatePopoverPosition =
        () => {
          if (
            !triggerRef.current
          ) {
            return;
          }

          const triggerRect =
            triggerRef.current.getBoundingClientRect();

          const preferredWidth =
            Math.min(
              352,
              window.innerWidth -
                gutter * 2
            );

          const popoverWidth =
            Math.min(
              preferredWidth,
              popoverRef.current
                ?.offsetWidth ||
                preferredWidth
            );

          const popoverHeight =
            popoverRef.current
              ?.offsetHeight ||
            0;

          let left =
            triggerRect.left -
            gap -
            popoverWidth;

          if (left < gutter) {
            left =
              triggerRect.right +
              gap;
          }

          if (
            left +
              popoverWidth >
            window.innerWidth -
              gutter
          ) {
            left = Math.max(
              gutter,
              window.innerWidth -
                gutter -
                popoverWidth
            );
          }

          let top =
            triggerRect.top +
            triggerRect.height /
              2 -
            popoverHeight / 2;

          if (top < gutter) {
            top = gutter;
          }

          const maxTop =
            window.innerHeight -
            gutter -
            popoverHeight;

          if (top > maxTop) {
            top = Math.max(
              gutter,
              maxTop
            );
          }

          setPopoverStyle({
            top: `${top}px`,
            left: `${left}px`,
            maxWidth: `${preferredWidth}px`,
          });
        };

      const rafId =
        window.requestAnimationFrame(
          updatePopoverPosition
        );

      updatePopoverPosition();

      window.addEventListener(
        "resize",
        updatePopoverPosition,
        {
          passive: true,
        }
      );

      window.addEventListener(
        "scroll",
        updatePopoverPosition,
        true
      );

      return () => {
        window.cancelAnimationFrame(
          rafId
        );
        window.removeEventListener(
          "resize",
          updatePopoverPosition
        );
        window.removeEventListener(
          "scroll",
          updatePopoverPosition,
          true
        );
      };
    },
    [
      meta.definition,
      meta.formula,
      meta.interpretation,
      meta.title,
      meta.whenUseful,
      popoverOpen,
    ]
  );

  return (
    <span className="--TSD-info-wrap">
      <button
        ref={triggerRef}
        className="--TSD-icon-button --TSD-info-button"
        type="button"
        aria-label={`Explain ${meta.title}`}
        onMouseEnter={
          openPopover
        }
        onMouseLeave={
          schedulePopoverClose
        }
        onFocus={
          openPopover
        }
        onBlur={
          schedulePopoverClose
        }
      >
        i
      </button>

      {popoverOpen &&
      typeof document !==
        "undefined"
        ? createPortal(
            <span
              ref={popoverRef}
              className="--TSD-info-popover --TSD-info-popover--portal"
              role="tooltip"
              style={
                popoverStyle ||
                undefined
              }
              onMouseEnter={
                openPopover
              }
              onMouseLeave={
                schedulePopoverClose
              }
            >
              <strong>
                {meta.title}
              </strong>

              <span>
                <b>
                  Definition:
                </b>{" "}
                {
                  meta.definition
                }
              </span>

              <span>
                <b>
                  Formula:
                </b>{" "}
                {meta.formula}
              </span>

              <span>
                <b>
                  Interpretation:
                </b>{" "}
                {
                  meta.interpretation
                }
              </span>

              <span>
                <b>
                  Useful when:
                </b>{" "}
                {
                  meta.whenUseful
                }
              </span>
            </span>,
            document.body
          )
        : null}
    </span>
  );
}

function TSDMiniChart({
  row,
  series,
  settings,
  compact = false,
}) {
  const safeSettings = {
    ...tsdGraphDefaults(row),
    ...(settings || {}),
    dataFilters: settings?.dataFilters || row?._cardFilters,
  };

  const graphType =
    safeSettings.graphType;

  const isPercent =
    row?.valueType ===
    "percent";

  const values =
    series
      .map(
        (point) =>
          Number(
            point.value
          )
      )
      .filter(
        Number.isFinite
      );

  const currentValue =
    Number(
      row?.value
    );

  if (
    (
      graphType ===
        "pie" ||
      graphType ===
        "donut"
    ) &&
    isPercent &&
    row?.value != null && Number.isFinite(
      currentValue
    )
  ) {
    return <StudioProportionChart type={graphType} value={currentValue} title={tsdGetStatMeta(row).title} compact={compact}/>;
  }

  if(['pie','donut'].includes(graphType)&&!isPercent)return <div className="--TSD-chart-empty">Pie and doughnut charts require a percentage metric. Choose a trend chart for this statistic.</div>;

  if (!values.length) {
    return (
      <div className="--TSD-chart-empty">
        No trend data in
        this filtered
        sample.
      </div>
    );
  }

  const width = 100;

  const height =
    compact
      ? 40
      : 62;

  const autoMin =
    Math.min(
      ...values
    );

  const autoMax =
    Math.max(
      ...values
    );

  let yMin =
    safeSettings.yMin !==
      "" &&
    Number.isFinite(
      Number(
        safeSettings.yMin
      )
    )
      ? Number(
          safeSettings.yMin
        )
      : isPercent
        ? 0
        : Math.floor(
            (
              autoMin -
              Math.max(
                1,
                Math.abs(
                  autoMin
                ) * 0.1
              )
            ) /
              5
          ) * 5;

  let yMax =
    safeSettings.yMax !==
      "" &&
    Number.isFinite(
      Number(
        safeSettings.yMax
      )
    )
      ? Number(
          safeSettings.yMax
        )
      : isPercent
        ? 100
        : Math.ceil(
            (
              autoMax +
              Math.max(
                1,
                Math.abs(
                  autoMax
                ) * 0.1
              )
            ) /
              5
          ) * 5;

  if (
    yMax <= yMin
  ) {
    yMax =
      yMin + 1;
  }

  const yStep =
    Math.max(
      1,
      Number(
        safeSettings.yInterval
      ) || 10
    );

  const gridValues =
    [];

  for (
    let value =
      Math.ceil(
        yMin /
          yStep
      ) *
      yStep;

    value <= yMax;

    value += yStep
  ) {
    gridValues.push(
      value
    );

    if (
      gridValues.length >
      20
    ) {
      break;
    }
  }

  const maxYAxisLabels =
    compact
      ? 0
      : 4;

  const yLabelSkip =
    maxYAxisLabels <= 0
      ? 1
      : Math.max(
          1,
          Math.ceil(
            gridValues.length /
              maxYAxisLabels
          )
        );

  const displayGridValues =
    compact
      ? []
      : gridValues.filter(
          (
            value,
            index
          ) =>
            index %
              yLabelSkip ===
              0 ||
            index ===
              gridValues.length -
                1
        );

  const longestYLabel =
    displayGridValues.reduce(
      (
        current,
        value
      ) =>
        Math.max(
          current,
          String(
            isPercent
              ? `${value}%`
              : value
          ).length
        ),
      0
    );

  const padLeft =
    compact
      ? 6
      : Math.min(
          22,
          Math.max(
            12,
            4 +
              longestYLabel *
                2.1
          )
        );

  const padRight =
    compact
      ? 2.5
      : 4;

  const padTop = 5;

  const padBottom =
    compact
      ? 8
      : 19;

  const plotWidth =
    width -
    padLeft -
    padRight;

  const plotHeight =
    height -
    padTop -
    padBottom;

  const xFor = (
    index
  ) =>
    padLeft +
    (
      series.length <=
      1
        ? plotWidth /
          2
        : (
            index /
            (
              series.length -
              1
            )
          ) *
          plotWidth
    );

  const yFor = (
    value
  ) =>
    padTop +
    (
      1 -
      (
        Number(
          value
        ) -
        yMin
      ) /
        (
          yMax -
          yMin
        )
    ) *
      plotHeight;

  const path =
    series
      .map(
        (
          point,
          index
        ) =>
          `${
            index === 0
              ? "M"
              : "L"
          }${xFor(index).toFixed(2)},${yFor(point.value).toFixed(2)}`
      )
      .join(" ");

  const areaPath =
    `${path} L${xFor(series.length - 1).toFixed(2)},${(padTop + plotHeight).toFixed(2)} L${xFor(0).toFixed(2)},${(padTop + plotHeight).toFixed(2)} Z`;

  const configuredInterval =
    Math.max(
      1,
      Number(
        safeSettings.xInterval
      ) || 1
    );

  const maxVisibleXLabels =
    compact
      ? 0
      : Math.max(
          2,
          Math.floor(
            plotWidth / 16
          )
        );

  const interval =
    compact
      ? series.length
      : Math.max(
          configuredInterval,
          Math.ceil(
            series.length /
              Math.max(
                1,
                maxVisibleXLabels
              )
          )
        );

  const rotateXLabels =
    !compact &&
    series.length > 4;

  const xAxisLabels =
    series.map(
      (point) =>
        safeSettings.showExactX
          ? point.dateLabel
          : point.shortDate
    );

  const condensedXAxisLabels =
    xAxisLabels.map(
      (label) => {
        if (
          compact ||
          typeof label !==
            "string"
        ) {
          return label;
        }

        const dayMatch =
          label.match(
            /\b(\d{1,2})\b/
          );

        if (
          series.length > 2 &&
          dayMatch
        ) {
          return dayMatch[1];
        }

        return label;
      }
    );

  const referenceY =
    Number.isFinite(
      Number(
        safeSettings.referenceValue
      )
    )
      ? yFor(
          Number(
            safeSettings.referenceValue
          )
        )
      : null;

  return (
    <div
      className={`--TSD-chart ${
        compact
          ? "--TSD-chart--compact"
          : ""
      }`}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${tsdGetStatMeta(row).title} trend`}
      >
        {safeSettings.showGrid
          ? (
              compact
                ? gridValues
                : displayGridValues
            ).map(
              (value) => (
                <g
                  key={`grid-${value}`}
                >
                  <line
                    className="--TSD-chart-grid"
                    x1={
                      padLeft
                    }
                    x2={
                      width -
                      padRight
                    }
                    y1={yFor(
                      value
                    )}
                    y2={yFor(
                      value
                    )}
                  />

                  {!compact ? (
                    <text
                      className="--TSD-chart-axis-label"
                      x={
                        padLeft - 0.8
                      }
                      y={
                        yFor(
                          value
                        ) +
                        1.3
                      }
                      textAnchor="end"
                    >
                      {isPercent
                        ? `${value}%`
                        : value}
                    </text>
                  ) : null}
                </g>
              )
            )
          : null}

        {safeSettings.showCI &&
        isPercent &&
        series.length >
          1 ? (
          <path
            className="--TSD-chart-ci"
            d={[
              ...series.map(
                (
                  point,
                  index
                ) =>
                  `${
                    index ===
                    0
                      ? "M"
                      : "L"
                  }${xFor(index).toFixed(2)},${yFor(point.ciHigh ?? point.value).toFixed(2)}`
              ),

              ...series
                .slice()
                .reverse()
                .map(
                  (
                    point,
                    reverseIndex
                  ) => {
                    const originalIndex =
                      series.length -
                      1 -
                      reverseIndex;

                    return `L${xFor(originalIndex).toFixed(2)},${yFor(point.ciLow ?? point.value).toFixed(2)}`;
                  }
                ),

              "Z",
            ].join(" ")}
          />
        ) : null}

        {safeSettings.showReferenceLine &&
        referenceY !=
          null &&
        referenceY >=
          padTop &&
        referenceY <=
          padTop +
            plotHeight ? (
          <line
            className="--TSD-chart-reference"
            x1={padLeft}
            x2={
              width -
              padRight
            }
            y1={referenceY}
            y2={referenceY}
          />
        ) : null}

        {graphType ===
        "area" ? (
          <path
            className="--TSD-chart-area"
            d={areaPath}
          />
        ) : null}

        {graphType ===
          "line" ||
        graphType ===
          "area" ? (
          <path
            className="--TSD-chart-line"
            d={path}
          />
        ) : null}

        {graphType ===
        "bar"
          ? series.map(
              (
                point,
                index
              ) => {
                const barWidth =
                  Math.max(
                    0.8,
                    (
                      plotWidth /
                      Math.max(
                        1,
                        series.length
                      )
                    ) *
                      0.65
                  );

                const x =
                  xFor(
                    index
                  ) -
                  barWidth /
                    2;

                const y =
                  yFor(
                    point.value
                  );

                return (
                  <rect
                    key={`bar-${index}`}
                    className="--TSD-chart-bar"
                    x={x}
                    y={y}
                    width={
                      barWidth
                    }
                    height={Math.max(
                      0.3,
                      padTop +
                        plotHeight -
                        y
                    )}
                    rx="0.35"
                  />
                );
              }
            )
          : null}

        {(graphType ===
          "scatter" ||
          safeSettings.showMarkers)
          ? series.map(
              (
                point,
                index
              ) => (
                <circle
                  key={`point-${index}`}
                  className="--TSD-chart-point"
                  cx={xFor(
                    index
                  )}
                  cy={yFor(
                    point.value
                  )}
                  r={
                    compact
                      ? 0.7
                      : 1
                  }
                />
              )
            )
          : null}

        {safeSettings.showExactY &&
        !compact
          ? series.map(
              (
                point,
                index
              ) => {
                const yOffset =
                  safeSettings.dataLabelPosition ===
                  "below"
                    ? 4
                    : safeSettings.dataLabelPosition ===
                        "on"
                      ? 1
                      : -2;

                return (
                  <text
                    key={`value-${index}`}
                    className="--TSD-chart-data-label"
                    x={xFor(
                      index
                    )}
                    y={
                      yFor(
                        point.value
                      ) +
                      yOffset
                    }
                    textAnchor="middle"
                  >
                    {row.valueType ===
                    "percent"
                      ? `${point.value.toFixed(0)}%`
                      : point.value.toFixed(1)}
                  </text>
                );
              }
            )
          : null}

        {!compact
          ? series.map(
              (
                point,
                index
              ) => {
                const axisLabel =
                  condensedXAxisLabels[
                    index
                  ];

                const previousAxisLabel =
                  condensedXAxisLabels[
                    index - 1
                  ];

                const nextAxisLabel =
                  condensedXAxisLabels[
                    index + 1
                  ];

                if (
                  index !== 0 &&
                  index !==
                    series.length -
                      1 &&
                  (
                    index %
                      interval !==
                      0 ||
                    axisLabel ===
                      previousAxisLabel ||
                    axisLabel ===
                      nextAxisLabel
                  )
                ) {
                  return null;
                }

                return (
                  <text
                    key={`x-${index}`}
                    className="--TSD-chart-axis-label --TSD-chart-axis-label--x"
                    x={xFor(
                      index
                    )}
                    y={
                      height -
                      (
                        rotateXLabels
                          ? 0.8
                          : 1.5
                      )
                    }
                    textAnchor={
                      rotateXLabels
                        ? index ===
                          0
                          ? "start"
                          : "end"
                        : index ===
                            0
                          ? "start"
                          : index ===
                              series.length -
                                1
                            ? "end"
                            : "middle"
                    }
                    transform={
                      rotateXLabels
                        ? `rotate(-32 ${xFor(index)} ${height - 0.8})`
                        : undefined
                    }
                  >
                    {axisLabel}
                  </text>
                );
              }
            )
          : null}
      </svg>
    </div>
  );
}

function TSDGraphSettingsModal({
  row,
  settings,
  onChange,
  onClose,
  initialTab='graph',dataEditor,duplicate,onRemove,
}) {
  const [settingsTab,setSettingsTab]=useState(initialTab);
  const safeSettings = {
    ...tsdGraphDefaults(row),
    ...(settings || {}),
    dataFilters:settings?.dataFilters || row?._cardFilters,
  };

  const update = (
    patch
  ) => {
    onChange({
      ...safeSettings,
      ...patch,
    });
  };

  return (
    <div
      className="--TSD-modal-backdrop"
      role="presentation"
      onMouseDown={
        onClose
      }
    >
      <div
        className="--TSD-modal --TSD-graph-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Graph settings for ${tsdGetStatMeta(row).title}`}
        onMouseDown={(
          event
        ) =>
          event.stopPropagation()
        }
      >
        <div className="--TSD-modal-header">
          <div>
            <span className="--TSD-eyebrow">
              Graph settings
            </span>

            <h3>
              {
                tsdGetStatMeta(
                  row
                ).title
              }
            </h3>
          </div>

          <button
            className="--TSD-icon-button --TSD-icon-button--large app-cancel-control"
            type="button"
            onClick={
              onClose
            }
            aria-label="Close graph settings"
          >
            ×
          </button>
        </div>

        <nav className="--TSD-metrics-subtabs" aria-label="Element settings"><button className={`--TSD-category-tab ${settingsTab==='graph'?'--TSD-is-active':''}`} type="button" onClick={()=>setSettingsTab('graph')}>Graph settings</button><button className={`--TSD-category-tab ${settingsTab==='data'?'--TSD-is-active':''}`} type="button" onClick={()=>setSettingsTab('data')}>Data Filtering Settings</button></nav>
        {duplicate&&<p className="--TSD-duplicate-warning" role="status">Another element with this statistic and the same data filtering conditions already exists. You can keep both or change these conditions.</p>}
        {settingsTab==='data'?dataEditor:<>
        <div className="--TSD-settings-grid">
          <section className="--TSD-settings-section">
            <div className="--TSD-section-heading">
              <div>
                <span className="--TSD-section-kicker">
                  Visualization
                </span>

                <h4>
                  Graph type
                </h4>
              </div>
            </div>

            <div className="--TSD-graph-type-grid">
              {TSD_GRAPH_TYPES.map(
                (type) => (
                  <button
                    key={
                      type.value
                    }
                    className={`--TSD-graph-type-button ${
                      safeSettings.graphType ===
                      type.value
                        ? "--TSD-is-active"
                        : ""
                    }`}
                    disabled={["pie","donut"].includes(type.value)&&row.valueType!=="percent"}
                    title={["pie","donut"].includes(type.value)&&row.valueType!=="percent"?"Use a percentage metric for a share-of-total chart":undefined}
                    aria-label={`${type.label} chart`}
                    type="button"
                    onClick={() =>
                      update({
                        graphType:
                          type.value,
                      })
                    }
                  >
                    <span
                      className="--TSD-graph-type-preview"
                      aria-hidden="true"
                    >
                      <type.icon size={22}/>
                    </span>

                    <span>
                      {
                        type.label
                      }
                    </span>
                  </button>
                )
              )}
            </div>
          </section>

          <section className="--TSD-settings-section">
            <div className="--TSD-section-heading">
              <div>
                <span className="--TSD-section-kicker">
                  Statistical
                  display
                </span>

                <h4>
                  Show with
                  this graph
                </h4>
              </div>
            </div>

            <div className="--TSD-checkbox-grid">
              {[
                [
                  "showN",
                  "N / sample size",
                ],

                [
                  "showCI",
                  "Confidence interval",
                ],

                [
                  "showPValue",
                  "P-value",
                ],

                [
                  "showMarkers",
                  "Data-point markers",
                ],

                [
                  "showExactY",
                  "Exact Y values",
                ],

                [
                  "showExactX",
                  "Exact X dates",
                ],

                [
                  "showGrid",
                  "Grid lines",
                ],

                [
                  "showReferenceLine",
                  "Reference line",
                ],
              ].map(
                ([
                  key,
                  label,
                ]) => (
                  <label
                    className="--TSD-checkbox-row"
                    key={
                      key
                    }
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(
                        safeSettings[
                          key
                        ]
                      )}
                      onChange={(
                        event
                      ) =>
                        update({
                          [key]:
                            event
                              .target
                              .checked,
                        })
                      }
                    />

                    <span>
                      {label}
                    </span>
                  </label>
                )
              )}
            </div>
          </section>

          <section className="--TSD-settings-section">
            <div className="--TSD-section-heading">
              <div>
                <span className="--TSD-section-kicker">
                  Data points
                </span>

                <h4>
                  Labels &
                  location
                </h4>
              </div>
            </div>

            <div className="--TSD-form-grid">
              <label className="--TSD-field">
                <span>
                  Exact-value
                  label position
                </span>

                <select
                  value={
                    safeSettings.dataLabelPosition
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      dataLabelPosition:
                        event
                          .target
                          .value,
                    })
                  }
                >
                  <option value="above">
                    Above point
                  </option>

                  <option value="on">
                    On point
                  </option>

                  <option value="below">
                    Below point
                  </option>
                </select>
              </label>

              <label className="--TSD-field">
                <span>
                  Confidence
                  level
                </span>

                <select
                  value={
                    safeSettings.confidenceLevel
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      confidenceLevel:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  }
                >
                  <option value={90}>
                    90%
                  </option>

                  <option value={95}>
                    95%
                  </option>

                  <option value={99}>
                    99%
                  </option>
                </select>
              </label>

              <label className="--TSD-field">
                <span>
                  Reference
                  value
                </span>

                <input
                  type="number"
                  value={
                    safeSettings.referenceValue
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      referenceValue:
                        event
                          .target
                          .value,
                    })
                  }
                />
              </label>
            </div>
          </section>

          <section className="--TSD-settings-section">
            <div className="--TSD-section-heading">
              <div>
                <span className="--TSD-section-kicker">
                  Axes
                </span>

                <h4>
                  Intervals &
                  range
                </h4>
              </div>
            </div>

            <div className="--TSD-form-grid">
              <label className="--TSD-field">
                <span>
                  X-axis label
                  interval
                </span>

                <select
                  value={
                    safeSettings.xInterval
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      xInterval:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  }
                >
                  <option value={1}>
                    Every point
                  </option>

                  <option value={2}>
                    Every 2 points
                  </option>

                  <option value={5}>
                    Every 5 points
                  </option>

                  <option value={10}>
                    Every 10 points
                  </option>
                </select>
              </label>

              <label className="--TSD-field">
                <span>
                  Y-axis
                  interval
                </span>

                <select
                  value={
                    safeSettings.yInterval
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      yInterval:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  }
                >
                  <option value={5}>
                    5
                  </option>

                  <option value={10}>
                    10
                  </option>

                  <option value={20}>
                    20
                  </option>

                  <option value={25}>
                    25
                  </option>
                </select>
              </label>

              <label className="--TSD-field">
                <span>
                  Y-axis
                  minimum
                </span>

                <input
                  type="number"
                  placeholder="Auto"
                  value={
                    safeSettings.yMin
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      yMin:
                        event
                          .target
                          .value,
                    })
                  }
                />
              </label>

              <label className="--TSD-field">
                <span>
                  Y-axis
                  maximum
                </span>

                <input
                  type="number"
                  placeholder="Auto"
                  value={
                    safeSettings.yMax
                  }
                  onChange={(
                    event
                  ) =>
                    update({
                      yMax:
                        event
                          .target
                          .value,
                    })
                  }
                />
              </label>
            </div>
          </section>
        </div>

        </>}
        <div className="--TSD-modal-footer">
          {onRemove&&<button className="--TSD-text-button --TSD-text-button--danger" type="button" onClick={onRemove}>Remove this element</button>}
          <button
            className="--TSD-button --TSD-button--ghost"
            type="button"
            onClick={() =>
              onChange({
                ...safeSettings, ...tsdGraphDefaults(row),
              })
            }
          >
            Reset graph
          </button>

          <button
            className="--TSD-button --TSD-button--primary"
            type="button"
            onClick={
              onClose
            }
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function TSDStatCard({
  row,
  matches,
  previousRow,
  settings,
  minimumSample,
  onOpenSettings,
  onOpenData,
  children,
}) {
  const meta =
    tsdGetStatMeta(
      row
    );

  const series =
    useMemo(
      () =>
        tsdBuildSeries(
          row,
          matches
        ),
      [
        row,
        matches,
      ]
    );

  const safeSettings = {
    ...tsdGraphDefaults(row),
    ...(settings || {}),
  };

  const belowSample =
    !row.isNoData &&
    Number(
      row.sample || 0
    ) <
      Number(
        minimumSample || 0
      );

  const previousValue =
    Number(
      previousRow?.value
    );

  const currentValue =
    Number(
      row?.value
    );

  const delta =
    Number.isFinite(
      currentValue
    ) &&
    Number.isFinite(
      previousValue
    )
      ? currentValue -
        previousValue
      : null;

  const ci =
    tsdGetConfidenceInterval(
      row,
      safeSettings.confidenceLevel
    );

  return (
    <article
      className={`--TSD-stat-card ${row.studioView?'--TSD-stat-card--insights':''} ${
        belowSample
          ? "--TSD-stat-card--below-sample"
          : ""
      }`}
    >
      <div className="--TSD-stat-card-header">
        <div className="--TSD-stat-card-title-wrap">
          <span
            className="--TSD-stat-card-icon"
            aria-hidden="true"
          >
            ◈
          </span>

          <h3>
            {meta.title}
          </h3>

          <TSDInfo
            row={row}
          />
        </div>

        <button
          className="--TSD-icon-button --TSD-settings-button"
          type="button"
          onClick={
            onOpenSettings
          }
          aria-label={`Open settings for ${meta.title}`}
          title={`Customize ${meta.title}`}
        >
          ⚙
        </button>
      </div>

      <div className="--TSD-stat-card-value-row">
        <strong className="--TSD-stat-card-value">
          {belowSample
            ? "—"
            : tsdFormatValue(
                row
              )}
        </strong>

        {delta !=
          null &&
        !belowSample ? (
          <span
            className={`--TSD-delta ${
              delta > 0
                ? "--TSD-delta--positive"
                : delta < 0
                  ? "--TSD-delta--negative"
                  : "--TSD-delta--neutral"
            }`}
          >
            {delta > 0
              ? "↑"
              : delta < 0
                ? "↓"
                : "→"}{" "}

            {Math.abs(
              delta
            ).toFixed(1)}

            {row.valueType ===
            "percent"
              ? "%"
              : ""}

            <small>
              vs previous
              period
            </small>
          </span>
        ) : (
          <span className="--TSD-delta --TSD-delta--neutral">
            <small>
              No
              previous-period
              comparison
            </small>
          </span>
        )}
      </div>

      {belowSample ? (
        <div className="--TSD-sample-warning">
          <strong>
            Below sample
            threshold
          </strong>

          <span>
            This statistic
            has N=
            {row.sample ||
              0}
            ; your filter
            requires at
            least N=
            {minimumSample}.
          </span>
        </div>
      ) : row.studioView ? children : (
        <TSDMiniChart
          row={row}
          series={series}
          settings={
            safeSettings
          }
        />
      )}

      <div className="--TSD-stat-card-footer">
        <button className="StatsStudio__data-action" type="button" onClick={onOpenData} aria-label={`Data and display for ${meta.title}`}><SlidersHorizontal size={13}/>Data &amp; display</button>
        {true ? (
          <span>
            N:{" "}
            {row.sample ??
              0}
          </span>
        ) : null}

        {safeSettings.showCI &&
        ci ? (
          <span>
            {
              safeSettings.confidenceLevel
            }
            % CI:{" "}
            {ci.low.toFixed(
              1
            )}
            –
            {ci.high.toFixed(
              1
            )}
            %
          </span>
        ) : null}

        {safeSettings.showPValue &&
        row.pValue !=
          null ? (
          <span>
            p ={" "}
            {row.pValue.toFixed(
              4
            )}
          </span>
        ) : null}

        {!safeSettings.showN &&
        !safeSettings.showCI &&
        !safeSettings.showPValue ? (
          <span>
            Use ⚙ to add
            statistical
            detail.
          </span>
        ) : null}
      </div>
      <StudioFilterSummary row={row} filters={row._cardFilters || EMPTY_CARD_FILTERS} matches={matches} library={row.id?.startsWith("improvement:")||row.studioView==='improvements'}/>
    </article>
  );
}

function TSDReportStatElement({
  row,
  matches,
  settings,insight,
}) {
  const belowSample=Number(row.sample || 0)<Number(row._cardFilters?.minSample || 0);
  const series =
    useMemo(
      () =>
        tsdBuildSeries(
          row,
          matches
        ),
      [
        row,
        matches,
      ]
    );

  const meta =
    tsdGetStatMeta(
      row
    );

  return (
    <div className="--TSD-report-stat-content">
      <div className="--TSD-report-stat-heading">
        <strong>
          {meta.title}
        </strong>

        <span>
          {belowSample?'Below minimum N':tsdFormatValue(
            row
          )}
        </span>
      </div>

      {belowSample?<p>Below sample threshold · requires N≥{row._cardFilters.minSample}</p>:insight || <TSDMiniChart
        row={row}
        series={series}
        settings={
          settings
        }
        compact
      />}

      <div className="--TSD-report-stat-meta">
        <span>
          N=
          {row.sample ??
            0}
        </span>

        {row.valueType ===
          "percent" &&
        row.ciLow !=
          null &&
        row.ciHigh !=
          null ? (
          <span>
            95% CI{" "}
            {row.ciLow.toFixed(
              1
            )}
            –
            {row.ciHigh.toFixed(
              1
            )}
            %
          </span>
        ) : null}
      </div>
      <StudioFilterSummary row={row} filters={row._cardFilters || EMPTY_CARD_FILTERS} matches={matches} library={row.id?.startsWith("improvement:")||row.studioView==='improvements'}/>
    </div>
  );
}

function tsdGetReportPageDimensions(
  page
) {
  const base =
    TSD_PAGE_SIZES[
      page?.size
    ] ||
    TSD_PAGE_SIZES.a4;

  if (
    page?.orientation ===
      "landscape" &&
    page?.size !==
      "presentation"
  ) {
    return {
      width:
        base.height,

      height:
        base.width,
    };
  }

  return {
    width:
      base.width,

    height:
      base.height,
  };
}

function tsdClamp(
  value,
  min,
  max
) {
  return Math.max(
    min,
    Math.min(
      max,
      value
    )
  );
}

export default function TeamStatsSummary({
  fallbackTeamId = null,
}) {
  const {
    teamId:
      paramTeamId,
  } = useParams();

  const teamId =
    paramTeamId ||
    fallbackTeamId;

  const prefsStorageKey =
    `${TSD_PREFS_KEY_PREFIX}${teamId || "default"}`;

  const savedViewsStorageKey =
    `${TSD_SAVED_VIEWS_KEY_PREFIX}${teamId || "default"}`;

  const statMenuSessionStorageKey =
    `${TSD_STAT_MENU_SESSION_KEY_PREFIX}${teamId || "default"}`;

  const [
    teamName,
    setTeamName,
  ] = useState(
    "Team"
  );

  const [
    rawMatches,
    setMatches,
  ] = useState([]);
  const [setupEvents, setSetupEvents] = useState([]);
  const [rosterNames,setRosterNames]=useState({});
  const [eventFilters,setEventFilters]=useState({});
  const [,setDraftEventFilters]=useState({});
  const [metricsMenuTab,setMetricsMenuTab]=useState('statistics');
  const [studioPointFilters,setStudioPointFilters]=useState({});
  const unfilteredMatches = useMemo(() => canonicalPlayerMatches(rawMatches.map(match => setupStatsProjection(match, setupEvents)),rosterNames), [rawMatches, setupEvents,rosterNames]);
  const matches=unfilteredMatches;



  const [
    customAlert,
    setCustomAlert,
  ] = useState(
    null
  );

  const [, setMenuPortalTarget] = useState(
    null
  );

  const [, setDraftMaps] = useState([]);

  const [, setDraftComps] = useState([]);

  const [, setDraftSide] = useState(
    "both"
  );

  const [, setDraftGameTypes] = useState([]);

  const [, setDraftDateFrom] = useState("");

  const [, setDraftDateTo] = useState("");

  const [, setDraftMinSample] = useState(0);

  const [, setDraftPlayers] = useState([]);

  const [
    appliedFilters,
    setAppliedFilters,
  ] = useState({
    maps: [],
    comps: [],
    side:
      "both",
    gameTypes: [],
    dateFrom: "",
    dateTo: "",
    minSample: 0,
    players: [],
  });

  const [
    statMenuSearch,
    setStatMenuSearch,
  ] = useState("");

  const [
    statCategory,
    setStatCategory,
  ] = useState(
    "Starter"
  );

  const [
    statMenuScope,
    setStatMenuScope,
  ] = useState(
    "Overall"
  );

  const [
    selectedStatKeys,
    setSelectedStatKeys,
  ] = useState([]);

  const [
    selectionInitialized,
    setSelectionInitialized,
  ] = useState(
    false
  );

  const [
    graphSettingsByStat,
    setGraphSettingsByStat,
  ] = useState({});

  const [
    settingsStatKey,
    setSettingsStatKey,
  ] = useState(
    null
  );

  const [
    statMenuPinned,
    setStatMenuPinned,
  ] = useState(
    false
  );

  const [
    statMenuHoverOpen,
    setStatMenuHoverOpen,
  ] = useState(
    false
  );

  const [
    presetMenuHoverOpen,
    setPresetMenuHoverOpen,
  ] = useState(
    false
  );

  const [
    statMenuExpanded,
    setStatMenuExpanded,
  ] = useState(
    false
  );

  const [
    statMenuScrollTop,
    setStatMenuScrollTop,
  ] = useState(
    0
  );

  const [
    statMenuFlyoutStyle,
    setStatMenuFlyoutStyle,
  ] = useState({});

  const [
    dashboardScale,
    setDashboardScale,
  ] = useState(
    TSD_DASHBOARD_SCALE_RANGE.default
  );

  const [
    savedViews,
    setSavedViews,
  ] = useState({});

  const [
    saveViewOpen,
    setSaveViewOpen,
  ] = useState(
    false
  );

  const [
    saveViewName,
    setSaveViewName,
  ] = useState(
    ""
  );

  const [
    activePresetName,
    setActivePresetName,
  ] = useState("");

  const [
    presetBeingEdited,
    setPresetBeingEdited,
  ] = useState(
    null
  );

  const [
    presetEditValue,
    setPresetEditValue,
  ] = useState(
    ""
  );

  const [
    prefsLoaded,
    setPrefsLoaded,
  ] = useState(
    false
  );

  const [
    reportMode,
    setReportMode,
  ] = useState(
    false
  );

  const [
    reportPages,
    setReportPages,
  ] = useState([
    {
      id:
        "page-1",

      name:
        "Page 1",

      ...TSD_REPORT_DEFAULT_PAGE,
    },
  ]);

  const [
    activeReportPageId,
    setActiveReportPageId,
  ] = useState(
    "page-1"
  );

  const [
    reportElements,
    setReportElements,
  ] = useState([]);

  const [
    selectedReportElementId,
    setSelectedReportElementId,
  ] = useState(
    null
  );

  const reportCanvasRef =
    useRef(null);

  const pageShellRef =
    useRef(null);

  const reportDragRef =
    useRef(null);

  const statMenuShellRef =
    useRef(null);

  const statMenuFlyoutRef =
    useRef(null);

  const presetMenuShellRef =
    useRef(null);

  const statMenuScrollRef =
    useRef(null);

  const statMenuScrollRestoreRef =
    useRef(false);

  const statMenuCloseTimerRef =
    useRef(null);

  const presetMenuCloseTimerRef =
    useRef(null);

  const reportHistoryRef =
    useRef({
      past: [],
      future: [],
    });

  const reportStateRef =
    useRef(null);

  const reportShortcutHandlersRef =
    useRef({
      undo: null,
      redo: null,
      deleteElement:
        null,
    });

  const reportElementUpdaterRef =
    useRef(null);

  const clearHoverCloseTimer =
    (timerRef) => {
      if (
        timerRef.current
      ) {
        window.clearTimeout(
          timerRef.current
        );

        timerRef.current =
          null;
      }
    };

  const openStatMenuHover =
    () => {
      clearHoverCloseTimer(
        statMenuCloseTimerRef
      );

      setStatMenuHoverOpen(
        true
      );
    };

  const scheduleStatMenuHoverClose =
    () => {
      if (
        statMenuPinned
      ) {
        return;
      }

      clearHoverCloseTimer(
        statMenuCloseTimerRef
      );

      statMenuCloseTimerRef.current =
        window.setTimeout(
          () => {
            setStatMenuHoverOpen(
              false
            );

            statMenuCloseTimerRef.current =
              null;
          },
          180
        );
    };

  const openPresetMenuHover =
    () => {
      clearHoverCloseTimer(
        presetMenuCloseTimerRef
      );

      setPresetMenuHoverOpen(
        true
      );
    };

  const schedulePresetMenuHoverClose =
    () => {
      clearHoverCloseTimer(
        presetMenuCloseTimerRef
      );

      presetMenuCloseTimerRef.current =
        window.setTimeout(
          () => {
            setPresetMenuHoverOpen(
              false
            );

            presetMenuCloseTimerRef.current =
              null;
          },
          180
        );
    };

  useEffect(
    () => {
      setMenuPortalTarget(
        typeof document !==
          "undefined"
          ? document.body
          : null
      );
    },
    []
  );

  useEffect(
    () => () => {
      clearHoverCloseTimer(
        statMenuCloseTimerRef
      );

      clearHoverCloseTimer(
        presetMenuCloseTimerRef
      );
    },
    []
  );

  useEffect(
    () => {
      if (!teamId) {
        return;
      }

      let parsedPrefs =
        null;

      try {
        parsedPrefs =
          JSON.parse(
            localStorage.getItem(
              prefsStorageKey
            ) || "null"
          );
      } catch (
        error
      ) {
        console.warn(
          "Failed to load TeamStatsSummary dashboard preferences:",
          error
        );
      }

      setEventFilters(parsedPrefs?.eventFilters || {});
      setDraftEventFilters(parsedPrefs?.eventFilters || {});
      setStudioPointFilters(parsedPrefs?.studioPointFilters || {});

      if (
        parsedPrefs?.filters
      ) {
        const nextFilters = {
          maps:
            Array.isArray(
              parsedPrefs
                .filters
                .maps
            )
              ? parsedPrefs
                  .filters
                  .maps
              : [],

          comps:
            Array.isArray(
              parsedPrefs
                .filters
                .comps
            )
              ? parsedPrefs
                  .filters
                  .comps
              : [],

          side:
            [
              "attack",
              "defense",
              "both",
            ].includes(
              parsedPrefs
                .filters
                .side
            )
              ? parsedPrefs
                  .filters
                  .side
              : "both",

          gameTypes:
            Array.isArray(
              parsedPrefs
                .filters
                .gameTypes
            )
              ? parsedPrefs
                  .filters
                  .gameTypes
              : [],

          dateFrom:
            parsedPrefs
              .filters
              .dateFrom ||
            "",

          dateTo:
            parsedPrefs
              .filters
              .dateTo ||
            "",

          minSample:
            Number(
              parsedPrefs
                .filters
                .minSample ||
                0
            ),

          players:
            Array.isArray(
              parsedPrefs
                .filters
                .players
            )
              ? parsedPrefs
                  .filters
                  .players
              : [],
        };

        setAppliedFilters(
          nextFilters
        );

        setDraftMaps(
          nextFilters.maps
        );

        setDraftComps(
          nextFilters.comps
        );

        setDraftSide(
          nextFilters.side
        );

        setDraftGameTypes(
          nextFilters.gameTypes
        );

        setDraftDateFrom(
          nextFilters.dateFrom
        );

        setDraftDateTo(
          nextFilters.dateTo
        );

        setDraftMinSample(
          nextFilters.minSample
        );

        setDraftPlayers(
          nextFilters.players
        );
      }

      if (
        Array.isArray(
          parsedPrefs?.selectedStatKeys
        )
      ) {
        setSelectedStatKeys(
          parsedPrefs.selectedStatKeys
        );

        setSelectionInitialized(
          true
        );
      }

      if (
        parsedPrefs?.graphSettingsByStat &&
        typeof parsedPrefs.graphSettingsByStat ===
          "object"
      ) {
        setGraphSettingsByStat(
          parsedPrefs.graphSettingsByStat
        );
      }

      if (
        Number.isFinite(
          Number(
            parsedPrefs?.dashboardScale
          )
        )
      ) {
        setDashboardScale(
          tsdNormalizeDashboardScale(
            parsedPrefs.dashboardScale,
            parsedPrefs?.dashboardScaleVersion
          )
        );
      } else if (
        typeof parsedPrefs?.dashboardScale ===
        "string" &&
        Object.prototype.hasOwnProperty.call(
          TSD_DASHBOARD_SCALE_OPTIONS,
          parsedPrefs.dashboardScale
        )
      ) {
        setDashboardScale(
          tsdNormalizeDashboardScale(
            parsedPrefs.dashboardScale,
            parsedPrefs?.dashboardScaleVersion
          )
        );
      }

      try {
        const parsedViews =
          JSON.parse(
            localStorage.getItem(
              savedViewsStorageKey
            ) || "{}"
          );

        const nextViews =
          parsedViews &&
          typeof parsedViews ===
            "object"
            ? parsedViews
            : {};

        setSavedViews(
          nextViews
        );

        const presetNames =
          Object.keys(
            nextViews
          ).sort();

        setActivePresetName(
          presetNames[0] ||
            ""
        );
      } catch (
        error
      ) {
        console.warn(
          "Failed to load TeamStatsSummary saved views:",
          error
        );

        setSavedViews({});
        setActivePresetName(
          ""
        );
      }

      try {
        const parsedMenuSession =
          JSON.parse(
            sessionStorage.getItem(
              statMenuSessionStorageKey
            ) || "null"
          );

        if (
          parsedMenuSession &&
          typeof parsedMenuSession ===
            "object"
        ) {
          if (
            typeof parsedMenuSession.search ===
            "string"
          ) {
            setStatMenuSearch(
              parsedMenuSession.search
            );
          }

          if (
            TSD_MENU_GROUP_ORDER.includes(
              parsedMenuSession.group
            )
          ) {
            setStatCategory(
              parsedMenuSession.group
            );
          }

          if (
            TSD_MENU_SCOPE_OPTIONS.some(
              (option) =>
                option.value ===
                parsedMenuSession.scope
            )
          ) {
            setStatMenuScope(
              parsedMenuSession.scope
            );
          }

          setStatMenuPinned(
            Boolean(
              parsedMenuSession.pinned
            )
          );

          setStatMenuScrollTop(
            Number(
              parsedMenuSession.scrollTop ||
                0
            )
          );
        }
      } catch (
        error
      ) {
        console.warn(
          "Failed to load TeamStatsSummary statistics menu session:",
          error
        );
      }

      setPrefsLoaded(
        true
      );
    },
    [
      prefsStorageKey,
      savedViewsStorageKey,
      statMenuSessionStorageKey,
      teamId,
    ]
  );

  useEffect(
    () => {
      if (
        !prefsLoaded ||
        !teamId
      ) {
        return;
      }

      localStorage.setItem(
        prefsStorageKey,
        JSON.stringify({
          filters:
            appliedFilters,

          selectedStatKeys,eventFilters,studioPointFilters,

          graphSettingsByStat,

          dashboardScale,
          dashboardScaleVersion:
            TSD_DASHBOARD_SCALE_VERSION,
        })
      );
    },
    [
      prefsLoaded,
      teamId,
      prefsStorageKey,eventFilters,studioPointFilters,
      appliedFilters,
      selectedStatKeys,
      graphSettingsByStat,
      dashboardScale,
    ]
  );

  useEffect(
    () => {
      if (!teamId) {
        return;
      }

      sessionStorage.setItem(
        statMenuSessionStorageKey,
        JSON.stringify({
          search:
            statMenuSearch,
          group:
            statCategory,
          scope:
            statMenuScope,
          pinned:
            statMenuPinned,
          scrollTop:
            statMenuScrollTop,
        })
      );
    },
    [
      teamId,
      statMenuSessionStorageKey,
      statMenuSearch,
      statCategory,
      statMenuScope,
      statMenuPinned,
      statMenuScrollTop,
    ]
  );

  useEffect(
    () => {
      const statMenuIsOpen =
        statMenuPinned ||
        statMenuHoverOpen;

      if (!statMenuIsOpen) {
        statMenuScrollRestoreRef.current =
          false;
        return;
      }

      if (
        statMenuScrollRestoreRef.current ||
        !statMenuScrollRef.current
      ) {
        return;
      }

      statMenuScrollRef.current.scrollTop =
        statMenuScrollTop;
      statMenuScrollRestoreRef.current =
        true;
    },
    [
      statMenuPinned,
      statMenuHoverOpen,
      statMenuScrollTop,
    ]
  );

  useEffect(
    () => {
      if (
        reportMode ||
        !pageShellRef.current
      ) {
        return undefined;
      }

      const overflowTargets =
        [];

      let current =
        pageShellRef.current
          .parentElement;

      while (current) {
        if (
          current.classList?.contains(
            "Main__content__box"
          ) ||
          current.classList?.contains(
            "Main__content__column"
          )
        ) {
          overflowTargets.push({
            element:
              current,
            overflow:
              current.style
                .overflow,
            overflowY:
              current.style
                .overflowY,
          });

          current.style.overflow =
            "visible";

          current.style.overflowY =
            "visible";
        }

        current =
          current.parentElement;
      }

      return () => {
        overflowTargets.forEach(
          ({
            element,
            overflow,
            overflowY,
          }) => {
            element.style.overflow =
              overflow;

            element.style.overflowY =
              overflowY;
          }
        );
      };
    },
    [reportMode]
  );

  useEffect(
    () => {
      if (!reportMode) {
        return undefined;
      }

      const beforePrint =
        () => {
          if (
            typeof document !==
            "undefined"
          ) {
            document.body.classList.add(
              "--TSD-is-printing-report"
            );
          }
        };

      const afterPrint =
        () => {
          if (
            typeof document !==
            "undefined"
          ) {
            document.body.classList.remove(
              "--TSD-is-printing-report"
            );
          }
        };

      const onKeyDown =
        (event) => {
          if (
            tsdIsEditableTarget(
              event.target
            )
          ) {
            return;
          }

          const key =
            String(
              event.key || ""
            ).toLowerCase();

          const hasModifier =
            event.ctrlKey ||
            event.metaKey;

          if (
            hasModifier &&
            key === "z"
          ) {
            const handlers =
              reportShortcutHandlersRef.current;

            event.preventDefault();

            if (
              event.shiftKey
            ) {
              handlers.redo?.();
            } else {
              handlers.undo?.();
            }

            return;
          }

          if (
            hasModifier &&
            key === "y"
          ) {
            const handlers =
              reportShortcutHandlersRef.current;

            event.preventDefault();
            handlers.redo?.();
            return;
          }

          if (
            (
              event.key ===
                "Backspace" ||
              event.key ===
                "Delete"
            ) &&
            selectedReportElementId
          ) {
            const handlers =
              reportShortcutHandlersRef.current;

            event.preventDefault();
            handlers.deleteElement?.(
              selectedReportElementId
            );
            return;
          }

          if (
            event.key ===
            "Escape"
          ) {
            setSelectedReportElementId(
              null
            );
          }
        };

      window.addEventListener(
        "beforeprint",
        beforePrint
      );

      window.addEventListener(
        "afterprint",
        afterPrint
      );

      window.addEventListener(
        "keydown",
        onKeyDown
      );

      return () => {
        window.removeEventListener(
          "beforeprint",
          beforePrint
        );

        window.removeEventListener(
          "afterprint",
          afterPrint
        );

        window.removeEventListener(
          "keydown",
          onKeyDown
        );

        if (
          typeof document !==
          "undefined"
        ) {
          document.body.classList.remove(
            "--TSD-is-printing-report"
          );
        }
      };
    },
    [
      reportMode,
      selectedReportElementId,
    ]
  );

  useEffect(
    () => {
      if (!teamId) {
        return undefined;
      }

      setSetupEvents([]);
      setMatches([]);
      setRosterNames({});
      const unsubTeam =
        onSnapshot(
          doc(
            db,
            "teams",
            teamId
          ),

          (snap) => {
            setSetupEvents(snap.data()?.events || []);
            setRosterNames(snap.data()?.memberUsernames || {});
            setTeamName(
              snap.exists()
                ? snap.data()
                    ?.teamName ||
                    "Team"
                : "Team"
            );
          },

          (error) => {
            console.error(
              "TeamStatsSummary team listener failed:",
              error
            );

            setCustomAlert({
              message:
                "Failed to load team details.",

              type:
                "error",

              position:
                "top",
            });
          }
        );

      const q =
        query(
          collection(
            db,
            "teams",
            teamId,
            "matches"
          ),

          orderBy(
            "matchDate",
            "desc"
          )
        );

      const unsubMatches =
        onSnapshot(
          q,

          (snap) =>
            setMatches(
              snap.docs.map(
                (item) => ({
                  id:
                    item.id,

                  ...item.data(),
                })
              )
            ),

          (error) => {
            console.error(
              "TeamStatsSummary match listener failed:",
              error
            );

            setCustomAlert({
              message:
                "Failed to load team summary data.",

              type:
                "error",

              position:
                "top",
            });
          }
        );

      return () => {
        unsubTeam();
        unsubMatches();
      };
    },
    [
      teamId,

    ]
  );

  const allMaps =
    useMemo(
      () =>
        getDistinctMaps(
          matches
        ).map(
          (value) => ({
            value,
            label:
              value,
          })
        ),
      [matches]
    );

  const allComps =
    useMemo(
      () =>
        getDistinctComps(
          matches
        ).map(
          (value) => ({
            value,
            label:
              value,
          })
        ),
      [matches]
    );

  const allPlayers =
    useMemo(
      () =>
        getDistinctPlayers(
          matches
        ).map(
          (value) => ({
            value,
            label:
              value,
          })
        ),
      [matches]
    );

  // The catalog always uses all data; each dashboard instance evaluates its own conditions.
  const filteredMatches = unfilteredMatches;
  const [studioRows,setStudioRows]=useState([]);
  const [studioData,setStudioData]=useState({reviews:[],notes:[],loading:true,error:''});
  const [settingsInitialTab,setSettingsInitialTab]=useState('graph');
  const sideVariantRows=useMemo(()=>tsdBuildOverallRowsOnly(unfilteredMatches),[unfilteredMatches]);
  const availableRows=useMemo(()=>[...tsdFilterRowsForSide(sideVariantRows,'both'),eventCountRow(unfilteredMatches),...studioRows,...INSIGHT_VIEWS.map(([key,title,definition])=>({id:`view:${key}`,studioMetric:true,studioView:key,displayLabel:title,category:'Review',sectionType:'overall',scope:'Overall',side:'Match',value:0,valueType:'count',sample:0,meta:{title,definition}}))],[unfilteredMatches,studioRows,sideVariantRows]);

  useEffect(
    () => {
      if (
        !prefsLoaded || selectionInitialized ||
        availableRows.length ===
          0
      ) {
        return;
      }

      setSelectedStatKeys(
        tsdDefaultSelectedKeys(
          availableRows
        )
      );

      setSelectionInitialized(
        true
      );
    },
    [
      availableRows,
      selectionInitialized,prefsLoaded,
    ]
  );

  const rowBySelectionKey =
    useMemo(
      () => {
        const map =
          new Map();

        availableRows.forEach(
          (row) => {
            const key =
              tsdSelectionKey(
                row
              );

            const existing =
              map.get(key);

            if (
              !existing ||
              tsdGetSidePreferenceScore(
                row,
                appliedFilters.side
              ) >
                tsdGetSidePreferenceScore(
                  existing,
                  appliedFilters.side
                )
            ) {
              map.set(
                key,
                row
              );
            }
          }
        );

        return map;
      },
      [
        availableRows,
        appliedFilters.side,
      ]
    );

  const legacyRowBySelectionKey =
    useMemo(
      () => {
        const map =
          new Map();

        availableRows.forEach(
          (row) => {
            const key =
              tsdLegacySelectionKey(
                row
              );

            const existing =
              map.get(key);

            if (
              !existing ||
              tsdGetSidePreferenceScore(
                row,
                appliedFilters.side
              ) >
                tsdGetSidePreferenceScore(
                  existing,
                  appliedFilters.side
                )
            ) {
              map.set(
                key,
                row
              );
            }
          }
        );

        return map;
      },
      [
        availableRows,
        appliedFilters.side,
      ]
    );

  const evaluatedRows=useMemo(()=>{const datasets=new Map();return [...new Set([...selectedStatKeys,...reportElements.filter(element=>element.type==='stat').map(element=>element.statKey).filter(Boolean)])].map(instanceKey=>{
    const metricKey=metricKeyFor(instanceKey);
    const base=rowBySelectionKey.get(metricKey)||legacyRowBySelectionKey.get(metricKey);
    if(!base)return null;
    const settings=graphSettingsByStat[instanceKey] || {};
    const filters=cardFilters(settings,appliedFilters,eventFilters);
    const supportsSide=metricKey.startsWith('focus:')||metricKey.startsWith('focus-change:')||metricKey.startsWith('improvement:')||['focus','improvements'].includes(base.studioView)||/^(ATK|DEF)/.test(base.side || '')||tsdRowHasSideVariant(base,sideVariantRows,'ATK')||tsdRowHasSideVariant(base,sideVariantRows,'DEF');
    if(!supportsSide)filters.side='both';
    const datasetKey=filterSignature('dataset',{...filters,minSample:0,focusPoint:''});
    let dataset=datasets.get(datasetKey);
    if(!dataset){
      const cardMatches=tsdFilterMatches(unfilteredMatches,filters).filter(match=>extraMatchFilter(match,filters.eventFilters));
      const insightData=filterInsightData(studioData,filters,cardMatches);
      dataset={cardMatches,insightData,matchRows:buildAllRows(cardMatches),reviewRows:studioReportRows(insightData.reviews,insightData.notes,cardMatches)};
      datasets.set(datasetKey,dataset);
    }
    const {cardMatches,insightData}=dataset;
    const candidates=dataset.reviewRows;
    let evaluated=metricKey==='events:count'?eventCountRow(cardMatches):base.studioMetric?(candidates.find(row=>tsdSelectionKey(row)===metricKey)||{...base,value:null,sample:0,total:0,made:0,series:[],isNoData:true}):tsdEvaluateCardMetric(base,cardMatches,filters.side,dataset.matchRows);
    if(base.studioView)evaluated={...base,value:base.studioView==='improvements'?insightData.notes.length:base.studioView==='focus'?insightData.reviews.length:cardMatches.length,sample:base.studioView==='improvements'?insightData.notes.length:base.studioView==='focus'?insightData.reviews.length:cardMatches.length,isNoData:false};
    if(base.studioView==='focus'){const chosen=filters.focusPoint || insightData.reviews[0]?.analyticsKey;const focusRow=candidates.find(row=>row.id==='focus:'+chosen);evaluated={...evaluated,value:focusRow?.value ?? null,valueType:'number',sample:focusRow?.sample || 0,isNoData:!focusRow || focusRow.isNoData};}
    const previousFilters=tsdBuildPreviousPeriodFilters(filters);
    const previousMatches=previousFilters?tsdFilterMatches(unfilteredMatches,previousFilters).filter(match=>extraMatchFilter(match,filters.eventFilters)):[];
    const previousRow=previousFilters&&!base.studioMetric?tsdEvaluateCardMetric(base,previousMatches,filters.side):null;
    const baseMeta=tsdGetStatMeta(evaluated);
    return {...evaluated,_dashboardKey:instanceKey,_supportsSide:supportsSide,_cardMatches:cardMatches,_cardFilters:filters,_insightData:insightData,_previousRow:previousRow,_dashboardMeta:{...baseMeta,title:filteredTitle(baseMeta.title,filters,{library:metricKey.startsWith('improvement:')||base.studioView==='improvements'})}};
  }).filter(Boolean);},[selectedStatKeys,reportElements,rowBySelectionKey,legacyRowBySelectionKey,graphSettingsByStat,appliedFilters,eventFilters,unfilteredMatches,studioData,sideVariantRows]);
  const selectedRows=useMemo(()=>evaluatedRows.filter(row=>selectedStatKeys.includes(row._dashboardKey)),[evaluatedRows,selectedStatKeys]);
  const dashboardRowByKey=useMemo(()=>new Map(evaluatedRows.map(row=>[row._dashboardKey,row])),[evaluatedRows]);

  useEffect(
    () => {
      if (
        selectedStatKeys.length ===
        0
      ) {
        return;
      }

      const normalizedKeys =
        Array.from(
          new Set(
            selectedStatKeys.map(
              (key) => {
                if(key.includes('::instance:'))return key;
                const row =
                  rowBySelectionKey.get(
                    key
                  ) ||
                  legacyRowBySelectionKey.get(
                    key
                  );

                return row
                  ? tsdSelectionKey(
                      row
                    )
                  : key;
              }
            )
          )
        );

      if (
        normalizedKeys.length ===
          selectedStatKeys.length &&
        normalizedKeys.every(
          (
            key,
            index
          ) =>
            key ===
            selectedStatKeys[
              index
            ]
        )
      ) {
        return;
      }

      setSelectedStatKeys(
        normalizedKeys
      );
    },
    [
      legacyRowBySelectionKey,
      rowBySelectionKey,
      selectedStatKeys,
    ]
  );

  const statMenuOpen =
    statMenuPinned ||
    statMenuHoverOpen;

  const presetMenuOpen =
    presetMenuHoverOpen;

  useEffect(()=>{
    if(!presetMenuOpen)return undefined;
    const outside=event=>{if(!presetMenuShellRef.current?.contains(event.target))setPresetMenuHoverOpen(false);};
    const escape=event=>{if(event.key==='Escape'){setPresetMenuHoverOpen(false);presetMenuShellRef.current?.querySelector('button')?.focus();}};
    document.addEventListener('pointerdown',outside);document.addEventListener('focusin',outside);document.addEventListener('keydown',escape);
    return()=>{document.removeEventListener('pointerdown',outside);document.removeEventListener('focusin',outside);document.removeEventListener('keydown',escape);};
  },[presetMenuOpen]);

  const statCategories =
    useMemo(
      () => {
        return TSD_MENU_GROUP_ORDER.filter(
          (group) =>
            group !==
              "All" && group!=="Review & development" &&
            availableRows.some(
              (row) =>
                tsdGetMenuGroup(
                  row
                ) === group
            )
        );
      },
      [availableRows]
    );

  const statMenuScopeOptions =
    useMemo(
      () =>
        TSD_MENU_SCOPE_OPTIONS.filter(
          (option) =>
            option.value ===
              "All scopes" ||
            availableRows.some(
              (row) =>
                tsdGetMenuScope(
                  row
                ) ===
                option.value
            )
      ),
      [availableRows]
    );

  useEffect(
    () => {
      if (
        statCategories.length ===
        0
      ) {
        return;
      }

      if (
        statCategory ===
          "All" ||
        !statCategories.includes(
          statCategory
        )
      ) {
        setStatCategory(
          statCategories.includes(
            "Starter"
          )
            ? "Starter"
            : statCategories[0]
        );
      }
    },
    [
      statCategories,
      statCategory,
    ]
  );

  const statMenuRows =
    useMemo(
      () => {
        const search =
          statMenuSearch
            .trim()
            .toLowerCase();

        return availableRows.filter(
          (row) => {
            if(row.category==='Review')return false;
            if (
              statCategory !==
                "All" &&
              tsdGetMenuGroup(
                row
              ) !==
                statCategory
            ) {
              return false;
            }

            if (!search) {
              return true;
            }

            const meta =
              tsdGetStatMeta(
                row
              );

            const haystack =
              [
                meta.title,
                meta.definition,
                row.category,
                tsdGetMenuGroup(
                  row
                ),
                tsdGetMenuScope(
                  row
                ),
                row.scope,
                row.mapName,
                row.playerName,
                row.comboKey,
                row.compName,
                row.side,
              ]
                .join(" ")
                .toLowerCase();

            return haystack.includes(
              search
            );
          }
        );
      },
      [
        availableRows,
        statMenuSearch,
        statCategory,
      ]
    );

  const groupedStatMenuRows =
    useMemo(
      () => {
        const groups = {};

        statMenuRows.forEach(
          (row) => {
            const group =
              tsdGetMenuGroup(
                row
              );

            if (
              !groups[
                group
              ]
            ) {
              groups[
                group
              ] = [];
            }

            groups[
              group
            ].push(row);
          }
        );

        return groups;
      },
      [statMenuRows]
    );

  const visibleStatMenuGroups =
    useMemo(
      () => {
        const groupsToShow =
          statCategory ===
          "All"
            ? statCategories
            : [
                statCategory,
              ];

        return groupsToShow.filter(
          (group) =>
            (
              groupedStatMenuRows[
                group
              ] || []
            ).length > 0
        );
      },
      [
        statCategory,
        statCategories,
        groupedStatMenuRows,
      ]
    );

  useEffect(
    () => {
      if (!statMenuOpen) {
        setStatMenuFlyoutStyle(
          (current) =>
            Object.keys(
              current
            ).length
              ? {}
              : current
        );

        return undefined;
      }

      const compactBreakpoint =
        64 * 16;
      const viewportGutter =
        16;
      const flyoutGap =
        14;

      let frameId = 0;

      const applyStatMenuFlyoutViewportStyle =
        () => {
          if (
            !statMenuShellRef.current ||
            !statMenuFlyoutRef.current
          ) {
            return;
          }

          if (
            window.innerWidth <=
            compactBreakpoint
          ) {
            setStatMenuFlyoutStyle(
              (current) =>
                Object.keys(
                  current
                ).length
                  ? {}
                  : current
            );

            return;
          }

          const shellRect =
            statMenuShellRef.current.getBoundingClientRect();
          const flyoutRect =
            statMenuFlyoutRef.current.getBoundingClientRect();

          const embeddedDesktopHost =
            statMenuShellRef.current.closest(
              ".desktop-team-stats-summary-host"
            );

          const hostRect =
            embeddedDesktopHost &&
            typeof embeddedDesktopHost.getBoundingClientRect ===
              "function"
              ? embeddedDesktopHost.getBoundingClientRect()
              : null;

          const viewportWidth =
            Math.min(
              window.innerWidth,
              window.visualViewport
                ?.width ||
                window.innerWidth,
              document
                .documentElement
                ?.clientWidth ||
                window.innerWidth
            );

          const viewportHeight =
            Math.min(
              window.innerHeight,
              window.visualViewport
                ?.height ||
                window.innerHeight,
              document
                .documentElement
                ?.clientHeight ||
                window.innerHeight
            );

          const horizontalMin =
            hostRect
              ? Math.max(
                  viewportGutter,
                  hostRect.left +
                    viewportGutter
                )
              : viewportGutter;

          const horizontalMax =
            hostRect
              ? Math.min(
                  viewportWidth -
                    viewportGutter,
                  hostRect.right -
                    viewportGutter
                )
              : viewportWidth -
                viewportGutter;

          const availableWidth =
            Math.max(
              0,
              horizontalMax -
                horizontalMin
            );

          const availableHeight =
            Math.max(
              0,
              viewportHeight -
                viewportGutter *
                  2
            );

          const preferredWidth =
            Math.max(
              statMenuFlyoutRef.current.scrollWidth,
              flyoutRect.width
            );

          const preferredHeight =
            Math.max(
              statMenuFlyoutRef.current.scrollHeight,
              flyoutRect.height
            );

          const nextWidth =
            Math.min(
              preferredWidth,
              availableWidth
            );

          const nextHeight =
            Math.min(
              preferredHeight,
              availableHeight
            );

          let nextLeft =
            shellRect.right +
            flyoutGap;

          if (
            nextLeft +
              nextWidth >
            horizontalMax
          ) {
            nextLeft =
              horizontalMax -
              nextWidth;
          }

          nextLeft =
            Math.max(
              horizontalMin,
              nextLeft
            );

          let nextTop =
            shellRect.top;

          if (
            nextTop +
              nextHeight >
            viewportHeight -
              viewportGutter
          ) {
            nextTop =
              viewportHeight -
              viewportGutter -
              nextHeight;
          }

          nextTop =
            Math.max(
              viewportGutter,
              nextTop
            );

          const nextStyle =
            {
              position:
                "fixed",
              top: `${Math.round(
                nextTop
              )}px`,
              left: `${Math.round(
                nextLeft
              )}px`,
              width: `${Math.floor(
                nextWidth
              )}px`,
              maxWidth: `${Math.floor(
                nextWidth
              )}px`,
              maxHeight: `${Math.floor(
                availableHeight
              )}px`,
            };

          setStatMenuFlyoutStyle(
            (current) => {
              const keys =
                Object.keys(
                  nextStyle
                );

              if (
                keys.length ===
                  Object.keys(
                    current
                  ).length &&
                keys.every(
                  (key) =>
                    current[
                      key
                    ] ===
                    nextStyle[
                      key
                    ]
                )
              ) {
                return current;
              }

              return nextStyle;
            }
          );
        };

      const scheduleViewportStyleRefresh =
        () => {
          if (frameId) {
            window.cancelAnimationFrame(
              frameId
            );
          }

          frameId =
            window.requestAnimationFrame(
              applyStatMenuFlyoutViewportStyle
            );
        };

      const resizeObserver =
        typeof ResizeObserver ===
        "function"
          ? new ResizeObserver(
              () =>
                scheduleViewportStyleRefresh()
            )
          : null;

      if(statMenuShellRef.current)resizeObserver?.observe(statMenuShellRef.current);
      if(statMenuFlyoutRef.current)resizeObserver?.observe(statMenuFlyoutRef.current);

      scheduleViewportStyleRefresh();

      window.addEventListener(
        "resize",
        scheduleViewportStyleRefresh
      );

      window.addEventListener(
        "scroll",
        scheduleViewportStyleRefresh,
        true
      );

      return () => {
        if (frameId) {
          window.cancelAnimationFrame(
            frameId
          );
        }

        resizeObserver?.disconnect();

        window.removeEventListener(
          "resize",
          scheduleViewportStyleRefresh
        );

        window.removeEventListener(
          "scroll",
          scheduleViewportStyleRefresh,
          true
        );
      };
    },
    [
      statMenuOpen,
      statMenuPinned,
      statMenuSearch,
      statCategory,
      statMenuRows.length,
      visibleStatMenuGroups.length,
    ]
  );

  const orderedSavedViewNames =
    useMemo(
      () =>
        Object.keys(
          savedViews
        ).sort(),
      [savedViews]
    );

  const activeFilterChips=['Independent conditions · shown beneath each element'];

  const dashboardScaleStyle =
    useMemo(
      () => {
        const safeScale =
          tsdClampDashboardScale(
            dashboardScale
          );

        const multiplier =
          safeScale / 100;

        return {
          "--TSD-stat-grid-min":
            `${tsdInterpolateScaleAnchors(
              multiplier,
              [
                {
                  scale:
                    0.25,
                  value:
                    18.25,
                },
                {
                  scale:
                    0.5,
                  value:
                    20.5,
                },
                {
                  scale:
                    1,
                  value:
                    22.75,
                },
                {
                  scale:
                    1.5,
                  value:
                    25,
                },
                {
                  scale:
                    3,
                  value:
                    29.5,
                },
              ]
            ).toFixed(2)}rem`,

          "--TSD-stat-card-min-height":
            `${tsdInterpolateScaleAnchors(
              multiplier,
              [
                {
                  scale:
                    0.25,
                  value:
                    20.38,
                },
                {
                  scale:
                    0.5,
                  value:
                    22.5,
                },
                {
                  scale:
                    1,
                  value:
                    24.63,
                },
                {
                  scale:
                    1.5,
                  value:
                    26.75,
                },
                {
                  scale:
                    3,
                  value:
                    31,
                },
              ]
            ).toFixed(2)}rem`,

          "--TSD-chart-min-height":
            `${tsdInterpolateScaleAnchors(
              multiplier,
              [
                {
                  scale:
                    0.25,
                  value:
                    8.65,
                },
                {
                  scale:
                    0.5,
                  value:
                    10,
                },
                {
                  scale:
                    1,
                  value:
                    11.35,
                },
                {
                  scale:
                    1.5,
                  value:
                    12.7,
                },
                {
                  scale:
                    3,
                  value:
                    15.4,
                },
              ]
            ).toFixed(2)}rem`,
        };
      },
      [dashboardScale]
    );

  const dashboardScaleValue =
    Math.round(
      tsdClampDashboardScale(
        dashboardScale
      )
    );

  const dashboardScaleReadout =
    useMemo(
      () => {
        const multiplier =
          (
            dashboardScaleValue /
            100
          )
            .toFixed(2)
            .replace(
              /\.00$/,
              ".0"
            )
            .replace(
              /(\.\d)0$/,
              "$1"
            );

        return `${multiplier}x`;
      },
      [dashboardScaleValue]
    );

  const settingsRow=settingsStatKey?(dashboardRowByKey.get(settingsStatKey)||rowBySelectionKey.get(settingsStatKey)):null;

  const selectedReportElement =
    reportElements.find(
      (element) =>
        element.id ===
        selectedReportElementId
    ) || null;

  const activeReportPage =
    reportPages.find(
      (page) =>
        page.id ===
        activeReportPageId
    ) ||
    reportPages[0];

  const reportStatUsageMap =
    useMemo(
      () => {
        const usage =
          new Map();

        reportElements.forEach(
          (element) => {
            if (
              element.type !==
                "stat" ||
              !element.statKey
            ) {
              return;
            }

            const current =
              usage.get(
                element.statKey
              ) || {
                count: 0,
                pages:
                  new Set(),
              };

            current.count +=
              1;

            current.pages.add(
              element.pageId
            );

            usage.set(
              element.statKey,
              current
            );
          }
        );

        return usage;
      },
      [reportElements]
    );

  reportStateRef.current = {
    reportPages,
    reportElements,
    activeReportPageId,
    selectedReportElementId,
  };

  const buildCurrentViewSnapshot =
    () =>
      JSON.parse(
        JSON.stringify({
          filters:
            appliedFilters,
          selectedStatKeys,eventFilters,studioPointFilters,
          graphSettingsByStat,
          dashboardScale,
          dashboardScaleVersion:
            TSD_DASHBOARD_SCALE_VERSION,
          updatedAt:
            Date.now(),
        })
      );

  const applySavedViewState =
    (
      view,
      nextPresetName = ""
    ) => {
      if (!view) {
        return;
      }

      setEventFilters(view.eventFilters || {});
      setDraftEventFilters(view.eventFilters || {});
      setStudioPointFilters(view.studioPointFilters || {});
      const filters =
        view.filters ||
        appliedFilters;

      setAppliedFilters(
        filters
      );

      setDraftMaps(
        filters.maps ||
          []
      );

      setDraftComps(
        filters.comps ||
          []
      );

      setDraftSide(
        filters.side ||
          "both"
      );

      setDraftGameTypes(
        filters.gameTypes ||
          []
      );

      setDraftDateFrom(
        filters.dateFrom ||
          ""
      );

      setDraftDateTo(
        filters.dateTo ||
          ""
      );

      setDraftMinSample(
        Number(
          filters.minSample ||
            0
        )
      );

      setDraftPlayers(
        filters.players ||
          []
      );

      setSelectedStatKeys(
        Array.isArray(
          view.selectedStatKeys
        )
          ? view.selectedStatKeys
          : []
      );

      setGraphSettingsByStat(
        view.graphSettingsByStat ||
          {}
      );

      if (
        Number.isFinite(
          Number(
            view.dashboardScale
          )
        )
      ) {
        setDashboardScale(
          tsdNormalizeDashboardScale(
            view.dashboardScale,
            view?.dashboardScaleVersion
          )
        );
      } else if (
        typeof view.dashboardScale ===
        "string" &&
        Object.prototype.hasOwnProperty.call(
          TSD_DASHBOARD_SCALE_OPTIONS,
          view.dashboardScale
        )
      ) {
        setDashboardScale(
          tsdNormalizeDashboardScale(
            view.dashboardScale,
            view?.dashboardScaleVersion
          )
        );
      }

      setSelectionInitialized(
        true
      );

      setActivePresetName(
        nextPresetName
      );
    };

  const persistSavedViews =
    (nextViews) => {
      setSavedViews(
        nextViews
      );

      localStorage.setItem(
        savedViewsStorageKey,
        JSON.stringify(
          nextViews
        )
      );
    };

  const pushReportHistory =
    () => {
      if (
        !reportMode ||
        !reportStateRef.current
      ) {
        return;
      }

      reportHistoryRef.current.past.push(
        tsdCloneReportSnapshot(
          reportStateRef.current
        )
      );

      if (
        reportHistoryRef.current
          .past.length >
        60
      ) {
        reportHistoryRef.current.past.shift();
      }

      reportHistoryRef.current.future =
        [];
    };

  const restoreReportSnapshot =
    (snapshot) => {
      if (!snapshot) {
        return;
      }

      setReportPages(
        snapshot.reportPages ||
          []
      );

      setReportElements(
        snapshot.reportElements ||
          []
      );

      setActiveReportPageId(
        snapshot.activeReportPageId ||
          "page-1"
      );

      setSelectedReportElementId(
        snapshot.selectedReportElementId ||
          null
      );
    };

  const handleUndoReport =
    () => {
      const previous =
        reportHistoryRef.current.past.pop();

      if (!previous) {
        return;
      }

      reportHistoryRef.current.future.push(
        tsdCloneReportSnapshot(
          reportStateRef.current
        )
      );

      restoreReportSnapshot(
        previous
      );
    };

  const handleRedoReport =
    () => {
      const next =
        reportHistoryRef.current.future.pop();

      if (!next) {
        return;
      }

      reportHistoryRef.current.past.push(
        tsdCloneReportSnapshot(
          reportStateRef.current
        )
      );

      restoreReportSnapshot(
        next
      );
    };

  const handleToggleStat=(row)=>{
    const metricKey=tsdSelectionKey(row),instanceKey=newMetricInstance(metricKey);
    const filters={...EMPTY_CARD_FILTERS,eventFilters:{}};
    setGraphSettingsByStat(current=>({...current,[instanceKey]:{...tsdGraphDefaults(row),dataFilters:filters}}));
    setSelectedStatKeys(current=>[...current,instanceKey]);
    setSettingsInitialTab('data');
    setSettingsStatKey(instanceKey);
    setStatMenuPinned(false);setStatMenuHoverOpen(false);
  };

  const handleSelectPopularStats=()=>{
    const keys=tsdDefaultSelectedKeys(availableRows).map(newMetricInstance);
    setGraphSettingsByStat(current=>({...current,...Object.fromEntries(keys.map(key=>[key,{...tsdGraphDefaults(availableRows.find(row=>tsdSelectionKey(row)===metricKeyFor(key))),dataFilters:{...EMPTY_CARD_FILTERS,eventFilters:{}}}]))}));
    setSelectedStatKeys(keys);
  };

  const handleGraphSettingsChange=(key,nextSettings)=>{
    setGraphSettingsByStat(current=>({...current,[key]:{...nextSettings,dataFilters:nextSettings.dataFilters || cardFilters(current[key],appliedFilters,eventFilters)}}));
  };
  const settingsDuplicate=settingsRow&&selectedRows.some(row=>row._dashboardKey!==settingsStatKey&&filterSignature(row._dashboardKey,row._cardFilters)===filterSignature(settingsStatKey,settingsRow._cardFilters));

  const renderInsightTrend=(series,label,max,settings)=> <figure><TSDMiniChart row={{valueType:'number',sample:series.length,value:series.at(-1)?.value}} series={series.map(point=>({...point,date:point.date || new Date(point.label+'-01'),dateLabel:point.label,shortDate:point.label,n:point.n || 1}))} settings={{...TSD_GRAPH_DEFAULTS,yMin:0,yMax:max,...settings}}/><figcaption>{label}</figcaption></figure>;
  const settingsDataEditor=settingsRow?<StudioCardFilters focusPoints={settingsRow.studioView==='focus'?[...new Map(studioData.reviews.map(point=>[studioPointKey(point),point.text])).entries()]:[]} supportsSide={settingsRow._supportsSide} value={settingsRow._cardFilters} onChange={dataFilters=>handleGraphSettingsChange(settingsStatKey,{...graphSettingsByStat[settingsStatKey],dataFilters})} matches={unfilteredMatches} maps={allMaps} comps={allComps} players={allPlayers} library={metricKeyFor(settingsStatKey).startsWith('improvement:')||settingsRow.studioView==='improvements'} assignees={settingsRow.category==='Review'?[...new Map([...studioData.notes,...studioData.reviews].flatMap(point=>{const c=noteClassification(point);return c.assigneeUids.map((uid,i)=>[uid,c.assignees[i] || uid]);})).entries()]:[]}/>:null;

  const handleSaveView =
    (
      nextName = saveViewName,
      replaceName = null
    ) => {
      const name =
        String(
          nextName || ""
        ).trim();

      if (!name) {
        return;
      }

      const existingNames =
        Object.keys(
          savedViews
        );

      const needsNewSlot =
        !savedViews[
          name
        ] &&
        (
          !replaceName ||
          replaceName !==
            name
        );

      if (
        needsNewSlot &&
        existingNames.length >=
          TSD_MAX_ANALYTICS_PRESETS
      ) {
        setCustomAlert({
          message:
            `You can save up to ${TSD_MAX_ANALYTICS_PRESETS} analytics presets.`,

          type:
            "error",

          position:
            "top",
        });

        return;
      }

      const nextViews = {
        ...savedViews,
      };

      if (
        replaceName &&
        replaceName !==
          name
      ) {
        delete nextViews[
          replaceName
        ];
      }

      nextViews[
        name
      ] =
        buildCurrentViewSnapshot();

      persistSavedViews(
        nextViews
      );

      setSaveViewName(
        ""
      );

      setSaveViewOpen(
        false
      );

      setPresetBeingEdited(
        null
      );

      setPresetEditValue(
        ""
      );

      setActivePresetName(
        name
      );

      setCustomAlert({
        message:
          replaceName &&
          replaceName !==
            name
            ? `Renamed preset to "${name}".`
            : `Saved preset "${name}".`,

        type:
          "success",

        position:
          "top",
      });
    };

  const handleLoadSavedView =
    (name) => {
      if (
        !name ||
        !savedViews[
          name
        ]
      ) {
        return;
      }

      const view =
        savedViews[
          name
        ];

      applySavedViewState(
        view,
        name
      );
    };

  const handleDeleteSavedView =
    (name) => {
      if (
        !name ||
        !savedViews[
          name
        ]
      ) {
        return;
      }

      const next = {
        ...savedViews,
      };

      delete next[
        name
      ];

      persistSavedViews(
        next
      );

      if (
        activePresetName ===
        name
      ) {
        setActivePresetName(
          ""
        );
      }

      if (
        presetBeingEdited ===
        name
      ) {
        setPresetBeingEdited(
          null
        );

        setPresetEditValue(
          ""
        );
      }
    };

  const exportRows =
    useMemo(
      () =>
        selectedRows.filter(
          (row) =>
            Number(
              row.sample ||
                0
            ) >=
            Number(
              row._cardFilters?.minSample ||
                0
            )
        ),
      [
        selectedRows,
      ]
    );

  const handleExportCsv =
    () => {
      const header = [
        "Statistic",
        "Category",
        "Value",
        "N",
        "Made",
        "Total",
        "SE",
        "95% CI",
        "P-value",
      ];

      const rows =
        exportRows.map(
          (row) => [
            tsdGetStatMeta(
              row
            ).title,

            row.category,

            tsdFormatValue(
              row
            ),

            row.sample ??
              "",

            row.made ??
              "",

            row.total ??
              "",

            row.se !=
            null
              ? row.se.toFixed(
                  3
                )
              : "",

            row.ciLow !=
              null &&
            row.ciHigh !=
              null
              ? `${row.ciLow.toFixed(2)} - ${row.ciHigh.toFixed(2)}`
              : "",

            row.pValue !=
            null
              ? row.pValue.toFixed(
                  5
                )
              : "",
          ]
        );

      const csv =
        [
          header,
          ...rows,
        ]
          .map(
            (line) =>
              line
                .map(
                  (value) =>
                    `"${String(value ?? "").replace(/"/g, '""')}"`
                )
                .join(",")
          )
          .join("\n");

      tsdDownloadBlob(
        new Blob(
          [csv],
          {
            type:
              "text/csv;charset=utf-8",
          }
        ),

        `${teamName}-team-statistics.csv`
      );
    };

  const handleExportExcel =
    () => {
      const tableRows =
        exportRows
          .map(
            (row) =>
              `<tr><td>${tsdGetStatMeta(row).title}</td><td>${row.category}</td><td>${tsdFormatValue(row)}</td><td>${row.sample ?? ""}</td><td>${row.pValue != null ? row.pValue.toFixed(5) : ""}</td></tr>`
          )
          .join("");

      const html =
        `<!doctype html><html><head><meta charset="utf-8"></head><body><table><thead><tr><th>Statistic</th><th>Category</th><th>Value</th><th>N</th><th>P-value</th></tr></thead><tbody>${tableRows}</tbody></table></body></html>`;

      tsdDownloadBlob(
        new Blob(
          [html],
          {
            type:
              "application/vnd.ms-excel",
          }
        ),

        `${teamName}-team-statistics.xls`
      );
    };

  const handleExportPng =
    () => {
      const rows =
        exportRows.slice(
          0,
          20
        );

      const width =
        1600;

      const cardHeight =
        150;

      const padding =
        70;

      const height =
        Math.max(
          650,

          padding * 2 +
            140 +
            rows.length *
              cardHeight
        );

      const canvas =
        document.createElement(
          "canvas"
        );

      canvas.width =
        width;

      canvas.height =
        height;

      const ctx =
        canvas.getContext(
          "2d"
        );

      if (!ctx) {
        return;
      }

      const css =
        getComputedStyle(
          document.querySelector(
            ".--TSD-page"
          ) ||
            document.body
        );

      const bg =
        css
          .getPropertyValue(
            "--TSD-export-bg"
          )
          .trim() ||
        "#FFFFFF";

      const text =
        css
          .getPropertyValue(
            "--TSD-export-text"
          )
          .trim() ||
        "#111827";

      const muted =
        css
          .getPropertyValue(
            "--TSD-export-muted"
          )
          .trim() ||
        "#64748B";

      const accent =
        css
          .getPropertyValue(
            "--TSD-export-accent"
          )
          .trim() ||
        "#6D5DFB";

      const border =
        css
          .getPropertyValue(
            "--TSD-export-border"
          )
          .trim() ||
        "#DCE3EC";

      ctx.fillStyle =
        bg;

      ctx.fillRect(
        0,
        0,
        width,
        height
      );

      ctx.fillStyle =
        text;

      ctx.font =
        "700 48px Arial";

      ctx.fillText(
        `${teamName} Team Statistics`,
        padding,
        90
      );

      ctx.fillStyle =
        muted;

      ctx.font =
        "24px Arial";

      ctx.fillText(
        `${filteredMatches.length} available matches • ${activeFilterChips.join(" • ")}`,
        padding,
        132
      );

      rows.forEach(
        (
          row,
          index
        ) => {
          const y =
            180 +
            index *
              cardHeight;

          ctx.strokeStyle =
            border;

          ctx.strokeRect(
            padding,
            y,
            width -
              padding *
                2,
            cardHeight -
              20
          );

          ctx.fillStyle =
            text;

          ctx.font =
            "700 26px Arial";

          ctx.fillText(
            tsdGetStatMeta(
              row
            ).title,
            padding +
              24,
            y + 42
          );

          ctx.fillStyle =
            accent;

          ctx.font =
            "700 44px Arial";

          ctx.fillText(
            tsdFormatValue(
              row
            ),
            padding +
              24,
            y + 95
          );

          ctx.fillStyle =
            muted;

          ctx.font =
            "22px Arial";

          ctx.fillText(
            `N=${row.sample ?? 0}`,
            width -
              padding -
              210,
            y + 78
          );
        }
      );

      canvas.toBlob(
        (blob) => {
          if (blob) {
            tsdDownloadBlob(
              blob,
              `${teamName}-team-statistics.png`
            );
          }
        },
        "image/png"
      );
    };

  const handleRenameSavedView =
    (name) => {
      handleSaveView(
        presetEditValue,
        name
      );
    };

  const handleUpdateSavedView =
    (name) => {
      if (
        !name ||
        !savedViews[
          name
        ]
      ) {
        return;
      }

      const nextViews = {
        ...savedViews,

        [name]:
          buildCurrentViewSnapshot(),
      };

      persistSavedViews(
        nextViews
      );

      setActivePresetName(
        name
      );

      setCustomAlert({
        message:
          `Updated preset "${name}".`,

        type:
          "success",

        position:
          "top",
      });
    };

  const handlePrintReport =
    () => {
      if (
        typeof document !==
        "undefined"
      ) {
        document.body.classList.add(
          "--TSD-is-printing-report"
        );
      }

      window.print();

      window.setTimeout(
        () => {
          if (
            typeof document !==
            "undefined"
          ) {
            document.body.classList.remove(
              "--TSD-is-printing-report"
            );
          }
        },
        300
      );
    };

  const handleEnterReportBuilder =
    () => {
      setReportMode(
        true
      );

      setSelectedReportElementId(
        null
      );

      if (
        reportPages.length ===
        0
      ) {
        const first = {
          id:
            "page-1",

          name:
            "Page 1",

          ...TSD_REPORT_DEFAULT_PAGE,
        };

        setReportPages([
          first,
        ]);

        setActiveReportPageId(
          first.id
        );
      }
    };

  const addReportPage =
    () => {
      const id =
        `page-${Date.now()}`;

      const page = {
        id,

        name:
          `Page ${reportPages.length + 1}`,

        ...TSD_REPORT_DEFAULT_PAGE,

        size:
          activeReportPage?.size ||
          "a4",

        orientation:
          activeReportPage?.orientation ||
          "portrait",
      };

      pushReportHistory();

      setReportPages(
        (current) => [
          ...current,
          page,
        ]
      );

      setActiveReportPageId(
        id
      );

      setSelectedReportElementId(
        null
      );
    };

  const updateReportPage =
    (
      patch,
      options = {}
    ) => {
      if (
        !options.skipHistory
      ) {
        pushReportHistory();
      }

      setReportPages(
        (current) =>
          current.map(
            (page) =>
              page.id ===
              activeReportPageId
                ? {
                    ...page,
                    ...patch,
                  }
                : page
          )
      );
    };

  const deleteReportPage =
    () => {
      if (
        reportPages.length <=
        1
      ) {
        setCustomAlert({
          message:
            "A report must contain at least one page.",

          type:
            "error",

          position:
            "top",
        });

        return;
      }

      pushReportHistory();

      const index =
        reportPages.findIndex(
          (page) =>
            page.id ===
            activeReportPageId
        );

      const nextPages =
        reportPages.filter(
          (page) =>
            page.id !==
            activeReportPageId
        );

      setReportPages(
        nextPages
      );

      setReportElements(
        (current) =>
          current.filter(
            (element) =>
              element.pageId !==
              activeReportPageId
          )
      );

      const nextActive =
        nextPages[
          Math.max(
            0,
            index - 1
          )
        ] ||
        nextPages[0];

      setActiveReportPageId(
        nextActive.id
      );

      setSelectedReportElementId(
        null
      );
    };

  const addReportStat =
    (
      row,
      position = null
    ) => {
      const id =
        `element-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      pushReportHistory();

      setReportElements(
        (current) => [
          ...current,

          {
            id,

            pageId:
              activeReportPageId,

            type:
              "stat",

            statKey:
              tsdSelectionKey(
                row
              ),

            x:
              position?.x ??
              6,

            y:
              position?.y ??
              8,

            w:
              position?.w ??
              42,

            h:
              position?.h ??
              26,
          },
        ]
      );

      setSelectedReportElementId(
        id
      );
    };

  const addReportText =
    (
      type = "text"
    ) => {
      const id =
        `element-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      pushReportHistory();

      setReportElements(
        (current) => [
          ...current,

          {
            id,

            pageId:
              activeReportPageId,

            type,

            text:
              type ===
              "title"
                ? `${teamName} Performance Summary`
                : "Add your notes here…",

            x:
              7,

            y:
              type ===
              "title"
                ? 5
                : 12,

            w:
              70,

            h:
              type ===
              "title"
                ? 8
                : 12,

            fontSize:
              type ===
              "title"
                ? 2
                : 1,
          },
        ]
      );

      setSelectedReportElementId(
        id
      );
    };

  const addReportDivider =
    () => {
      const id =
        `element-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      pushReportHistory();

      setReportElements(
        (current) => [
          ...current,

          {
            id,

            pageId:
              activeReportPageId,

            type:
              "divider",

            x:
              8,

            y:
              18,

            w:
              84,

            h:
              2,
          },
        ]
      );

      setSelectedReportElementId(
        id
      );
    };

  const updateReportElement =
    (
      id,
      patch,
      options = {}
    ) => {
      if (
        !options.skipHistory
      ) {
        pushReportHistory();
      }

      setReportElements(
        (current) =>
          current.map(
            (element) =>
              element.id ===
              id
                ? {
                    ...element,
                    ...patch,
                  }
                : element
          )
      );
    };

  const deleteReportElement =
    (id) => {
      pushReportHistory();

      setReportElements(
        (current) =>
          current.filter(
            (element) =>
              element.id !==
              id
          )
      );

      setSelectedReportElementId(
        (current) =>
          current === id
            ? null
            : current
      );
    };

  reportShortcutHandlersRef.current =
    {
      undo: handleUndoReport,
      redo: handleRedoReport,
      deleteElement:
        deleteReportElement,
    };

  reportElementUpdaterRef.current =
    updateReportElement;

  const duplicateReportElement =
    (id) => {
      const source =
        reportElements.find(
          (element) =>
            element.id ===
            id
        );

      if (!source) {
        return;
      }

      pushReportHistory();

      const next = {
        ...source,

        id:
          `element-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,

        x:
          tsdClamp(
            Number(
              source.x ||
                0
            ) + 3,
            0,
            95
          ),

        y:
          tsdClamp(
            Number(
              source.y ||
                0
            ) + 3,
            0,
            95
          ),
      };

      setReportElements(
        (current) => [
          ...current,
          next,
        ]
      );

      setSelectedReportElementId(
        next.id
      );
    };

  const applyReportTemplate =
    (template) => {
      pushReportHistory();

      const currentPageElements =
        reportElements.filter(
          (element) =>
            element.pageId !==
            activeReportPageId
        );

      const additions = [];

      const makeId =
        () =>
          `element-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      if (
        template ===
        "blank"
      ) {
        setReportElements(
          currentPageElements
        );

        setSelectedReportElementId(
          null
        );

        return;
      }

      additions.push({
        id:
          makeId(),

        pageId:
          activeReportPageId,

        type:
          "title",

        text:
          `${teamName} Performance Summary`,

        x:
          6,

        y:
          4,

        w:
          88,

        h:
          8,

        fontSize:
          2,
      });

      const rowsForTemplate =
        selectedRows.slice(
          0,
          template ===
          "executive"
            ? 4
            : 8
        );

      rowsForTemplate.forEach(
        (
          row,
          index
        ) => {
          let x;
          let y;
          let w;
          let h;

          if (
            template ===
            "two-column"
          ) {
            x =
              index % 2 ===
              0
                ? 6
                : 52;

            y =
              16 +
              Math.floor(
                index / 2
              ) *
                20;

            w =
              42;

            h =
              17;
          } else if (
            template ===
            "grid"
          ) {
            x =
              5 +
              (
                index %
                2
              ) *
                47;

            y =
              15 +
              Math.floor(
                index / 2
              ) *
                20;

            w =
              43;

            h =
              17;
          } else {
            x =
              index % 2 ===
              0
                ? 6
                : 52;

            y =
              18 +
              Math.floor(
                index / 2
              ) *
                30;

            w =
              42;

            h =
              25;
          }

          additions.push({
            id:
              makeId(),

            pageId:
              activeReportPageId,

            type:
              "stat",

            statKey:
              tsdSelectionKey(
                row
              ),

            x,
            y,
            w,
            h,
          });
        }
      );

      setReportElements([
        ...currentPageElements,
        ...additions,
      ]);

      setSelectedReportElementId(
        null
      );
    };

  const handleReportPointerDown =
    (
      event,
      element,
      mode
    ) => {
      if (
        !reportCanvasRef.current
      ) {
        return;
      }

      pushReportHistory();

      event.preventDefault();
      event.stopPropagation();

      const rect =
        reportCanvasRef.current.getBoundingClientRect();

      reportDragRef.current = {
        id:
          element.id,

        mode,

        startX:
          event.clientX,

        startY:
          event.clientY,

        rect,

        startElement: {
          ...element,
        },
      };

      setSelectedReportElementId(
        element.id
      );
    };

  useEffect(
    () => {
      if (!reportMode) {
        return undefined;
      }

      const move =
        (event) => {
          const drag =
            reportDragRef.current;

          if (!drag) {
            return;
          }

          const dxPercent =
            (
              event.clientX -
              drag.startX
            ) /
            drag.rect.width *
            100;

          const dyPercent =
            (
              event.clientY -
              drag.startY
            ) /
            drag.rect.height *
            100;

          const grid =
            activeReportPage?.snapToGrid
              ? 1
              : 0.25;

          const snap =
            (value) =>
              Math.round(
                value /
                  grid
              ) *
              grid;

          if (
            drag.mode ===
            "move"
          ) {
            reportElementUpdaterRef.current?.(
              drag.id,
              {
                x:
                  tsdClamp(
                    snap(
                      Number(
                        drag.startElement.x
                      ) +
                        dxPercent
                    ),
                    0,
                    100 -
                      Number(
                        drag.startElement.w
                      )
                  ),

                y:
                  tsdClamp(
                    snap(
                      Number(
                        drag.startElement.y
                      ) +
                        dyPercent
                    ),
                    0,
                    100 -
                      Number(
                        drag.startElement.h
                      )
                  ),
              },
              {
                skipHistory:
                  true,
              }
            );
          } else {
            reportElementUpdaterRef.current?.(
              drag.id,
              {
                w:
                  tsdClamp(
                    snap(
                      Number(
                        drag.startElement.w
                      ) +
                        dxPercent
                    ),
                    8,
                    100 -
                      Number(
                        drag.startElement.x
                      )
                  ),

                h:
                  tsdClamp(
                    snap(
                      Number(
                        drag.startElement.h
                      ) +
                        dyPercent
                    ),
                    5,
                    100 -
                      Number(
                        drag.startElement.y
                      )
                  ),
              },
              {
                skipHistory:
                  true,
              }
            );
          }
        };

      const up =
        () => {
          reportDragRef.current =
            null;
        };

      window.addEventListener(
        "pointermove",
        move
      );

      window.addEventListener(
        "pointerup",
        up
      );

      return () => {
        window.removeEventListener(
          "pointermove",
          move
        );

        window.removeEventListener(
          "pointerup",
          up
        );
      };
    },
    [
      reportMode,
      activeReportPage,
    ]
  );

  const renderReportElement =
    (
      element,
      pageId,
      interactive
    ) => {
      const selected =
        interactive &&
        selectedReportElementId ===
          element.id;

      const row =
        element.type ===
        "stat"
          ? dashboardRowByKey.get(element.statKey) || rowBySelectionKey.get(element.statKey)
          : null;

      const settings =
        element.type ===
        "stat"
          ? {
              ...tsdGraphDefaults(row),
              ...(graphSettingsByStat[
                element.statKey
              ] || {}),
            }
          : null;

      return (
        <div
          key={
            element.id
          }
          className={`--TSD-report-element --TSD-report-element--${element.type} ${
            selected
              ? "--TSD-is-selected"
              : ""
          }`}
          style={{
            left:
              `${element.x}%`,

            top:
              `${element.y}%`,

            width:
              `${element.w}%`,

            height:
              `${element.h}%`,

            fontSize:
              element.fontSize
                ? `${element.fontSize}rem`
                : undefined,
          }}
          onPointerDown={
            interactive
              ? (
                  event
                ) =>
                  handleReportPointerDown(
                    event,
                    element,
                    "move"
                  )
              : undefined
          }
          onClick={
            interactive
              ? (
                  event
                ) => {
                  event.stopPropagation();

                  setSelectedReportElementId(
                    element.id
                  );
                }
              : undefined
          }
        >
          {element.type ===
            "stat" &&
          row ? (
            <TSDReportStatElement
              row={row}
              matches={row._cardMatches || filteredMatches}
              settings={settings}
              insight={row.studioView?<StudioInsights expanded hideControls data={row._insightData} view={row.studioView} selectedPoint={row._cardFilters.focusPoint} renderTrend={(series,label,max)=>renderInsightTrend(series,label,max,settings)} teamId={teamId} events={setupEvents} matches={row._cardMatches} allMatches={unfilteredMatches} filters={EMPTY_CARD_FILTERS} eventFilters={EMPTY_CARD_FILTERS.eventFilters}/>:null}
            />
          ) : element.type ===
            "divider" ? (
            <div className="--TSD-report-divider-line" />
          ) : (
            <div className="--TSD-report-text-content">
              {
                element.text
              }
            </div>
          )}

          {interactive &&
          selected ? (
            <button
              className="--TSD-report-resize-handle"
              type="button"
              aria-label="Resize report element"
              onPointerDown={(
                event
              ) =>
                handleReportPointerDown(
                  event,
                  element,
                  "resize"
                )
              }
            />
          ) : null}
        </div>
      );
    };

  const renderReportPage =
    (
      page,
      interactive
    ) => {
      const dimensions =
        tsdGetReportPageDimensions(
          page
        );

      const pageElements =
        reportElements.filter(
          (element) =>
            element.pageId ===
            page.id
        );

      const pageNumber =
        reportPages.findIndex(
          (item) =>
            item.id ===
            page.id
        ) + 1;

      return (
        <div
          key={
            page.id
          }
          className={`--TSD-report-page-stage ${
            page.id ===
            activeReportPageId
              ? "--TSD-is-active"
              : "--TSD-is-inactive"
          }`}
        >
          <div
            ref={
              interactive &&
              page.id ===
                activeReportPageId
                ? reportCanvasRef
                : null
            }
            className={`--TSD-report-page ${
              page.showGrid
                ? "--TSD-report-page--grid"
                : ""
            }`}
            style={{
              aspectRatio:
                `${dimensions.width} / ${dimensions.height}`,
            }}
            onClick={
              interactive
                ? () =>
                    setSelectedReportElementId(
                      null
                    )
                : undefined
            }
          >
            <div
              className="--TSD-report-safe-area"
              style={{
                inset:
                  `${Number(page.margin || 0)}%`,
              }}
            />

            {pageElements.map(
              (element) =>
                renderReportElement(
                  element,
                  page.id,
                  interactive &&
                    page.id ===
                      activeReportPageId
                )
            )}

            {interactive ? (
              <span className="--TSD-report-page-number">
                {
                  pageNumber
                }
              </span>
            ) : (
              <div className="--TSD-report-print-footer">
                <div className="--TSD-report-print-footer-brand">
                  <img
                    className="--TSD-report-print-footer-logo"
                    src="/assets/valovate_logo_full.svg"
                    alt="Valovate"
                  />

                  <span className="--TSD-report-print-footer-url">
                    {
                      TSD_REPORT_PRINT_BRAND_URL
                    }
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };

  const libraryRef=useRef(null);


  if (!teamId) {
    return (
      <LoginTeamGate />
    );
  }

  if (reportMode) {
    return (
      <div className="--TSD-page --TSD-report-builder">
        <div hidden><StudioInsights hideControls onRows={setStudioRows} onData={setStudioData} key={teamId} teamId={teamId} events={setupEvents} matches={filteredMatches} allMatches={unfilteredMatches} filters={EMPTY_CARD_FILTERS} eventFilters={EMPTY_CARD_FILTERS.eventFilters}/></div>
        <header className="--TSD-report-topbar">
          <div className="--TSD-report-topbar-title">
            <button
              className="--TSD-button --TSD-button--ghost"
              type="button"
              onClick={() =>
                setReportMode(
                  false
                )
              }
            >
              ← Back to
              Dashboard
            </button>

            <div>
              <span className="--TSD-eyebrow">
                Create
                Summary
                Document
              </span>

              <h1>
                {teamName}{" "}
                Report
                Builder
              </h1>
            </div>
          </div>

          <div className="--TSD-topbar-actions">
            <button
              className="--TSD-button --TSD-button--secondary"
              type="button"
              onClick={
                handlePrintReport
              }
            >
              Print / Save
              PDF
            </button>
          </div>
        </header>

        <div className="--TSD-report-workspace">
          <aside className="--TSD-report-left-panel">
            <section className="--TSD-report-panel-section">
              <div className="--TSD-section-heading --TSD-section-heading--compact">
                <div>
                  <span className="--TSD-section-kicker">
                    Pages
                  </span>

                  <h3>
                    Document
                  </h3>
                </div>

                <button
                  className="--TSD-icon-button"
                  type="button"
                  onClick={
                    addReportPage
                  }
                  title="Add page"
                >
                  ＋
                </button>
              </div>

              <div className="--TSD-page-list">
                {reportPages.map(
                  (
                    page,
                    index
                  ) => (
                    <button
                      key={
                        page.id
                      }
                      className={`--TSD-page-thumbnail ${
                        page.id ===
                        activeReportPageId
                          ? "--TSD-is-active"
                          : ""
                      }`}
                      type="button"
                      onClick={() => {
                        setActiveReportPageId(
                          page.id
                        );

                        setSelectedReportElementId(
                          null
                        );
                      }}
                    >
                      <span className="--TSD-page-thumbnail-preview">
                        {index +
                          1}
                      </span>

                      <span>
                        {
                          page.name
                        }
                      </span>

                      <small>
                        {
                          reportElements.filter(
                            (
                              element
                            ) =>
                              element.pageId ===
                              page.id
                          ).length
                        }{" "}
                        elements
                      </small>
                    </button>
                  )
                )}
              </div>
            </section>

            <section className="--TSD-report-panel-section">
              <div className="--TSD-section-heading --TSD-section-heading--compact">
                <div>
                  <span className="--TSD-section-kicker">
                    Templates
                  </span>

                  <h3>
                    Start a
                    layout
                  </h3>
                </div>
              </div>

              <div className="--TSD-template-grid">
                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={() =>
                    applyReportTemplate(
                      "blank"
                    )
                  }
                >
                  Blank
                </button>

                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={() =>
                    applyReportTemplate(
                      "executive"
                    )
                  }
                >
                  Executive
                </button>

                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={() =>
                    applyReportTemplate(
                      "two-column"
                    )
                  }
                >
                  Two Column
                </button>

                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={() =>
                    applyReportTemplate(
                      "grid"
                    )
                  }
                >
                  Grid
                </button>
              </div>
            </section>

            <section className="--TSD-report-panel-section --TSD-report-panel-section--grow">
              <div className="--TSD-section-heading --TSD-section-heading--compact">
                <div>
                  <span className="--TSD-section-kicker">
                    Selected
                    Statistics
                  </span>

                  <h3>
                    Add to
                    page
                  </h3>
                </div>
              </div>

              <div className="--TSD-report-stat-list">
                {selectedRows.length ===
                0 ? (
                  <p className="--TSD-empty-note">
                    Choose
                    statistics
                    on the
                    dashboard
                    first.
                  </p>
                ) : (
                  selectedRows.map(
                    (row) => {
                      const statKey =
                        tsdSelectionKey(
                          row
                        );

                      const usage =
                        reportStatUsageMap.get(
                          statKey
                        );

                      return (
                        <button
                          className={`--TSD-report-stat-add ${
                            usage
                              ? "--TSD-is-implemented"
                              : ""
                          }`}
                          type="button"
                          key={
                            statKey
                          }
                          onClick={() =>
                            addReportStat(
                              row
                            )
                          }
                        >
                          <span>
                            {
                              tsdGetStatMeta(
                                row
                              )
                                .title
                            }
                            {usage ? (
                              <small className="--TSD-report-stat-usage">
                                {usage.count}{" "}
                                added /{" "}
                                {
                                  usage.pages
                                    .size
                                }{" "}
                                pages
                              </small>
                            ) : null}
                          </span>

                          <strong>
                            {usage
                              ? "Added"
                              : tsdFormatValue(
                                  row
                                )}
                          </strong>
                        </button>
                      );
                    }
                  )
                )}
              </div>
            </section>

            <section className="--TSD-report-panel-section">
              <span className="--TSD-section-kicker">
                Other
                elements
              </span>

              <div className="--TSD-template-grid">
                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={() =>
                    addReportText(
                      "title"
                    )
                  }
                >
                  Title
                </button>

                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={() =>
                    addReportText(
                      "text"
                    )
                  }
                >
                  Text
                </button>

                <button
                  className="--TSD-template-button"
                  type="button"
                  onClick={
                    addReportDivider
                  }
                >
                  Divider
                </button>
              </div>
            </section>
          </aside>

          <main className="--TSD-report-canvas-area">
            <div className="--TSD-report-canvas-toolbar">
              <span>
                {
                  activeReportPage?.name
                }
              </span>

              <span>
                Drag elements
                to move • use
                the corner
                handle to
                resize
              </span>
            </div>

            <div className="--TSD-report-pages-screen">
              {reportPages.map(
                (page) =>
                  renderReportPage(
                    page,
                    true
                  )
              )}
            </div>
          </main>

          <aside className="--TSD-report-right-panel">
            <section className="--TSD-report-panel-section">
              <div className="--TSD-section-heading --TSD-section-heading--compact">
                <div>
                  <span className="--TSD-section-kicker">
                    Page setup
                  </span>

                  <h3>
                    {
                      activeReportPage?.name
                    }
                  </h3>
                </div>
              </div>

              <label className="--TSD-field">
                <span>
                  Page size
                </span>

                <select
                  value={
                    activeReportPage?.size ||
                    "a4"
                  }
                  onChange={(
                    event
                  ) =>
                    updateReportPage({
                      size:
                        event
                          .target
                          .value,
                    })
                  }
                >
                  {Object.entries(
                    TSD_PAGE_SIZES
                  ).map(
                    ([
                      key,
                      value,
                    ]) => (
                      <option
                        key={
                          key
                        }
                        value={
                          key
                        }
                      >
                        {
                          value.label
                        }
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="--TSD-field">
                <span>
                  Orientation
                </span>

                <select
                  value={
                    activeReportPage?.orientation ||
                    "portrait"
                  }
                  onChange={(
                    event
                  ) =>
                    updateReportPage({
                      orientation:
                        event
                          .target
                          .value,
                    })
                  }
                >
                  <option value="portrait">
                    Portrait
                  </option>

                  <option value="landscape">
                    Landscape
                  </option>
                </select>
              </label>

              <label className="--TSD-field">
                <span>
                  Page margin
                  (%)
                </span>

                <input
                  type="number"
                  min="0"
                  max="15"
                  value={
                    activeReportPage?.margin ??
                    4
                  }
                  onChange={(
                    event
                  ) =>
                    updateReportPage({
                      margin:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  }
                />
              </label>

              <label className="--TSD-checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(
                    activeReportPage?.showGrid
                  )}
                  onChange={(
                    event
                  ) =>
                    updateReportPage({
                      showGrid:
                        event
                          .target
                          .checked,
                    })
                  }
                />

                <span>
                  Show grid
                </span>
              </label>

              <label className="--TSD-checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(
                    activeReportPage?.snapToGrid
                  )}
                  onChange={(
                    event
                  ) =>
                    updateReportPage({
                      snapToGrid:
                        event
                          .target
                          .checked,
                    })
                  }
                />

                <span>
                  Snap to grid
                </span>
              </label>

              <button
                className="--TSD-button --TSD-button--danger-ghost"
                type="button"
                onClick={
                  deleteReportPage
                }
              >
                Delete page
              </button>
            </section>

            <section className="--TSD-report-panel-section --TSD-report-panel-section--grow">
              <div className="--TSD-section-heading --TSD-section-heading--compact">
                <div>
                  <span className="--TSD-section-kicker">
                    Element
                  </span>

                  <h3>
                    {selectedReportElement
                      ? "Selected element"
                      : "Nothing selected"}
                  </h3>
                </div>
              </div>

              {selectedReportElement ? (
                <div className="--TSD-inspector">
                  {selectedReportElement.type ===
                    "text" ||
                  selectedReportElement.type ===
                    "title" ? (
                    <label className="--TSD-field">
                      <span>
                        Text
                      </span>

                      <textarea
                        value={
                          selectedReportElement.text ||
                          ""
                        }
                        onChange={(
                          event
                        ) =>
                          updateReportElement(
                            selectedReportElement.id,
                            {
                              text:
                                event
                                  .target
                                  .value,
                            }
                          )
                        }
                      />
                    </label>
                  ) : null}

                  <div className="--TSD-form-grid --TSD-form-grid--two">
                    {[
                      [
                        "x",
                        "X position",
                      ],
                      [
                        "y",
                        "Y position",
                      ],
                      [
                        "w",
                        "Width",
                      ],
                      [
                        "h",
                        "Height",
                      ],
                    ].map(
                      ([
                        key,
                        label,
                      ]) => (
                        <label
                          className="--TSD-field"
                          key={
                            key
                          }
                        >
                          <span>
                            {label}{" "}
                            (%)
                          </span>

                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={Number(
                              selectedReportElement[
                                key
                              ] ||
                                0
                            ).toFixed(
                              1
                            )}
                            onChange={(
                              event
                            ) =>
                              updateReportElement(
                                selectedReportElement.id,
                                {
                                  [key]:
                                    Number(
                                      event
                                        .target
                                        .value
                                    ),
                                }
                              )
                            }
                          />
                        </label>
                      )
                    )}
                  </div>

                  {selectedReportElement.type ===
                    "text" ||
                  selectedReportElement.type ===
                    "title" ? (
                    <label className="--TSD-field">
                      <span>
                        Font size
                        (rem)
                      </span>

                      <input
                        type="number"
                        min="0.5"
                        max="4"
                        step="0.1"
                        value={
                          selectedReportElement.fontSize ||
                          1
                        }
                        onChange={(
                          event
                        ) =>
                          updateReportElement(
                            selectedReportElement.id,
                            {
                              fontSize:
                                Number(
                                  event
                                    .target
                                    .value
                                ),
                            }
                          )
                        }
                      />
                    </label>
                  ) : null}

                  {selectedReportElement.type ===
                  "stat" ? (
                    <button
                      className="--TSD-button --TSD-button--secondary"
                      type="button"
                      onClick={() =>
                        setSettingsStatKey(
                          selectedReportElement.statKey
                        )
                      }
                    >
                      Open graph
                      settings
                    </button>
                  ) : null}

                  <div className="--TSD-inline-actions">
                    <button
                      className="--TSD-button --TSD-button--ghost"
                      type="button"
                      onClick={() =>
                        duplicateReportElement(
                          selectedReportElement.id
                        )
                      }
                    >
                      Duplicate
                    </button>

                    <button
                      className="--TSD-button --TSD-button--danger-ghost"
                      type="button"
                      onClick={() =>
                        deleteReportElement(
                          selectedReportElement.id
                        )
                      }
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <p className="--TSD-empty-note">
                  Click an element
                  on the page to
                  edit its position,
                  size, content, or
                  graph settings.
                </p>
              )}
            </section>
          </aside>
        </div>

        <div
          className="--TSD-report-print-pages"
          aria-hidden="true"
        >
          {reportPages.map(
            (page) =>
              renderReportPage(
                page,
                false
              )
          )}
        </div>

        {settingsRow ? (
          <TSDGraphSettingsModal
            key={settingsStatKey} initialTab={settingsInitialTab} duplicate={settingsDuplicate} dataEditor={settingsDataEditor}
            row={
              settingsRow
            }
            settings={
              graphSettingsByStat[
                settingsStatKey
              ]
            }
            onChange={(
              next
            ) =>
              handleGraphSettingsChange(
                settingsStatKey,
                next
              )
            }
            onClose={() =>
              setSettingsStatKey(
                null
              )
            }
          />
        ) : null}

        {customAlert && (
          <CustomAlert
            message={
              customAlert.message
            }
            type={
              customAlert.type
            }
            position={
              customAlert.position
            }
            onClose={() =>
              setCustomAlert(
                null
              )
            }
          />
        )}
      </div>
    );
  }

  const statMenuLauncher = <StudioMetricLibrary rows={availableRows} getMeta={tsdGetStatMeta} getKey={tsdSelectionKey} selectedKeys={selectedStatKeys} onAdd={handleToggleStat} onStarter={handleSelectPopularStats} Info={TSDInfo}/>;

  const presetsLauncher =
    (
      <section className="--TSD-presets-card">
        <div
          ref={
            presetMenuShellRef
          }
          className="--TSD-stat-menu-shell --TSD-preset-menu-shell"
        >
          <button
            className="--TSD-stat-menu-trigger --TSD-preset-menu-trigger"
            type="button"
            onClick={()=>setPresetMenuHoverOpen(open=>!open)}
            aria-label="Saved views"
            title={activePresetName?`Saved view: ${activePresetName}`:"Load and manage saved views"}
            aria-haspopup="dialog"
            aria-expanded={
              presetMenuOpen
            }
          >
            <Bookmark size={16} aria-hidden="true"/><span className="StatsStudio__view-name">{activePresetName||'Saved views'}</span>{orderedSavedViewNames.length>0&&<span className="--TSD-stat-menu-trigger-badge" title={orderedSavedViewNames.length+' of '+TSD_MAX_ANALYTICS_PRESETS+' saved views'}>{orderedSavedViewNames.length}</span>}<ChevronDown size={14} aria-hidden="true"/>
          </button>

          {presetMenuOpen ? (
            <div
              className="--TSD-preset-menu-flyout"
              role="dialog"
              aria-label="Analytics presets"
            >
              <div className="--TSD-preset-menu-header">
                <h3>Saved views</h3>
              </div>

              {orderedSavedViewNames.length >
              0 ? (
                <div className="--TSD-preset-list">
                  {orderedSavedViewNames.map(
                    (name) => {
                      const isEditing =
                        presetBeingEdited ===
                        name;

                      return (
                        <article
                          className={`--TSD-preset-list-item ${
                            activePresetName ===
                            name
                              ? "--TSD-is-active"
                              : ""
                          }`}
                          key={
                            name
                          }
                        >
                          <div className="--TSD-preset-list-row">
                            {isEditing ? (
                              <input
                                className="--TSD-preset-name-input"
                                autoFocus
                                type="text"
                                value={
                                  presetEditValue
                                }
                                onChange={(
                                  event
                                ) =>
                                  setPresetEditValue(
                                    event
                                      .target
                                      .value
                                  )
                                }
                                onKeyDown={(
                                  event
                                ) => {
                                  if (
                                    event.key ===
                                    "Enter"
                                  ) {
                                    handleRenameSavedView(
                                      name
                                    );
                                  }

                                  if (
                                    event.key ===
                                    "Escape"
                                  ) {
                                    setPresetBeingEdited(
                                      null
                                    );

                                    setPresetEditValue(
                                      ""
                                    );
                                  }
                                }}
                              />
                            ) : (
                              <button
                                className="--TSD-preset-list-name"
                                type="button"
                                onClick={() =>
                                  handleLoadSavedView(
                                    name
                                  )
                                }
                              >
                                {name}
                              </button>
                            )}

                            <div className="--TSD-preset-list-actions">
                              <button
                                className="--TSD-icon-button --TSD-preset-row-action"
                                type="button"
                                aria-label={
                                  activePresetName ===
                                  name
                                    ? `Update preset ${name}`
                                    : `Load preset ${name}`
                                }
                              title={
                                  activePresetName ===
                                  name
                                    ? "Update preset"
                                    : "Load preset"
                                }
                              onClick={() =>
                                  activePresetName ===
                                  name
                                    ? handleUpdateSavedView(
                                        name
                                      )
                                    : handleLoadSavedView(
                                        name
                                      )
                                }
                              >
                                <span aria-hidden="true">
                                  {activePresetName ===
                                  name
                                    ? "\u21BB"
                                    : "\u2197"}
                                </span>
                              </button>

                              <button
                                className="--TSD-icon-button --TSD-preset-row-action"
                                type="button"
                                aria-label={
                                  presetBeingEdited ===
                                  name
                                    ? `Apply new name for ${name}`
                                    : `Rename preset ${name}`
                                }
                                title={
                                  presetBeingEdited ===
                                  name
                                    ? "Apply name"
                                    : "Rename preset"
                                }
                                onClick={() => {
                                  if (
                                    presetBeingEdited ===
                                    name
                                  ) {
                                    handleRenameSavedView(
                                      name
                                    );
                                    return;
                                  }

                                  setPresetBeingEdited(
                                    name
                                  );

                                  setPresetEditValue(
                                    name
                                  );
                                }}
                              >
                                <span aria-hidden="true">
                                  {presetBeingEdited ===
                                  name
                                    ? "\u2713"
                                    : "\u270E"}
                                </span>
                              </button>

                            <button
                              className="--TSD-icon-button --TSD-preset-delete-button"
                              type="button"
                              aria-label={`Delete preset ${name}`}
                              title="Delete preset"
                              onClick={() =>
                                handleDeleteSavedView(
                                  name
                                )
                              }
                            />
                            </div>
                          </div>
                        </article>
                      );
                    }
                  )}
                </div>
              ) : null}

              {saveViewOpen ? (
                <div className="--TSD-preset-composer">
                  <input
                    className="--TSD-preset-name-input"
                    autoFocus
                    type="text"
                    placeholder="Name a new preset"
                    value={
                      saveViewName
                    }
                    onChange={(
                      event
                    ) =>
                      setSaveViewName(
                        event
                          .target
                          .value
                      )
                    }
                    onKeyDown={(
                      event
                    ) => {
                      if (
                        event.key ===
                        "Enter"
                      ) {
                        handleSaveView();
                      }

                      if (
                        event.key ===
                        "Escape"
                      ) {
                        setSaveViewOpen(
                          false
                        );

                        setSaveViewName(
                          ""
                        );
                      }
                    }}
                  />

                  <div className="--TSD-preset-composer-actions">
                    <button
                      className="--TSD-text-button app-cancel-control"
                      type="button"
                      onClick={() => {
                        setSaveViewOpen(
                          false
                        );

                        setSaveViewName(
                          ""
                        );
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      className="--TSD-button --TSD-button--primary"
                      type="button"
                      disabled={
                        !saveViewName.trim()
                      }
                      onClick={() =>
                        handleSaveView()
                      }
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="--TSD-preset-new-row"
                  type="button"
                  onClick={() =>
                    setSaveViewOpen(
                      true
                    )
                  }
                >
                  + New saved view
                </button>
              )}
            </div>
          ) : null}
        </div>
      </section>
    );

  const displaySettingsPanel =
    (
      <section className="--TSD-workspace-panel --TSD-display-panel">
        <div className="--TSD-display-panel-head">
          <span className="--TSD-stat-menu-trigger-top">
            <span className="--TSD-stat-menu-trigger-kicker">
              Display
            </span>

            <span className="--TSD-stat-menu-trigger-badge --TSD-display-size-readout">
              {
                dashboardScaleReadout
              }
            </span>
          </span>

          <span className="--TSD-stat-menu-trigger-headline">
            <strong>
              Graph size
            </strong>
          </span>
        </div>

        <div className="--TSD-display-size-bar">
          <input
            className="--TSD-display-size-slider"
            aria-label="Graph size"
            type="range"
            min={
              TSD_DASHBOARD_SCALE_RANGE.min
            }
            max={
              TSD_DASHBOARD_SCALE_RANGE.max
            }
            step="1"
            value={
              dashboardScaleValue
            }
            onChange={(
              event
            ) =>
              setDashboardScale(
                tsdClampDashboardScale(
                  Number(
                    event
                      .target
                      .value
                  )
                )
              )
            }
          />

          <div className="--TSD-display-size-track-labels">
            <span>
              Smaller
            </span>

            <span>
              Larger
            </span>
          </div>
        </div>
      </section>
    );

  const studioToolbar = <StatisticsHeaderActions><div className="StatsStudio__toolbar" aria-label="Dashboard controls">
    <div className="StatsStudio__view-actions" role="group" aria-label="Saved view controls">{presetsLauncher}</div>
    <div className="StatsStudio__document-actions" role="group" aria-label="Export and summary document"><StudioExportMenu onPng={handleExportPng} onCsv={handleExportCsv} onExcel={handleExportExcel} onReport={handleEnterReportBuilder}/><button className="--TSD-button --TSD-button--primary" type="button" onClick={handleEnterReportBuilder}><FileChartColumn size={16}/>Create Summary Document</button></div>
  </div></StatisticsHeaderActions>;

  return (
    <div
      ref={pageShellRef}
      className="--TSD-page"
      style={
        dashboardScaleStyle
      }
    >
      {studioToolbar}
      <div className="--TSD-dashboard-layout">
        <aside ref={libraryRef} className="--TSD-filter-sidebar">
          {displaySettingsPanel}

          <div className="--TSD-filter-sidebar-scrollshell">
            {statMenuLauncher}
          </div>
        </aside>

        <main className="--TSD-main">
      <StudioInsights expanded={false} hideControls onRows={setStudioRows} onData={setStudioData} key={teamId} teamId={teamId} events={setupEvents} matches={filteredMatches} allMatches={unfilteredMatches} filters={EMPTY_CARD_FILTERS} eventFilters={EMPTY_CARD_FILTERS.eventFilters}/>

          {false ? (
          <section className="--TSD-presets-card">
            <div
              ref={
                presetMenuShellRef
              }
              className="--TSD-stat-menu-shell --TSD-preset-menu-shell"
              onMouseEnter={
                openPresetMenuHover
              }
              onMouseLeave={
                schedulePresetMenuHoverClose
              }
              onFocus={
                openPresetMenuHover
              }
              onBlur={(
                event
              ) => {
                if (
                  !event.relatedTarget
                ) {
                  return;
                }

                if (
                  presetMenuShellRef.current?.contains(
                    event.relatedTarget
                  )
                ) {
                  return;
                }

                schedulePresetMenuHoverClose();
              }}
            >
              <button
                className="--TSD-stat-menu-trigger --TSD-preset-menu-trigger"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={
                  presetMenuOpen
                }
              >
                <strong>
                  Presets
                </strong>

                <small>
                  {
                    orderedSavedViewNames.length
                  }
                  /
                  {
                    TSD_MAX_ANALYTICS_PRESETS
                  }{" "}
                  saved
                  {activePresetName
                    ? ` · ${activePresetName}`
                    : ""}
                </small>
              </button>

              {presetMenuOpen ? (
                <div
                  className="--TSD-preset-menu-flyout"
                  role="dialog"
                  aria-label="Analytics presets"
                  onMouseEnter={
                    openPresetMenuHover
                  }
                  onMouseLeave={
                    schedulePresetMenuHoverClose
                  }
                >
                  <div className="--TSD-section-heading --TSD-section-heading--compact">
                    <div>
                      <span className="--TSD-section-kicker">
                        Workspace
                      </span>

                      <h3>
                        Team analytics
                        presets
                      </h3>

                      <p>
                        Autosave is
                        always on.
                        Save up to{" "}
                        {
                          TSD_MAX_ANALYTICS_PRESETS
                        }{" "}
                        named presets
                        for quick
                        reuse.
                      </p>
                    </div>

                    <span className="--TSD-match-count-badge">
                      {
                        orderedSavedViewNames.length
                      }
                      /
                      {
                        TSD_MAX_ANALYTICS_PRESETS
                      }{" "}
                      presets
                    </span>
                  </div>

                  <div className="--TSD-presets-toolbar">
                    <div className="--TSD-search-wrap --TSD-search-wrap--preset">
                      <span aria-hidden="true">
                        +
                      </span>

                      <input
                        type="text"
                        placeholder="Name a new analytics preset"
                        value={
                          saveViewName
                        }
                        onChange={(
                          event
                        ) =>
                          setSaveViewName(
                            event
                              .target
                              .value
                          )
                        }
                        onKeyDown={(
                          event
                        ) => {
                          if (
                            event.key ===
                            "Enter"
                          ) {
                            handleSaveView();
                          }
                        }}
                      />
                    </div>

                    <button
                      className="--TSD-button --TSD-button--primary"
                      type="button"
                      disabled={
                        !saveViewName.trim()
                      }
                      onClick={() =>
                        handleSaveView()
                      }
                    >
                      Save
                    </button>
                  </div>

                  {orderedSavedViewNames.length >
                  0 ? (
                    <div className="--TSD-preset-grid">
                      {orderedSavedViewNames.map(
                        (name) => {
                          const preset =
                            savedViews[
                              name
                            ] || {};

                          const isEditing =
                            presetBeingEdited ===
                            name;

                          const presetStatCount =
                            Array.isArray(
                              preset.selectedStatKeys
                            )
                              ? preset
                                  .selectedStatKeys
                                  .length
                              : 0;

                          const presetMapCount =
                            Array.isArray(
                              preset.filters
                                ?.maps
                            )
                              ? preset
                                  .filters
                                  .maps
                                  .length
                              : 0;

                          return (
                            <article
                              className={`--TSD-preset-card ${
                                activePresetName ===
                                name
                                  ? "--TSD-is-active"
                                  : ""
                              }`}
                              key={
                                name
                              }
                            >
                              <div className="--TSD-preset-card-header">
                                {isEditing ? (
                                  <input
                                    className="--TSD-preset-name-input"
                                    autoFocus
                                    type="text"
                                    value={
                                      presetEditValue
                                    }
                                    onChange={(
                                      event
                                    ) =>
                                      setPresetEditValue(
                                        event
                                          .target
                                          .value
                                      )
                                    }
                                    onKeyDown={(
                                      event
                                    ) => {
                                      if (
                                        event.key ===
                                        "Enter"
                                      ) {
                                        handleRenameSavedView(
                                          name
                                        );
                                      }

                                      if (
                                        event.key ===
                                        "Escape"
                                      ) {
                                        setPresetBeingEdited(
                                          null
                                        );

                                        setPresetEditValue(
                                          ""
                                        );
                                      }
                                    }}
                                  />
                                ) : (
                                  <button
                                    className="--TSD-preset-load"
                                    type="button"
                                    onClick={() =>
                                      handleLoadSavedView(
                                        name
                                      )
                                    }
                                  >
                                    {name}
                                  </button>
                                )}

                                <span className="--TSD-preset-meta">
                                  {
                                    presetStatCount
                                  }{" "}
                                  stats
                                </span>
                              </div>

                              <p className="--TSD-preset-summary">
                                {presetMapCount >
                                0
                                  ? `${presetMapCount} maps`
                                  : "All maps"}{" "}
                                /{" "}
                                {preset.filters
                                  ?.side ||
                                  "both"}{" "}
                                / min N{" "}
                                {Number(
                                  preset.filters
                                    ?.minSample ||
                                    0
                                )}
                              </p>

                              <div className="--TSD-preset-actions">
                                <button
                                  className="--TSD-text-button"
                                  type="button"
                                  onClick={() =>
                                    handleUpdateSavedView(
                                      name
                                    )
                                  }
                                >
                                  Update
                                </button>

                                <button
                                  className="--TSD-text-button"
                                  type="button"
                                  onClick={() => {
                                    if (
                                      presetBeingEdited ===
                                      name
                                    ) {
                                      handleRenameSavedView(
                                        name
                                      );
                                      return;
                                    }

                                    setPresetBeingEdited(
                                      name
                                    );

                                    setPresetEditValue(
                                      name
                                    );
                                  }}
                                >
                                  {presetBeingEdited ===
                                  name
                                    ? "Apply name"
                                    : "Rename"}
                                </button>

                                <button
                                  className="--TSD-icon-button --TSD-preset-delete-button"
                                  type="button"
                                  aria-label={`Delete preset ${name}`}
                                  title="Delete preset"
                                  onClick={() =>
                                    handleDeleteSavedView(
                                      name
                                    )
                                  }
                                />
                              </div>
                            </article>
                          );
                        }
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>
          ) : null}

          {false ? (
            <section className="--TSD-stat-menu-launcher">
            <div
              ref={
                statMenuShellRef
              }
              className="--TSD-stat-menu-shell"
              onMouseEnter={
                openStatMenuHover
              }
              onMouseLeave={
                scheduleStatMenuHoverClose
              }
              onFocus={
                openStatMenuHover
              }
              onBlur={(
                event
              ) => {
                if (
                  statMenuPinned
                ) {
                  return;
                }

                if (
                  !event.relatedTarget
                ) {
                  return;
                }

                if (
                  statMenuShellRef.current?.contains(
                    event.relatedTarget
                  )
                ) {
                  return;
                }

                scheduleStatMenuHoverClose();
              }}
            >
              <button
                className="--TSD-stat-menu-trigger"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={
                  statMenuOpen
                }
              >
                <strong>
                  Metrics menu
                </strong>

                <small>
                  {
                    selectedRows.length
                  }{" "}
                  selected ·{" "}
                  {
                    filteredMatches.length
                  }{" "}
                  matches in
                  analysis
                </small>
              </button>

              {statMenuOpen ? (
                <div
                  ref={statMenuFlyoutRef}
                  className={`--TSD-stat-menu-flyout ${
                    statMenuPinned
                      ? "--TSD-is-pinned"
                      : ""
                  }`}
                  style={statMenuFlyoutStyle}
                  role="dialog"
                  aria-label="Statistics menu"
              data-metrics-section={metricsMenuTab}
                  onMouseEnter={
                    openStatMenuHover
                  }
                  onMouseLeave={
                    scheduleStatMenuHoverClose
                  }
                >
                  <div className="--TSD-section-heading --TSD-section-heading--compact">
                    <div>
                      <span className="--TSD-section-kicker">
                        Choose your
                        metrics
                      </span>

                      <h3>
                        Guided
                        statistics
                        menu
                      </h3>

                      <p>
                        Start with
                        a scope,
                        then narrow
                        to the
                        metric
                        family you
                        need.
                      </p>
                    </div>

                    <div className="--TSD-inline-actions">
                      <button
                        className="--TSD-button --TSD-button--ghost"
                        type="button"
                        onClick={
                          handleSelectPopularStats
                        }
                      >
                        Starter Set
                      </button>

                      {statMenuPinned ? (
                        <button
                          className="--TSD-icon-button --TSD-icon-button--large app-cancel-control"
                          type="button"
                          aria-label="Close statistics menu"
                          onClick={() => {
                            clearHoverCloseTimer(
                              statMenuCloseTimerRef
                            );

                            setStatMenuPinned(
                              false
                            );

                            setStatMenuHoverOpen(
                              false
                            );
                          }}
                        >
                          X
                        </button>
                      ) : (
                        <button
                          className="--TSD-icon-button --TSD-icon-button--large"
                          type="button"
                          aria-label="Keep statistics menu open"
                          onClick={() => {
                            clearHoverCloseTimer(
                              statMenuCloseTimerRef
                            );

                            setStatMenuPinned(
                              true
                            );

                            setStatMenuHoverOpen(
                              true
                            );
                          }}
                        >
                          [ ]
                        </button>
                      )}
                    </div>
                  </div>

                  <StudioMetricsMenuTabs active={metricsMenuTab} onChange={tab=>{setMetricsMenuTab(tab);setStatMenuPinned(true);setStatMenuHoverOpen(true);}}>
                  <StudioInsightPicker rows={availableRows} onAdd={handleToggleStat}/>
                </StudioMetricsMenuTabs>
                <div className="--TSD-stat-menu-toolbar">
                    <div className="--TSD-search-wrap">
                      <span aria-hidden="true">
                        S
                      </span>

                      <input
                        type="search"
                        placeholder="Search statistics, e.g. pistol, conversion, player..."
                        value={
                          statMenuSearch
                        }
                        onChange={(
                          event
                        ) =>
                          setStatMenuSearch(
                            event
                              .target
                              .value
                          )
                        }
                      />
                    </div>

                    <div className="--TSD-inline-actions">
                      

                      <button
                        className="--TSD-text-button"
                        type="button"
                        onClick={() =>
                          setSelectedStatKeys(
                            []
                          )
                        }
                      >
                        Clear stats
                      </button>
                    </div>
                  </div>

                  <div className="--TSD-scope-tabs">
                    {statMenuScopeOptions.map(
                      (option) => (
                        <button
                          className={`--TSD-scope-tab ${
                            statMenuScope ===
                            option.value
                              ? "--TSD-is-active"
                              : ""
                          }`}
                          type="button"
                          key={
                            option.value
                          }
                          onClick={() =>
                            setStatMenuScope(
                              option.value
                            )
                          }
                        >
                          <strong>
                            {
                              option.label
                            }
                          </strong>

                          <span>
                            {
                              option.description
                            }
                          </span>
                        </button>
                      )
                    )}
                  </div>

                  <div className="--TSD-category-tabs">
                    {statCategories.map(
                      (category) => (
                        <button
                          className={`--TSD-category-tab ${
                            statCategory ===
                            category
                              ? "--TSD-is-active"
                              : ""
                          }`}
                          type="button"
                        key={
                          category
                        }
                        onClick={() =>
                          setStatCategory(
                            (
                              current
                            ) =>
                              current ===
                              category
                                ? "All"
                                : category
                          )
                        }
                      >
                        {
                          TSD_MENU_GROUP_META[
                              category
                            ]?.title ||
                            category
                          }
                        </button>
                      )
                    )}
                  </div>

                  <div
                    ref={
                      statMenuScrollRef
                    }
                    className="--TSD-stat-menu-groups"
                    onScroll={(
                      event
                    ) =>
                      setStatMenuScrollTop(
                        event
                          .currentTarget
                          .scrollTop
                      )
                    }
                  >
                    {statMenuRows.length ===
                    0 ? (
                      <div className="--TSD-empty-state">
                        No
                        statistics
                        match this
                        search,
                        scope, and
                        category.
                      </div>
                    ) : (
                      visibleStatMenuGroups.map(
                        (group) => (
                          <section
                            className="--TSD-stat-group"
                            key={
                              group
                            }
                          >
                            <div className="--TSD-stat-group-header">
                              <strong>
                                {
                                  TSD_MENU_GROUP_META[
                                    group
                                  ]
                                    ?.title
                                }
                              </strong>

                              <span>
                                {
                                  TSD_MENU_GROUP_META[
                                    group
                                  ]
                                    ?.description
                                }
                              </span>
                            </div>

                            <div className="--TSD-stat-selector-grid --TSD-stat-selector-grid--flyout">
                              {(
                                groupedStatMenuRows[
                                  group
                                ] || []
                              ).map(
                                (row) => {
                                  const key =
                                    tsdSelectionKey(
                                      row
                                    );

                                  const selected =
                                    selectedStatKeys.some(instanceKey=>metricKeyFor(instanceKey)===key);

                                  return (
                                    <div
                                      className={`--TSD-stat-selector ${
                                        selected
                                          ? "--TSD-is-selected"
                                          : ""
                                      }`}
                                      key={
                                        key
                                      }
                                    >
                                      <button
                                        className="--TSD-stat-selector-main"
                                        type="button"
                                        onClick={() =>
                                          handleToggleStat(
                                            row
                                          )
                                        }
                                      >
                                        <span className="--TSD-stat-selector-checkbox">
                                          {selected
                                            ? "OK"
                                            : ""}
                                        </span>

                                        <span className="--TSD-stat-selector-copy">
                                          <strong>
                                            {
                                              tsdGetStatMeta(
                                                row
                                              )
                                                .title
                                            }
                                          </strong>

                                          <small>
                                            N=
                                            {row.sample ??
                                              0}
                                          </small>
                                        </span>
                                      </button>

                                      <TSDInfo
                                        row={
                                          row
                                        }
                                      />
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </section>
                        )
                      )
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            </section>
          ) : null}

          <section className="--TSD-active-filters-card">
            <div className="--TSD-section-heading --TSD-section-heading--compact">
              <div>
                <span className="--TSD-section-kicker">
                  Dataset
                </span>

                <h2>
                  Active
                  Filters
                </h2>
              </div>

              <span className="--TSD-match-count-badge">
                {
                  filteredMatches.length
                }{" "}
                matches
              </span>
            </div>

            <div className="--TSD-chip-list">
              {activeFilterChips.map(
                (chip) => (
                  <span
                    className="--TSD-filter-chip"
                    key={
                      chip
                    }
                  >
                    {chip}
                  </span>
                )
              )}
            </div>
          </section>

          <section className="--TSD-stat-menu-card">
            <div className="--TSD-section-heading">
              <div>
                <span className="--TSD-section-kicker">
                  Choose your
                  metrics
                </span>

                <h2>
                  Statistics
                  Menu
                </h2>

                {false ? (
                  <p>
                  Select only
                  the
                  statistics
                  you want
                  displayed.
                  Hover each{" "}
                  <b>
                    i
                  </b>{" "}
                  for its
                  definition,
                  formula,
                  interpretation,
                  and use
                  case.
                  </p>
                ) : null}
              </div>

              <div className="--TSD-inline-actions">
                <button
                  className="--TSD-button --TSD-button--ghost"
                  type="button"
                  onClick={
                    handleSelectPopularStats
                  }
                >
                  Popular Set
                </button>

                <button
                  className="--TSD-icon-button --TSD-icon-button--large"
                  type="button"
                  onClick={() =>
                    setStatMenuExpanded(
                      (
                        current
                      ) =>
                        !current
                    )
                  }
                  aria-label="Toggle statistics menu"
                >
                  {statMenuExpanded
                    ? "−"
                    : "+"}
                </button>
              </div>
            </div>

            {statMenuExpanded ? (
              <>
                <StudioMetricsMenuTabs active={metricsMenuTab} onChange={tab=>{setMetricsMenuTab(tab);setStatMenuPinned(true);setStatMenuHoverOpen(true);}}>
                  <StudioInsightPicker rows={availableRows} onAdd={handleToggleStat}/>
                </StudioMetricsMenuTabs>
                <div className="--TSD-stat-menu-toolbar">
                  <div className="--TSD-search-wrap">
                    <span aria-hidden="true">
                      ⌕
                    </span>

                    <input
                      type="search"
                      placeholder="Search statistics, e.g. pistol, conversion, player…"
                      value={
                        statMenuSearch
                      }
                      onChange={(
                        event
                      ) =>
                        setStatMenuSearch(
                          event
                            .target
                            .value
                        )
                      }
                    />
                  </div>

                  <div className="--TSD-inline-actions">
                    

                    <button
                      className="--TSD-text-button"
                      type="button"
                      onClick={() =>
                        setSelectedStatKeys(
                          []
                        )
                      }
                    >
                      Clear stats
                    </button>
                  </div>
                </div>

                <div className="--TSD-category-tabs">
                  {statCategories.map(
                    (
                      category
                    ) => (
                      <button
                        className={`--TSD-category-tab ${
                          statCategory ===
                          category
                            ? "--TSD-is-active"
                            : ""
                        }`}
                        type="button"
                        key={
                          category
                        }
                        onClick={() =>
                          setStatCategory(
                            (
                              current
                            ) =>
                              current ===
                              category
                                ? "All"
                                : category
                          )
                        }
                      >
                        {
                          category
                        }
                      </button>
                    )
                  )}
                </div>

                <div className="--TSD-stat-selector-grid">
                  {statMenuRows.length ===
                  0 ? (
                    <div className="--TSD-empty-state">
                      No
                      statistics
                      match this
                      search and
                      category.
                    </div>
                  ) : (
                    statMenuRows.map(
                      (row) => {
                        const key =
                          tsdSelectionKey(
                            row
                          );

                        const selected =
                          selectedStatKeys.some(instanceKey=>metricKeyFor(instanceKey)===key);

                        return (
                          <div
                            className={`--TSD-stat-selector ${
                              selected
                                ? "--TSD-is-selected"
                                : ""
                            }`}
                            key={
                              key
                            }
                          >
                            <button
                              className="--TSD-stat-selector-main"
                              type="button"
                              onClick={() =>
                                handleToggleStat(
                                  row
                                )
                              }
                            >
                              <span className="--TSD-stat-selector-checkbox">
                                {selected
                                  ? "✓"
                                  : ""}
                              </span>

                              <span className="--TSD-stat-selector-copy">
                                <strong>
                                  {
                                    tsdGetStatMeta(
                                      row
                                    )
                                      .title
                                  }
                                </strong>

                                <small>
                                  {
                                    row.category
                                  }{" "}
                                  • N=
                                  {row.sample ??
                                    0}
                                </small>
                              </span>
                            </button>

                            <TSDInfo
                              row={
                                row
                              }
                            />
                          </div>
                        );
                      }
                    )
                  )}
                </div>
              </>
            ) : null}
          </section>

          <section className="--TSD-overview-section">
            <div className="--TSD-section-heading">
              <div>
                <span className="--TSD-section-kicker">
                  Dashboard
                </span>

                <h2>Analysis dashboard</h2>

                {false ? (
                  <p>
                  {
                    selectedRows.length
                  }{" "}
                  statistics
                  selected •
                  every graph
                  has its own
                  settings.
                  </p>
                ) : null}
              </div>

              <div className="--TSD-overview-meta">
                <span className="--TSD-header-chip">
                  <strong>
                    {
                      selectedRows.length
                    }
                  </strong>
                  live stats
                </span>

                <span className="--TSD-header-chip">
                  <strong>
                    {
                      filteredMatches.length
                    }
                  </strong>
                  filtered
                  matches
                </span>

                {activePresetName&&<span className="--TSD-header-chip">{activePresetName}</span>}
              </div>


            </div>

            {selectedRows.length ===
            0 ? (
              <div className="--TSD-empty-state --TSD-empty-state--large">
                <strong>
                  No
                  statistics
                  selected.
                </strong>

                <span>
                  Choose
                  metrics from
                  the metrics
                  menu in the
                  page sidebar
                  to build this
                  dashboard.
                </span>
              </div>
            ) : (
              <div className="--TSD-stat-grid">
                {selectedRows.map(
                  (row) => {
                    const selectionKey =
                      tsdSelectionKey(
                        row
                      );

                    return (
                      <TSDStatCard key={selectionKey} row={row} matches={row._cardMatches} previousRow={row._previousRow} settings={graphSettingsByStat[selectionKey]} minimumSample={row._cardFilters.minSample} onOpenData={()=>{setSettingsInitialTab('data');setSettingsStatKey(selectionKey);}} onOpenSettings={()=>{setSettingsInitialTab('graph');setSettingsStatKey(selectionKey);}}>
                        {row.studioView&&<StudioInsights expanded hideControls data={row._insightData} view={row.studioView} selectedPoint={row._cardFilters.focusPoint} renderTrend={(series,label,max)=>renderInsightTrend(series,label,max,graphSettingsByStat[selectionKey])} onSelectedPoint={(focusPoint,focusPointLabel)=>handleGraphSettingsChange(selectionKey,{...graphSettingsByStat[selectionKey],dataFilters:{...row._cardFilters,focusPoint,focusPointLabel}})} teamId={teamId} events={setupEvents} matches={row._cardMatches} allMatches={unfilteredMatches} filters={EMPTY_CARD_FILTERS} eventFilters={EMPTY_CARD_FILTERS.eventFilters}/>}
                      </TSDStatCard>
                    );
                  }
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      {settingsRow ? (
        <TSDGraphSettingsModal
          key={settingsStatKey}
          row={settingsRow}
          initialTab={settingsInitialTab}
          duplicate={settingsDuplicate}
          onRemove={()=>{setSelectedStatKeys(keys=>keys.filter(key=>key!==settingsStatKey));setSettingsStatKey(null);}}
          dataEditor={settingsDataEditor}

          settings={
            graphSettingsByStat[
              settingsStatKey
            ]
          }
          onChange={(
            next
          ) =>
            handleGraphSettingsChange(
              settingsStatKey,
              next
            )
          }
          onClose={() =>
            setSettingsStatKey(
              null
            )
          }
        />
      ) : null}

      {false ? (
        <div
          className="--TSD-modal-backdrop"
          role="presentation"
          onMouseDown={() =>
            setSaveViewOpen(
              false
            )
          }
        >
          <div
            className="--TSD-modal --TSD-save-view-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Save dashboard view"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >
            <div className="--TSD-modal-header">
              <div>
                <span className="--TSD-eyebrow">
                  Saved views
                </span>

                <h3>
                  Save this
                  dashboard
                </h3>
              </div>

              <button
                className="--TSD-icon-button --TSD-icon-button--large app-cancel-control"
                type="button"
                onClick={() =>
                  setSaveViewOpen(
                    false
                  )
                }
              >
                ×
              </button>
            </div>

            <label className="--TSD-field">
              <span>
                View name
              </span>

              <input
                autoFocus
                type="text"
                placeholder="My Tournament View"
                value={
                  saveViewName
                }
                onChange={(
                  event
                ) =>
                  setSaveViewName(
                    event
                      .target
                      .value
                  )
                }
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                    "Enter"
                  ) {
                    handleSaveView();
                  }
                }}
              />
            </label>

            {Object.keys(
              savedViews
            ).length > 0 ? (
              <div className="--TSD-saved-view-list">
                {Object.keys(
                  savedViews
                )
                  .sort()
                  .map(
                    (
                      name
                    ) => (
                      <div
                        className="--TSD-saved-view-row"
                        key={
                          name
                        }
                      >
                        <span>
                          {name}
                        </span>

                        <button
                          className="--TSD-text-button --TSD-text-button--danger"
                          type="button"
                          onClick={() =>
                            handleDeleteSavedView(
                              name
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    )
                  )}
              </div>
            ) : null}

            <div className="--TSD-modal-footer">
              <button
                className="--TSD-button --TSD-button--ghost app-cancel-control"
                type="button"
                onClick={() =>
                  setSaveViewOpen(
                    false
                  )
                }
              >
                Cancel
              </button>

              <button
                className="--TSD-button --TSD-button--primary"
                type="button"
                disabled={
                  !saveViewName.trim()
                }
                onClick={
                  handleSaveView
                }
              >
                Save View
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {customAlert && (
        <CustomAlert
          message={
            customAlert.message
          }
          type={
            customAlert.type
          }
          position={
            customAlert.position
          }
          onClose={() =>
            setCustomAlert(
              null
            )
          }
        />
      )}
    </div>
  );
}
